import "@babylonjs/core/Rendering/boundingBoxRenderer"; //side effect for Mesh.showBoundingBox / getBoundingBoxRenderer
import "@babylonjs/core/Animations/animatable"; // https://doc.babylonjs.com/divingDeeper/developWithBjs/treeShaking#faq
import "@babylonjs/core/Audio/audioSceneComponent"; // https://forum.babylonjs.com/t/babylon-engine-audioengine-is-undefined/13097/2
import "@babylonjs/core/Audio/audioEngine"; // https://forum.babylonjs.com/t/babylon-engine-audioengine-is-undefined/13097/2
import { fromDataUri } from "@egr/wcf/modules/utils/arraybuffer";
import { AdvancedDynamicTexture } from "@babylonjs/gui";
import { Animation } from "@babylonjs/core/Animations/animation";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { GLTF2Export as BJSGLTF2Export } from "@babylonjs/serializers/glTF";
import { Quaternion, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { PowerEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { Ray } from "@babylonjs/core/Culling/ray";
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture as BJSTexture } from "@babylonjs/core/Materials/Textures/texture";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ajax } from '@egr/wcf/modules/utils/async';
import { ArticleElement, MainArticleElement } from '@egr/wcf/modules/cf';
import { contains, removeElement, addElements } from '@egr/wcf/modules/utils/array';
import { CameraMode } from '@egr/wcf/modules/core/view/CameraControl';
import { ZipDocumentContainer } from '@egr/wcf/modules/core/io';
import { DeleteElements, InsertElements } from '@egr/wcf/modules/core/cmd';
import { ElementChangedFlags, BoundingBox } from '@egr/wcf/modules/core/mdl';
import { clamp, eq } from '@egr/wcf/modules/utils/math';
import { convertLengthUnit, toLocaleLengthString, fromLocaleLengthString, LengthUnit } from "@egr/wcf/modules/utils/number";
import { isNullOrEmpty, isDataUri, isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { perpendicularVector2 } from "@egr/wcf/modules/core/utils/MathUtils";
import { Log, ListenableEvent, wcfConfig } from '@egr/wcf/modules/utils';
import { SceneElement } from '@egr/wcf/modules/core/mdl';
import { PbrMaterial } from '@egr/wcf/modules/core/mdl';
import { placeElementsInViewCenter, placeElementsAtFreePosition } from '@egr/wcf/modules/pl/utils';
import { PropertyClass, PropertyValue, StringProperty } from '@egr/wcf/modules/core/prop';
import { setLocalMatrix } from '@egr/wcf/modules/core/utils/MeshUtils';
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { Texture } from '@egr/wcf/modules/core/mdl';
import { updateFrozenWorldMatrix } from '@egr/wcf/modules/core/utils/MeshUtils';
import { CameraPath } from './mdl/geo/CameraPath';
import { AlignElements } from './cmd/AlignElements';
import { AppModule } from './AppModule';
import { EditWalls } from "./cmd/EditWalls";
import { GroupElement } from './mdl/GroupElement';
import { MATZLoader } from "./MATZloader";
import { Measure } from './mdl/Measure';
import { OPLeoxImporter } from './OPLeoxImporter';
import { FallbackResources } from './Resources';
import { Route } from './mdl/Route';
import { RoomElement } from './mdl/RoomElement';
import { RoomSettings } from './RoomSettings';
import { Wall } from './mdl/Wall';
import { UnGroup, Group } from './cmd/GroupUngroupElements';
import { Util } from './utils/Util';
import { WallAddition } from './mdl/WallAddition';
import { WallCrossHair } from './WallCrossHair';
import { WallOpening } from './mdl/WallOpening';
import { GltfScene as roomGltfScene } from './utils/GltfScene';
import { MoveCircle, MoveCircleHill, MoveHedge, MoveLine, MoveLineHill, MoveRectangle, MoveRectangleHill, MoveRoad, MoveSpline, MoveSplineHill } from './tool/tools';
export var AddEnding;
(function (AddEnding) {
    AddEnding[AddEnding["Start"] = 0] = "Start";
    AddEnding[AddEnding["End"] = 1] = "End";
})(AddEnding || (AddEnding = {}));
export var ShapeLevel;
(function (ShapeLevel) {
    ShapeLevel[ShapeLevel["default"] = 0] = "default";
    ShapeLevel[ShapeLevel["OnTop"] = 1] = "OnTop";
    ShapeLevel[ShapeLevel["manual"] = 2] = "manual";
})(ShapeLevel || (ShapeLevel = {}));
export var VisibilityMode;
(function (VisibilityMode) {
    VisibilityMode[VisibilityMode["Show2d"] = 0] = "Show2d";
    VisibilityMode[VisibilityMode["Show3d"] = 1] = "Show3d";
})(VisibilityMode || (VisibilityMode = {}));
export var InteractorMode;
(function (InteractorMode) {
    InteractorMode[InteractorMode["Default"] = 0] = "Default";
    InteractorMode[InteractorMode["AddDelete"] = 1] = "AddDelete";
    InteractorMode[InteractorMode["Target"] = 2] = "Target";
})(InteractorMode || (InteractorMode = {}));
export var GrassMatCAGType;
(function (GrassMatCAGType) {
    GrassMatCAGType[GrassMatCAGType["normal"] = 0] = "normal";
    GrassMatCAGType[GrassMatCAGType["notVisible"] = 1] = "notVisible";
})(GrassMatCAGType || (GrassMatCAGType = {}));
export class RoomManager {
    constructor(pApp, pDataPath = "", pLocaleString = "en", pProjectID = "001", penableSkyBox = false, pTestAppMode = false, pArticleManager, pSafetyZonesEnabled = false, pLayerList, pMaterialJsonURL = null, pHideAnimationPath = false, pCameraMaxZ = 500, pSoundsJsonURL = null) {
        this.eventEmptyWalls = new ListenableEvent(this);
        this.eventEmptyARWalls = new ListenableEvent(this);
        this.eventARWallsFinished = new ListenableEvent(this);
        this.eventARRoomRecognitionStarted = new ListenableEvent(this);
        this.eventARFirstWallEdgeBottom = new ListenableEvent(this);
        this.eventARFirstWallEdgeTop = new ListenableEvent(this);
        this.eventEmptyAROpenings = new ListenableEvent(this);
        this.eventAROpeningsFilled = new ListenableEvent(this);
        this.eventRoomContentChanged = new ListenableEvent(this);
        this.eventDeleteSelectionComplete = new ListenableEvent(this);
        this.eventStartInternalCalculation = new ListenableEvent(this);
        this.eventEndInternalCalculation = new ListenableEvent(this);
        this.eventViewModeChanged = new ListenableEvent(null);
        this.eventBuildingLayerVisibilityChanged = new ListenableEvent(this);
        this.eventInteractorMouseUpWOmove = new ListenableEvent(this);
        this.eventInteractorClickedWithoutMove = new ListenableEvent(this);
        this.eventNotEnoughWallSpaceLeft = new ListenableEvent(this);
        this.eventAnimationStarted = new ListenableEvent(this);
        this.eventAnimationPaused = new ListenableEvent(this);
        this.eventAnimationEnded = new ListenableEvent(this);
        this.eventCameraPathRemoved = new ListenableEvent(this);
        this.eventCameraPathAdded = new ListenableEvent(this);
        this.eventGeoMarkerMoved = new ListenableEvent(this);
        this.mRandomString = "12345678";
        this.mDoorTypes = [
            "solid",
            "inlay",
            "glass",
            "solidDouble",
            "inlayDouble",
            "glassDouble", //5
        ];
        this.mWallColors = [
            "#ffffff",
            "#fff6d9",
            "#fde4df",
            "#fbd2be",
            "#f8fcff",
            "#edf4e2",
            "#eadbd4",
            "#ebe7dc",
            "#ffecc2",
            "#e6c5c0",
            "#f7b79e",
            "#d8e2ee",
            "#d0dec7",
            "#c7b8b3",
            "#c6beb1",
            "#d9c080",
            "#c7948b",
            "#e29068",
            "#687793",
            "#b7b57a",
            "#8b817e",
            "#aaaaaa",
            "#999999",
            "#777777",
            "#555555",
            "#000000"
        ];
        this.mYesNo = [
            false,
            true
        ];
        this.mOpeningMaterial = [
            "SHEETGLASS",
            "WHITEPLASTIC",
            "GRAYPLASTIC",
            "METAL"
        ];
        this.mRouteWidths = [
            "0.50m",
            "0.60m",
            "0.80m",
            "0.875m",
            "1.00m",
            "1.20m",
            "1.80m",
            "2.40m"
        ];
        // in meter
        this.mgridFieldDimension = [
            new Vector2(1.5, 1)
        ];
        this.mHill_GaussCurve = [
            0,
            0.02,
            0.039,
            0.0585,
            0.079,
            0.1006,
            0.123,
            0.1465,
            0.1715,
            0.197,
            0.2225,
            0.2485,
            0.2735,
            0.298,
            0.3225,
            0.3465,
            0.371,
            0.395,
            0.421,
            0.4465,
            0.4756,
            0.505,
            0.537,
            0.57,
            0.5999,
            0.63,
            0.655,
            0.679,
            0.701,
            0.722,
            0.747,
            0.773,
            0.804,
            0.836,
            0.869,
            0.904,
            0.935,
            0.96,
            0.9818,
            0.9972,
            1
        ];
        this.onBeforeLoad = () => {
            this.mApp.model.setSelection([]);
            this.mSceneIsLoading = true;
            this.mCutRequestDepth = 0;
            this.eventCameraPathRemoved.trigger(this);
            this.beginCollectWallUpdates();
            this.beginCollectRouteUpdates();
            this.mNotTriggerContentChanged = true;
            this.mLoadedWalls = new Map();
            this.mLoadedWalls_deserialize = new Array();
            this.mLoadedRoutes = new Map();
            this.mSafetyZoneImportData = new Map();
            this.monAfterLoad = false;
            this.mfinalCutAfterLoad = false;
            this.mLoadingScene = true;
        };
        this.onBeforeClear = () => {
            this.mIsClearing = true;
        };
        // tslint:disable-next-line:cyclomatic-complexity
        this.onAfterLoad = async (pAborted) => {
            this.mSceneIsLoading = false;
            this.mWallLoadedDepth = 0;
            this.mMatManager.waitUntilTexturesLoaded().then(() => {
                if (!this.monAfterLoad) {
                    this.monAfterLoad = true;
                    this.mApp.viewer.forceRenderFrame();
                    setTimeout(() => {
                        setTimeout(() => {
                            this.cutOverlappingShapes(null, false, true);
                            this.startWallCreation();
                        }, 200);
                    }, 200);
                }
            });
            if (pAborted != null && !pAborted && this.mLoadedRoutes != null) {
                this.mLoadedRoutes.forEach((pEntry) => {
                    pEntry.route._finishLoading(pEntry.ioData);
                });
            }
            this.mLoadedRoutes = null;
            this.endCollectRouteUpdates();
            this.mLoadingScene = false;
            // re-order 2d shapes for correct cutting-order
            let tnumArray = new Array();
            for (let tRE of this.mREShapes)
                tnumArray.push(tRE.orderIndex);
            tnumArray.sort(this.sortNumber);
            let tsortedRE = new Array();
            for (let tnum of tnumArray) {
                for (let tRE of this.mREShapes) {
                    if (tRE.orderIndex === tnum)
                        tsortedRE.push(tRE);
                }
            }
            this.mREShapes = tsortedRE;
            // this.cutOverlappingShapes();
            if (this.mProjectId === "001") {
                this.createSafetyZonesAfterLoad();
                let tSZ_available = this.reOrderSZIndex();
                if (tSZ_available) {
                    this.cutOverlappingSZShapes();
                }
            }
            this._addCamData();
            this.mNotTriggerContentChanged = false;
        };
        this.onBeforeSafe = () => {
            // cut shapes complete
            this.cutOverlappingShapesCAG(false, false, true);
            this.prepareFenceObjects();
        };
        this.onAfterSafe = () => {
            this.cleanFenceObjects();
        };
        this.onLanguageChanged = () => {
            if (this.mApp.i18n.language != null)
                this.mAppLanguage = this.getLocaleLanguage();
            this.prepareValueLists();
            this.getMaterialList();
            this.getSoundEffectList();
            this.createPropClasses();
        };
        this.onLocaleChanged = () => {
            if (this.mApp.i18n.locale != null)
                this.mAppLanguage = this.getLocaleLanguage();
            this.prepareValueLists();
            this.getMaterialList();
            this.getSoundEffectList();
            this.createPropClasses();
        };
        this.onLengthUnitChanged = () => {
            for (let tMeasure of this.mMeasures) {
                if (tMeasure != null)
                    tMeasure.updateGeometry();
            }
            this.prepareGridDimPropValues();
        };
        this.onShowDefaultInputField = (pInputId, pLength, pClickedElement) => {
            let tLengthStr = toLocaleLengthString(pLength, this.mApp.i18n.locale, this.mApp.i18n.lengthUnit, true);
            if (tLengthStr == null)
                tLengthStr = pLength.toString();
            let tNewLengthStr = prompt("Enter value", tLengthStr);
            if (tNewLengthStr == null)
                return (Promise.resolve(null));
            let tNewLength = fromLocaleLengthString(tNewLengthStr, this.mApp.i18n.lengthUnit);
            if (tNewLength == null)
                return (Promise.resolve(pLength));
            return (Promise.resolve(tNewLength));
        };
        this.onShowDefaultInputFieldText = (pInputId, pText, pClickedElement) => {
            let tText = pText;
            if (tText == null)
                tText = "";
            let tNewText = prompt("Enter value", tText);
            if (tNewText == null)
                return (Promise.resolve(null));
            if (tNewText == null)
                return (Promise.resolve(""));
            return (Promise.resolve(tNewText));
        };
        this.ShowInputFieldText = (pInfoText, pText) => {
            let tText = pText;
            if (tText == null)
                tText = "";
            let tNewText = prompt(pInfoText, tText);
            if (tNewText == null)
                return (Promise.resolve(null));
            if (tNewText == null)
                return (Promise.resolve(""));
            return (Promise.resolve(tNewText));
        };
        this.onBeginTransform = () => {
            this.mTransformationDepth = 0;
            this.mTransformationDepth++;
            if (this.mTransformationDepth === 1) {
                let tSel = this.mApp.model.selection;
                for (let tElement of tSel) {
                    if (tElement instanceof Wall) {
                        this.beginCollectWallUpdates();
                        break;
                    }
                    if (tElement instanceof Measure) {
                        tElement.enableTextNode = false;
                        break;
                    }
                }
            }
            let tSelectedElements = this.mApp.model.selection;
            if (tSelectedElements.length === 0)
                return;
            /*
            for (let tElement of tSelectedElements){
                if (tElement instanceof ArticleElement){
                    if (tElement.basketId != null){
                        let tRE = this.getREforBasketId(tElement.basketId);

                        if (tRE != null){
                            tRE.setStatus("Transform");
                            this.cutOverlappingShapes(tRE, false);
                            tRE.updateGeometry(false, false);
                        }
                    }
                }


                if (tElement instanceof RoomElement){
                    let tRE = tElement;

                    if (tRE.type == "Rectangle" || tRE.type == "Spline" || tRE.type == "Circle" || tRE.type == "Line"){
                        if (tRE.status != "Transform"){
                            this.cutOverlappingShapes(tRE, true);
                            tRE.updateGeometry(false, false);
                        }
                    }
                }
            }
            */
        };
        this.onEndTransform = () => {
            this.mTransformationDepth--;
            if (this.mTransformationDepth <= 0) {
                this.mTransformationDepth = 0;
                let tWallMove = false;
                let tRouteMove = false;
                let tShapedMoved = false;
                // tslint:disable-next-line:cyclomatic-complexity
                this.mTransformedElements.forEach((pElement => {
                    if (pElement instanceof Wall) {
                        tWallMove = true;
                        pElement._finishTransformation();
                    }
                    if (pElement instanceof Measure) {
                        tWallMove = true;
                        pElement._finishTransformation();
                    }
                    if (pElement instanceof Route) {
                        tRouteMove = true;
                        pElement._finishTransformation();
                    }
                    if (pElement instanceof WallOpening)
                        pElement._finishTransformation();
                    if (pElement instanceof WallAddition)
                        pElement._finishTransformation();
                    if (pElement instanceof RoomElement) {
                        if (pElement.type === "Line" || pElement.type === "Spline" ||
                            pElement.type === "Rectangle" || pElement.type === "Circle") {
                            tShapedMoved = true;
                        }
                        pElement._finishTransformation();
                    }
                }));
                this.endCollectWallUpdates(true);
                this.enableMeasureText(true);
                this.mTransformedElements.clear();
                let tSelectedElements = this.mApp.model.selection;
                if (tSelectedElements.length === 0)
                    return;
                for (let tElement of tSelectedElements) {
                    if ((tElement instanceof ArticleElement) && (tElement.basketId != null)) {
                        let tRE = this.getREforBasketId(tElement.basketId);
                        if (tRE != null) {
                            tRE.setStatus("");
                        }
                    }
                }
                if (tShapedMoved) {
                    this.TransformShape();
                }
                if (this.isCollectingWallUpdates) {
                    this.mWallUpdateDepth = 0;
                    this.reconnectWalls();
                }
                if (this.isCollectingRouteUpdates) {
                    this.mRouteUpdateDepth = 0;
                }
            }
        };
        // tslint:disable-next-line:cyclomatic-complexity
        this.onToolStarted = (pTool, pToolManager) => {
            if (pTool === undefined)
                return;
            this.mActiveTool = pTool;
            let tdefTool = this.mApp.tools.defaultTool;
            if (pTool === tdefTool) {
                this.onToolStarted_DefaultTool(pTool, pToolManager);
                return;
            }
            if (this.mTransformationDepth > 0 ||
                pTool instanceof MoveCircle || pTool instanceof MoveCircleHill ||
                pTool instanceof MoveHedge ||
                pTool instanceof MoveLine || pTool instanceof MoveLineHill ||
                pTool instanceof MoveRectangle || pTool instanceof MoveRectangleHill ||
                pTool instanceof MoveRoad ||
                pTool instanceof MoveSpline || pTool instanceof MoveSplineHill) {
                this.onToolStarted_Transform(pTool, pToolManager);
                return;
            }
        };
        // tslint:disable-next-line:cyclomatic-complexity
        this.onToolStarted_DefaultTool = (pTool, pToolManager) => {
            if (pTool === undefined)
                return;
            this.mActiveTool = pTool;
            let tdefTool = this.mApp.tools.defaultTool;
            if (pTool !== tdefTool) {
                return;
            }
            this.mShapeToolStarted = false;
            if (this.mClickedWOmove) {
                this.mClickedWOmove = false;
                this.eventInteractorClickedWithoutMove.trigger(this);
            }
            this.contentChanged();
            let tSelectedElements = this.mApp.model.selection;
            let tshapesSelected = false;
            let twallSelected = false;
            let trouteSelected = false;
            for (let tElement of tSelectedElements) {
                if (tElement instanceof RoomElement) {
                    if (tElement.type === "Spline" || tElement.type === "Rectangle" || tElement.type === "Line" || tElement.type === "Circle"
                        || tElement.type === "CustomBuilding" || tElement.type === "Road" || tElement.type === "Hedge"
                        || tElement.type === "Fence" || tElement.type === "Arena" || tElement.type === "Grid") {
                        tElement.setMeshVisibility(1);
                        tshapesSelected = true;
                        if (tElement.type === "CustomBuilding")
                            tElement.setBuildingOpacity(tElement.BuildingOpacity, true);
                        tElement.setStatus("");
                    }
                    if (tElement.type === "Floor") {
                        twallSelected = true;
                    }
                    if (tElement.type === "Fence") {
                        tElement.updateGeometry();
                    }
                    if (tElement.type === "Grid") {
                        tElement.updateGeometry();
                    }
                }
                if (tElement instanceof Measure) {
                    let tmeasure = tElement;
                    let tnodePos = tmeasure.node.position.clone();
                    let tdiff = Util.to2d(tnodePos).subtract(tmeasure.startPoint);
                    tmeasure.setStartPoint(tmeasure.startPoint.add(tdiff));
                    tmeasure.setEndPoint(tmeasure.endPoint.add(tdiff));
                }
                if (tElement instanceof Wall && !twallSelected) {
                    twallSelected = true;
                }
                if (tElement instanceof Route && !trouteSelected) {
                    trouteSelected = true;
                }
                if (tElement instanceof ArticleElement) {
                    let tRE = this.getREofMainArticle(tElement);
                    if (tRE != null)
                        tRE.setMeshVisibility(1);
                }
            }
            if (twallSelected) {
                this.reconnectWalls();
                this.mWallUpdateDepth = 0;
                this.findRoom();
                for (let tRE of this.mRoomElements) {
                    if (tRE == null)
                        continue;
                    if (tRE.type === "Floor")
                        tRE.node.setEnabled(true);
                }
            }
            if (trouteSelected) {
                this.mRouteUpdateDepth = 0;
                this._findRoutesConnection();
            }
            if (this.mRE_transformedShape > 0 || tshapesSelected) {
                this.mRE_transformedShape = 0;
                this.cutOverlappingShapes();
            }
            if (this.mRE_transformedSafetyZone > 0) {
                for (let tRE of this.mRoomElements) {
                    if (tRE.type === "SafetyZone")
                        tRE.setMeshVisibility(1);
                }
                this.app.model.layers.setLayerVisible("room_safetyzones", this.SafetyZonesVisible);
                this.app.model.layers.setLayerVisible("room:PerimeterLine_Layer", this.SafetyZonesVisible);
                this.app.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", this.SafetyZonesVisible);
                this.app.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
                this.mRE_transformedSafetyZone = 0;
                this.cutOverlappingSZShapes();
            }
        };
        // tslint:disable-next-line:cyclomatic-complexity
        this.onToolStarted_Transform = (pTool, pToolManager) => {
            if (pTool === undefined) {
                return;
            }
            if (this.mApp.model.selection.length === 0)
                return;
            let tSelectedElements = this.mApp.model.selection;
            let tshapesSelected = false;
            let t_RE = null;
            let t_REselected = Array();
            for (let tElement of tSelectedElements) {
                if (tElement instanceof RoomElement) {
                    if (tElement.type === "Spline" || tElement.type === "Rectangle" || tElement.type === "Line" || tElement.type === "Circle"
                        || tElement.type === "CustomBuilding" || tElement.type === "Road" || tElement.type === "Hedge"
                        || tElement.type === "Arena") {
                        //tElement.setMeshVisibility(1);
                        tshapesSelected = true;
                        t_REselected.push(tElement);
                        tElement.setStatus("Transform");
                    }
                    if (tElement.type === "Floor") {
                        tElement.node.setEnabled(false);
                    }
                }
                // grid
                if (tElement instanceof RoomElement) {
                    if (tElement.type === "Grid") {
                        const tgrid = tElement;
                        const tgridChilds = this.app.model.getSubElementSelection(tgrid);
                        if (tgridChilds != null && tgrid.REforGrid_REF != null) {
                            tshapesSelected = true;
                            for (const tgridPoly of tgrid.REforGrid_REF) {
                                t_REselected.push(tgridPoly);
                                tgridPoly.setStatus("Transform");
                            }
                        }
                    }
                }
                if (tElement instanceof ArticleElement) {
                    let tBasketId = tElement.basketId;
                    let tRE = this.getRoomElementByBasketId(tBasketId);
                    if (tRE != null) {
                        t_RE = tRE;
                        t_REselected.push(tRE);
                        tshapesSelected = true;
                    }
                }
            }
            if (tshapesSelected && !this.mShapeToolStarted) {
                let t_REselectedSorted = new Array();
                if (t_REselected.length === 1) {
                    this.addOverlappingShapes([t_REselected[0]]);
                }
                else {
                    for (let i = this.mREShapes.length - 1; i >= 0; i--) {
                        let tRE = this.mREShapes[i];
                        if (contains(t_REselected, tRE))
                            t_REselectedSorted.push(tRE);
                    }
                    this.addOverlappingShapes(t_REselectedSorted);
                }
            }
        };
        // tslint:disable-next-line:cyclomatic-complexity
        this.onArticleAdded = (pArticle) => {
            if (this.mLoadingScene ||
                (pArticle == null || !(pArticle instanceof MainArticleElement)) ||
                (contains(this.mAddedMainArticles, pArticle))) {
                return;
            }
            if (pArticle.basketId != null) {
                this.mAddedMainArticles.push(pArticle);
                //let tRE = this.generateSafetyZones(tMainArticle.basketId);
                let tRE = this.addSafetyZoneToMainArticle(pArticle);
                if (tRE != null) {
                    tRE.collectSafetyZoneMeshes(pArticle, false);
                    let tmatID = "color" + this.mMaterialSplitStr + "#999999";
                    let tSZ_data = this.mSafetyZoneImportData.get(pArticle.basketId);
                    if (tSZ_data !== undefined) {
                        tmatID = tSZ_data.MaterialId;
                    }
                    tRE.setMaterialId(tmatID);
                    tRE.setBasketId(pArticle.basketId);
                    tRE.updateGeometry();
                    pArticle.updateTransformation();
                    pArticle.notifyChange(ElementChangedFlags.BoundingBox);
                    this.cutOverlappingSZShapes();
                }
            }
            else {
                for (let tRE of this.mRoomElements) {
                    let tRE_MA = tRE.getMainElement();
                    if (tRE_MA === pArticle) {
                        let tDummySafetyZone = new RoomElement(this, "DummySafetyZone");
                        if (tDummySafetyZone != null) {
                            tRE.setRE_reference(tDummySafetyZone);
                            tDummySafetyZone.setRE_reference(tRE);
                            this.app.model.addElement(tDummySafetyZone);
                        }
                        pArticle.eventRemoved.addListener(() => this.removeRE(pArticle));
                        pArticle.eventPropertiesChanged.addListener(() => this.mainArticlePropertiesChanged(pArticle, tRE));
                        break;
                    }
                }
            }
        };
        this.onArticleChanged = (pData) => {
            //console.log("article changed");
        };
        this.onBeforeCameraChanged = () => {
            this.setView();
            this._triggerHiddenWalls();
        };
        this._animateSwing = () => {
            if (this.mAnimatedNode.length === 0 || !this.mAnimationActive)
                return;
            for (let tAN of this.mAnimatedNode) {
                let t_animNode = tAN;
                t_animNode.Node.rotation.x = this.mRotationDelta;
                //t_animNode.MainArticle.updateTransformation();
                updateFrozenWorldMatrix(t_animNode.Node, true);
            }
            this.mRotationSum += this.mRotationDelta;
            if (this.mRotationSum > 0.5) {
                this.mRotationDelta = -(Math.abs(this.mRotationDelta));
            }
            if (this.mRotationSum < -0.5) {
                this.mRotationDelta = Math.abs(this.mRotationDelta);
            }
            //this.app.model.notifyModification();
            this.app.viewer.requestRenderFrame();
            setTimeout(this._animateSwing, this.mAnimationTimeOut);
        };
        this.onCameraChanged = () => {
            if (this.mView == null || this.mCurrentViewMode === this.mView.cameraControl.cameraMode)
                return;
            this.mCurrentViewMode = this.mView.cameraControl.cameraMode;
            this.mWallUpdateDepth = 0;
            this.mRouteUpdateDepth = 0;
            if (this.mView.cameraControl.cameraMode !== 7) {
                this.setTextPickPlane(true);
                if (this.mSkyBox != null)
                    this.mSkyBox.visibility = 0;
            }
            else {
                this.setTextPickPlane(false);
                if (this.mSkyBox != null && this.mSkyBoxVisible)
                    this.mSkyBox.visibility = 1;
            }
            for (let tWall of this.mWalls) {
                tWall.setHidden(false);
                tWall._updateVisibility();
            }
        };
        this.onSelectionChanged = () => {
            if (this.mApp.model.selection.length > 1 || this.mApp.model.selection.length === 0) {
                this.mApp.viewer.requestRenderFrame();
                return (true);
            }
            let tSelection = this.mApp.model.selection;
            if (tSelection[0] instanceof RoomElement) {
                let tRE = tSelection[0];
            }
            return (true);
        };
        this._CameraPathAdded = (pRoomManager) => {
            if (this.mAnimationStatus != null) {
                this.mAnimationStatus.stop();
                this.mAnimationStatus = null;
            }
        };
        this._CameraPathRemoved = (pRoomManager) => {
            if (this.mAnimationStatus != null) {
                this.mAnimationStatus.stop();
                this.mAnimationStatus = null;
                this.mAnimationPathRE = null;
            }
        };
        this._GeoMarkerMoved = () => {
            // console.log("Geo Marker moved.");
        };
        this.mApp = pApp;
        this.mScene = this.mApp.model.scene;
        this.mArticleManager = pArticleManager;
        this.mMatManager = this.app.model.materials;
        this.monAfterLoad = true;
        this.mfinalCutAfterLoad = false;
        this.mSceneIsLoading = false;
        this.mRoomSettings = new RoomSettings();
        this.mNotTriggerContentChanged = false;
        this.mAppLanguage = this.getLocaleLanguage();
        this.mLayerList = pLayerList;
        this.mAddedMainArticles = new Array();
        this.mLoadingScene = false;
        this.mUpdateOfmlArticles_active = false;
        this.mOutLineArray = new Array();
        this.mPropClasses = new Array();
        this.mTestAppMode = pTestAppMode;
        this.resources = new FallbackResources();
        this.mTouchDevice = false;
        this.mIsClearing = false;
        this.mcurrentKey = new Array();
        this.mInteractorState = 0;
        this.mInteractorState_CameraPath = InteractorMode.Default;
        this.mArticleLayerMeshes = new Array();
        this.mGenerateSafetyZonesActive = false;
        this.mSafetyZoneData = new Map();
        this.mSafetyZoneGenerationLevel = 0;
        this.mSafetyZoneImportData = new Map();
        this.mSafetyZonesVisible = false;
        this.onShowInputField = this.onShowDefaultInputField;
        this.onShowInputFieldText = this.onShowDefaultInputFieldText;
        this.changeWallLengthToRight = true;
        this.changeRouteLengthToRight = true;
        this.changeArea2DLengthToRight = true;
        this.mDataPath = pDataPath;
        if (pDataPath === "") {
            this.mDataPath = wcfConfig.dataPath;
        }
        this.mMaterialJsonURL = pMaterialJsonURL;
        this.mSoundsJsonURL = pSoundsJsonURL;
        this.mWalls = new Array();
        this.mMeasures = new Array();
        this.mWallUpdateDepth = 0;
        this.mWallLoadedDepth = 0;
        this.mPendingWallUpdates = new Set();
        this.mVisibility = VisibilityMode.Show3d;
        this.mWallDimensionsVisible = false;
        this.mHiddenWallsStatus = false;
        this.mWallOpacitiy = 1;
        this.mBuildingOpacity = 1;
        this.mRoomElements = new Array();
        this.mRoutes = new Array();
        this.mRouteUpdateDepth = 0;
        this.mPendingRouteUpdates = new Set();
        this.mRoutesVisible = true;
        this.mMeasureVisible = true;
        this.mAudioSpheresVisible = true;
        this.mAudioSpheresMute = false;
        this.mInteractionDepth = 0;
        this.mWallIdCounter = 0;
        this.mRouteIdCounter = 0;
        this.mREIdCounter = 0;
        this.mREGridIdCounter = 0;
        this.mArea2DIdCounter = 0;
        this.mTransformationDepth = 0;
        this.mTransformedElements = new Set();
        this.mRE_transformedShape = 0;
        this.mRE_transformedSafetyZone = 0;
        this.mCutRequestDepth = 0;
        this.mCutShapesDepth = 0;
        this.mMaterialLoading = false;
        this.mLoadedWalls = null;
        this.mLoadedWalls_deserialize = null;
        this.m2dLines = new Array();
        this.m2dPoints = new Array();
        this.mRooms = new Array();
        this.mfindRoomDepth = 0;
        this.mLocaleString = pLocaleString;
        if ((pProjectID === undefined || pProjectID.trim() === "") &&
            (this.mMaterialJsonURL == null || this.mMaterialJsonURL.trim() === ""))
            this.mProjectId = "egr";
        else
            this.mProjectId = pProjectID;
        this.mProjectManufacturer = "";
        this.mSeriesList = new Array();
        this.mSZ_standards = new Array();
        this.mSZ_standards_selected = "EN";
        this.mSZshapeVisibility = new Map();
        this.mShapeToolStarted = false;
        this.mFloors = new Array();
        this.mFloorColor = '#999999';
        this.mFloorsPolygons = new Map();
        this.mRoomFloorVisibility = true;
        this.mShowOpeningMirrorInteractor = true;
        this.mOverlappingShapes = new Array();
        this.mREShapes = new Array();
        this.m2dShapesCount = 0;
        this.mFindOverlappingShapes = false;
        this.mShapeHeight = 0.000;
        this.mBuildings = new Array();
        this.minitialBuildings = new Map();
        this.mCheckIntersectRoomElements = new Array();
        this.mFoundIntersectRoomElements = new Array();
        this.mGlobalFoundIntersectionList = new Array();
        this.mView = null;
        this.mCamLeftRight = new Array();
        this.mtmpTriangle = new Array();
        this.mEGRLayerVisibility = false;
        this.mRandomString = Util.HashString();
        this.mStandardColor = "#aaaaaa";
        this.mShapeKeepLineLength = false;
        this.mHillMaterials = new Map();
        this.mMaterialSplitStr = "_-_";
        this.createDefaultMaterials();
        this.mScene.getBoundingBoxRenderer().frontColor = Color3.FromHexString(this.app.theme.mainColorBright);
        this.mScene.getBoundingBoxRenderer().backColor = Color3.FromHexString(this.app.theme.mainColorBright);
        this.mScene.getBoundingBoxRenderer().showBackLines = false;
        this.mExtLoadedMaterialDefinitions = new Array();
        this.mExtLoadedMaterialCategory = new Array();
        this.mExtLoadedMaterials = new Array();
        this.mextMaterialisLoading = false;
        this.mExtLoadedSoundEffects = new Array();
        this.mExtLoadedSoundCategory = new Array();
        this.mextSoundEffectisLoading = false;
        this.getMaterialList();
        this.getSoundEffectList();
        this.mWallColorMaterialMap = new Map();
        this.mRouteColorMaterialMap = new Map();
        this.mExtMaterialsMap = new Map();
        this.mPictureFrameTypesArr = [];
        this.mTerrainTypePropList = [];
        this.mHillProfilePropList = [];
        this.mSpeedTypePropList = [];
        this.prepareValueLists();
        this.createPropClasses();
        this.createColorMaterial("#aaaaaa");
        this.mApp.document.eventBeforeClear.addListener(this.onBeforeClear);
        this.mApp.document.eventBeforeSave.addListener(this.onBeforeSafe);
        this.mApp.document.eventAfterSave.addListener(this.onAfterSafe);
        this.mApp.document.eventBeforeLoad.addListener(this.onBeforeLoad);
        this.mApp.document.eventAfterLoad.addListener(this.onAfterLoad);
        this.mApp.model.eventBeginTransformElements.addListener(this.onBeginTransform);
        this.mApp.model.eventEndTransformElements.addListener(this.onEndTransform);
        this.mApp.i18n.eventLanguageChanged.addListener(this.onLanguageChanged);
        this.mApp.i18n.eventLocaleChanged.addListener(this.onLocaleChanged);
        this.mApp.i18n.eventLengthUnitChanged.addListener(this.onLengthUnitChanged);
        this.mApp.model.eventSelectionChanged.addListener(this.onSelectionChanged);
        this.mArticleManager.eventArticleAdded.addListener(this.onArticleAdded);
        this.mArticleManager.eventArticleChanged.addListener(this.onArticleChanged);
        this.mApp.tools.eventToolStarted.addListener(this.onToolStarted);
        //this.eventRoomContentChanged.addListener(this._contentChanged);
        this.eventDeleteSelectionComplete.addListener(this._deleteSelectionComplete(this));
        this.eventInteractorMouseUpWOmove.addListener(this._InteractorClicked);
        this.eventInteractorClickedWithoutMove.addListener(this._InteractorClickedWOmove);
        this.eventNotEnoughWallSpaceLeft.addListener(this._NotEnoughWallSpaceLeft);
        this.eventAnimationStarted.addListener(this._AnimationStarted);
        this.eventAnimationPaused.addListener(this._AnimationPaused);
        this.eventAnimationEnded.addListener(this._AnimationEnded);
        this.eventCameraPathAdded.addListener(this._CameraPathAdded);
        this.eventCameraPathRemoved.addListener(this._CameraPathRemoved);
        this.eventGeoMarkerMoved.addListener(this._GeoMarkerMoved);
        // BackgroundSound.eventSoundListLoaded.addListener(this._SoundListLoaded);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this._keyEvents();
        this.startPathAnimation = this.startPathAnimation.bind(this);
        this.mApp.registerModule(new AppModule(this));
        Wall._registerClass(this);
        Route._registerClass(this);
        RoomElement._registerClass(this);
        Measure._registerClass(this);
        GroupElement._registerClass(this);
        this.mApp.model.layers.setLayerVisible("room:Building_Layer", false);
        this.eventBuildingLayerVisibilityChanged.trigger({});
        this.setSZLayerVisibility(pSafetyZonesEnabled);
        this.mApp.model.layers.setLayerVisible("room_safetyzones", pSafetyZonesEnabled);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLine_Layer", pSafetyZonesEnabled);
        this.mApp.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", pSafetyZonesEnabled);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
        this.setView();
        WallCrossHair.createCrossHair(this.mScene, this);
        this.detectTouchDevice();
        this.setSZLayerVisibility(false);
        this.mApp.model.notifyModification();
        this.mApp.viewer.requestRenderFrame();
        this.mSkyBoxVisible = false;
        if (penableSkyBox) {
            let tcamMaxZ = pCameraMaxZ;
            if (this.view != null) {
                tcamMaxZ = this.view.camera.maxZ * 8;
            }
            // 2021-11-17 [NHA]:
            // don't work, because the value of maxZ will be setted after
            // roomManager initialization!
            //
            // if (this.view != null){
            //     tcamMaxZ = this.view.camera.maxZ * 8;
            // }
            this.mSkyBoxVisible = true;
            this.mSkyBox = CreateSphere("skyBox", { segments: 40, diameter: tcamMaxZ, slice: 0.5 }, this.mScene);
            this.mSkyBox.flipFaces(true);
            ShadowGenerator.disableMeshShadow(this.mSkyBox);
            this.setSkyBox();
        }
        this.mSkyBoxMaterials = [];
        this.createSkyMaterials();
        this.createStandardColor();
        this.mClickedWOmove = false;
        // 2d GUI
        this.m2DGUI = AdvancedDynamicTexture.CreateFullscreenUI("UI");
        this.mEOX_CAT = null;
        this.mEOX_MAT = null;
        this.mEOX_PROD = null;
        OPLeoxImporter.loadMatchingTable(this);
        Util.Initialize(this.mApp);
        this.mcamLogo = null;
        this.createCamLogo();
        this.mAnimationActive = false;
        this.mAnimatedNode = [];
        this.mRotationSum = 0;
        this.mRotationDelta = 0.02;
        this.mAnimationTimeOut = 10;
        this.mAnimations = [];
        this.mHideAnimationPath = pHideAnimationPath;
        this.mcamData = {};
        this.mcamData.cameraPositions = [];
        this.mcamData.cameraTargets = [];
        this.mCamPathVisibility = true;
        this.mcamPath_ResetView = true;
        // this.mBGSound = new BackgroundSound(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }
    static shape2D_Y_Position() {
        return (0);
    }
    static SafetyZone_Y_Position() {
        return (0.015);
    }
    get version() {
        //will be replaced during build process
        return ("@@WCF_ROOM_VERSION");
    }
    get buildInfo() {
        //will be replaced during build process
        return ("@@WCF_ROOM_BUILDINFO");
    }
    get app() {
        return (this.mApp);
    }
    get MatManager() {
        return (this.mMatManager);
    }
    // public get BgSound(): BackgroundSound
    // {
    //     return (this.mBGSound);
    // }
    get articleManager() {
        return (this.mArticleManager);
    }
    get appLanguage() {
        return (this.mAppLanguage);
    }
    get touchDevice() {
        return (this.mTouchDevice);
    }
    get isClearing() {
        return (this.mIsClearing);
    }
    get isSceneLoading() {
        return (this.mSceneIsLoading);
    }
    get wallsLoading() {
        return (this.monAfterLoad);
    }
    get activeTool() {
        return (this.mActiveTool);
    }
    get view() {
        if (this.mApp == null || this.mApp.viewer == null)
            return (null);
        return (this.mApp.viewer.view);
    }
    get hoverView() {
        if (this.mApp == null || this.mApp.viewer == null)
            return (null);
        return (this.mApp.viewer.view);
    }
    get viewMode() {
        return (this.mCurrentViewMode);
    }
    get roomSettings() {
        return (this.mRoomSettings);
    }
    get interactorState() {
        return (this.mInteractorState);
    }
    get interactorState_CameraPath() {
        return (this.mInteractorState_CameraPath);
    }
    get GUI2d() {
        return (this.m2DGUI);
    }
    get TestAppMode() {
        return (this.mTestAppMode);
    }
    get isCollectingWallUpdates() {
        return (this.mWallUpdateDepth > 0);
    }
    get isCollectingRouteUpdates() {
        return (this.mRouteUpdateDepth > 0);
    }
    get isCollectingREUpdates() {
        return (this.mREUpdateDepth > 0);
    }
    get isInteracting() {
        return (this.mInteractionDepth > 0);
    }
    get walls() {
        return (this.mWalls);
    }
    get transformationDepth() {
        return (this.mTransformationDepth);
    }
    get localeString() {
        return (this.mLocaleString);
    }
    get ProjectId() {
        return (this.mProjectId);
    }
    get PropClasses() {
        return (this.mPropClasses);
    }
    get RoomElements() {
        return (this.mRoomElements);
    }
    get BuildingOpacity() {
        return (this.mBuildingOpacity);
    }
    get Shapes2D() {
        return (this.mREShapes);
    }
    get ShapeHeight() {
        return (this.mShapeHeight);
    }
    get shiftPressed() {
        if (contains(this.mcurrentKey, "shift"))
            return (true);
        return (false);
    }
    get soundJsonURL() {
        return (this.mSoundsJsonURL);
    }
    get audio_local_effects() {
        return (this.mAudio_local_effects);
    }
    get GenerateSafetyZoneActive() {
        return (this.mGenerateSafetyZonesActive);
    }
    get SafetyZoneImportData() {
        return (this.mSafetyZoneImportData);
    }
    get SafetyZoneData() {
        return (this.mSafetyZoneData);
    }
    get SafetyZonesVisible() {
        return (this.mSafetyZonesVisible);
    }
    get ManufacturerSeriesList() {
        return (this.mSeriesList);
    }
    get ProjectManufacturer() {
        return (this.mProjectManufacturer);
    }
    get SZ_standard_selected() {
        return (this.mSZ_standards_selected);
    }
    get floors() {
        return (this.mFloors);
    }
    get floorColor() {
        return (this.mFloorColor);
    }
    get measures() {
        return (this.mMeasures);
    }
    get hideAnimationPath() {
        return (this.mHideAnimationPath);
    }
    get hideWalls() {
        return (this.mHiddenWallsStatus);
    }
    get routes() {
        return (this.mRoutes);
    }
    get RoutesVisible() {
        return (this.mRoutesVisible);
    }
    get AudioSpheresVisible() {
        return (this.mAudioSpheresVisible);
    }
    get audioSpheresMute() {
        return (this.mAudioSpheresMute);
    }
    get DimensionLinesVisible() {
        return (this.mMeasureVisible);
    }
    get WallDimensionsVisible() {
        return (this.mWallDimensionsVisible);
    }
    get MirrorInteractorVisible() {
        return (this.mShowOpeningMirrorInteractor);
    }
    get wall2dMaterialDarkGrey() {
        return (this.mWall2dMaterialDarkGrey);
    }
    get wall2dMaterialGrey() {
        return (this.mWall2dMaterialGrey);
    }
    get wall2dMaterialSelected() {
        return (this.mWall2dMaterialSelected);
    }
    get camLogo() {
        return (this.mcamLogo);
    }
    get wallOpacity() {
        return (this.mWallOpacitiy);
    }
    get route2dMaterial() {
        return (this.mRoute2dMaterial);
    }
    get ShapeKeepLineLength() {
        return (this.mShapeKeepLineLength);
    }
    get FloorsPolygons() {
        return (this.mFloorsPolygons);
    }
    get firstextMaterialCategory() {
        let tRes = "";
        if (this.mExtLoadedMaterialCategory.length > 0)
            tRes = this.mExtLoadedMaterialCategory[0].id;
        return (tRes);
    }
    get materialSplitStr() {
        return (this.mMaterialSplitStr);
    }
    get MaterialLoading() {
        return (this.mMaterialLoading);
    }
    get mat_white() {
        return (this.mMat_white);
    }
    get mat_theme_bright() {
        return (this.mMat_theme_bright);
    }
    LoadedMaterial(pIdx) {
        return (this.mExtLoadedMaterials[pIdx]);
    }
    get externalMaterial() {
        return (this.mExtLoadedMaterials);
    }
    get externalMaterialMap() {
        return (this.mExtMaterialsMap);
    }
    get externalMaterialList() {
        return (this.mExtLoadedMaterialDefinitions);
    }
    get externalSoundEffectList() {
        return (this.mExtLoadedSoundEffects);
    }
    get skyBoxMaterial() {
        return (this.mSkyBoxMaterial);
    }
    get skyBoxMaterials() {
        return (this.mSkyBoxMaterials);
    }
    get wallDefaultMaterial() {
        return (this.mMatManager.getMaterial("color" + this.materialSplitStr + "#ffffff"));
    }
    get EGRLayerVisibility() {
        return (this.mEGRLayerVisibility);
    }
    get BuildingsLayerVisibility() {
        return (this.mApp.model.layers.isLayerVisible("room:Building_Layer"));
    }
    get OverlappingShapesActive() {
        return (this.mFindOverlappingShapes);
    }
    get CheckIntersectRoomElements() {
        return (this.mCheckIntersectRoomElements);
    }
    get PictureFrameTypes() {
        return (this.mPictureFrameTypesArr);
    }
    get TerrainTypePropList() {
        return (this.mTerrainTypePropList);
    }
    get HillProfilePropList() {
        return (this.mHillProfilePropList);
    }
    get SpeedTypePropList() {
        return (this.mSpeedTypePropList);
    }
    get hill_GaussCurve() {
        return (this.mHill_GaussCurve);
    }
    /**
     * Contains all 2d lines of ground plan (inner/outer room)
     */
    get2dLines() {
        this.m2dLines = [];
        for (let tWall of this.mWalls) {
            this.m2dLines = this.m2dLines.concat(tWall.cutLines);
        }
        return this.m2dLines;
    }
    /**
     * Contains all 2d points of ground plan (inner/outer room)
     */
    get2dPoints() {
        return this.m2dPoints;
    }
    /**
     * Contains all 2d points of ground plan (inner/outer room)
     * Without duplicates.
     */
    add2dPoint(pPoint) {
        let texist = false;
        for (let tPoint of this.m2dPoints) {
            if (Util.samePointsV2(tPoint, pPoint, 0.0001))
                texist = true;
        }
        if (!texist)
            this.m2dPoints.push(pPoint);
    }
    /**
    * Tries to find possible floors of wall construction.
     */
    // tslint:disable-next-line:cyclomatic-complexity
    async findRoom() {
        if (this.mfindRoomDepth > 0)
            return;
        this.mfindRoomDepth++;
        this.m2dLines = [];
        this.m2dPoints = [];
        for (let tWall of this.mWalls) {
            tWall.updateGeometry();
        }
        for (let tWall of this.mWalls) {
            this.m2dLines = this.m2dLines.concat(tWall.cutLines);
        }
        this.mRooms = [];
        this.sortLines(null, this.m2dLines);
        this.removeFloorUnderWalls();
        // no rooms recognized; remove all floors
        if (this.mRooms.length === 0) {
            this.removeFloors();
            this.mfindRoomDepth = 0;
            return;
        }
        let tcurrentRooms = new Array();
        for (let tRoom of this.mRooms) {
            let tRoomWalls = this.getWallsFromRoomPolygon(tRoom);
            let tsortedWallIds = this.getSortedWallIds(tRoomWalls);
            let twallIds = tsortedWallIds.toString();
            let tfloorEntry = this.mFloorsPolygons.get(twallIds);
            let tTransparent = this.roomSettings.Globals.floorPlanImported;
            if (tfloorEntry === undefined) {
                let tmatSET = {};
                this.mFloorsPolygons.set(twallIds, { floor: tRoom, floorRE: null, materialSet: tmatSET,
                    transparent: this.roomSettings.Globals.floorPlanImported });
                let tPromise = await this.insertFloor(tRoom).then((pFloor) => {
                    if (pFloor != null) {
                        tcurrentRooms.push(pFloor);
                        this.mFloorsPolygons.set(twallIds, { floor: tRoom, floorRE: pFloor, materialSet: pFloor.materialSet,
                            transparent: this.roomSettings.Globals.floorPlanImported });
                        pFloor.setWallIds(twallIds);
                        pFloor.Params.floorTransparent = this.roomSettings.Globals.floorPlanImported;
                        let tPolygonCenter = Util.getCenterofPolygon(Util.to3dArray(tRoom));
                        let tcenteredPoints = Util.centerPolygon(Util.to3dArray(tRoom), new Vector3(0, 0, 0));
                        let tFloorGeo = pFloor.geometry;
                        tFloorGeo.setPoints(Util.to2dArray(tcenteredPoints));
                        pFloor.setPosition(tPolygonCenter);
                        pFloor.updateGeometry();
                    }
                });
            }
            else {
                let tFloor = tfloorEntry.floorRE;
                let tMatSet = {};
                if (tFloor != null) {
                    tMatSet = tFloor.materialSet;
                }
                let ttransparent = tfloorEntry.transparent;
                if (contains(this.mRoomElements, tFloor)) {
                    if (tFloor != null) {
                        tcurrentRooms.push(tFloor);
                        let tPolygonCenter = Util.getCenterofPolygon(Util.to3dArray(tRoom));
                        let tcenteredPoints = Util.centerPolygon(Util.to3dArray(tRoom), new Vector3(0, 0, 0));
                        let tFloorGeo = tFloor.geometry;
                        tFloorGeo.setPoints(Util.to2dArray(tcenteredPoints));
                        tFloor.setPosition(tPolygonCenter);
                        tFloor.updateGeometry();
                    }
                }
                else {
                    let tPromise = await this.insertFloor(tRoom).then((pFloor) => {
                        if (pFloor != null) {
                            tcurrentRooms.push(pFloor);
                            pFloor.setWallIds(twallIds);
                            this.mFloorsPolygons.set(twallIds, { floor: tRoom, floorRE: pFloor, materialSet: tMatSet, transparent: ttransparent });
                            pFloor.Params.floorTransparent = ttransparent;
                            let tPolygonCenter = Util.getCenterofPolygon(Util.to3dArray(tRoom));
                            let tcenteredPoints = Util.centerPolygon(Util.to3dArray(tRoom), new Vector3(0, 0, 0));
                            let tFloorGeo = pFloor.geometry;
                            tFloorGeo.setPoints(Util.to2dArray(tcenteredPoints));
                            pFloor.setPosition(tPolygonCenter);
                            pFloor.setMaterialSet(tMatSet);
                            this.checkMaterial(tMatSet).then((pResult) => {
                                if (pResult === 1 || pResult === 2) {
                                    pFloor.setMaterialValue();
                                    pFloor.updateGeometry();
                                }
                            });
                        }
                    });
                }
            }
        }
        // remove old floors
        let tfloorRemove = [];
        for (let tRE of this.mFloors) {
            if (tRE.type !== "Floor")
                continue;
            if (!contains(tcurrentRooms, tRE)) {
                tfloorRemove.push(tRE);
            }
        }
        for (let tRE of tfloorRemove) {
            this.mApp.model.removeElement(tRE);
            removeElement(this.mFloors, tRE);
        }
        this.app.viewer.requestRenderFrame();
        this.mfindRoomDepth = 0;
        return (Promise.resolve);
    }
    emptyIntersectionLists(pPushToGlobalFoundList = false) {
        if (pPushToGlobalFoundList)
            this.mGlobalFoundIntersectionList.push(this.mFoundIntersectRoomElements);
        this.mCheckIntersectRoomElements = [];
        this.mFoundIntersectRoomElements = [];
    }
    exportMaterialJSON() {
        this.ShowInputFieldText("Please enter node to export:", "IBA Materialwelten").then((pInput) => {
            if (pInput != null) {
                this.EOX2JSON(pInput);
            }
        });
    }
    updateIntersectRoomElements() {
        for (let tRE of this.mFoundIntersectRoomElements)
            tRE.updateGeometry();
    }
    moveREShape_sendToBack() {
        let tselArray = this.mApp.model.selection;
        if (tselArray.length > 1) {
            return;
        }
        let tSelection = null;
        if (tselArray[0] instanceof ArticleElement) {
            let tBasketId = tselArray[0].basketId;
            tSelection = this.getRoomElementByBasketId(tBasketId);
        }
        else {
            tSelection = tselArray[0];
        }
        if (tSelection == null || !(tSelection instanceof RoomElement)) {
            return;
        }
        let tRE = this.getGridSubSelection(tSelection);
        let tIdx = this.mREShapes.indexOf(tRE);
        if (tIdx !== -1 && tIdx > 0) {
            Util.array_move(this.mREShapes, tIdx, 0);
        }
        if (tRE.type === "SafetyZone") {
            this.reOrderShapes();
            this.cutOverlappingSZShapes();
        }
        else {
            this.cutOverlappingShapes();
        }
        this.app.viewer.requestRenderFrame();
    }
    moveREShape_backward() {
        let tselArray = this.mApp.model.selection;
        if (tselArray.length > 1)
            return;
        let tSelection = null;
        if (tselArray[0] instanceof ArticleElement) {
            let tBasketId = tselArray[0].basketId;
            tSelection = this.getRoomElementByBasketId(tBasketId);
        }
        else {
            tSelection = tselArray[0];
        }
        if (tSelection == null || !(tSelection instanceof RoomElement)) {
            return;
        }
        let tRE = this.getGridSubSelection(tSelection);
        let tIdx = this.mREShapes.indexOf(tRE);
        if (tIdx !== -1 && tIdx > 0) {
            Util.array_move(this.mREShapes, tIdx, tIdx - 1);
        }
        if (tRE.type === "SafetyZone") {
            this.reOrderShapes();
            this.cutOverlappingSZShapes();
        }
        else {
            this.cutOverlappingShapes();
        }
        this.app.viewer.requestRenderFrame();
    }
    moveREShape_forward() {
        let tselArray = this.mApp.model.selection;
        if (tselArray.length > 1)
            return;
        let tSelection = null;
        if (tselArray[0] instanceof ArticleElement) {
            let tBasketId = tselArray[0].basketId;
            tSelection = this.getRoomElementByBasketId(tBasketId);
        }
        else {
            tSelection = tselArray[0];
        }
        if (tSelection == null || !(tSelection instanceof RoomElement)) {
            return;
        }
        let tRE = this.getGridSubSelection(tSelection);
        let tIdx = this.mREShapes.indexOf(tRE);
        if (tIdx !== -1 && tIdx < this.mREShapes.length - 1) {
            Util.array_move(this.mREShapes, tIdx, tIdx + 1);
        }
        if (tRE.type === "SafetyZone") {
            this.reOrderShapes();
            this.cutOverlappingSZShapes();
        }
        else {
            this.cutOverlappingShapes();
        }
        this.app.viewer.requestRenderFrame();
    }
    moveREShape_sendToFront() {
        let tselArray = this.mApp.model.selection;
        if (tselArray.length > 1)
            return;
        let tSelection = null;
        if (tselArray[0] instanceof ArticleElement) {
            let tBasketId = tselArray[0].basketId;
            tSelection = this.getRoomElementByBasketId(tBasketId);
        }
        else {
            tSelection = tselArray[0];
        }
        if (tSelection == null || !(tSelection instanceof RoomElement)) {
            return;
        }
        let tRE = this.getGridSubSelection(tSelection);
        let tIdx = this.mREShapes.indexOf(tRE);
        if (tIdx !== -1 && tIdx < this.mREShapes.length - 1) {
            Util.array_move(this.mREShapes, tIdx, this.mREShapes.length - 1);
        }
        if (tRE.type === "SafetyZone") {
            this.reOrderShapes();
            this.cutOverlappingSZShapes();
        }
        else {
            this.cutOverlappingShapes();
        }
        this.app.viewer.requestRenderFrame();
    }
    _align_Right() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.align_Right();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_Left() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.align_Left();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_Bottom() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.align_Bottom();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_Top() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.align_Top();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_CenterHorizontal() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.CenterHorizontal();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_CenterVertical() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tCmd.initialize(tSel);
        tCmd.CenterVertical();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_DistributeHorizontal() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tSel = tCmd.getSortedOrder_X(tSel);
        tCmd.initialize(tSel);
        tCmd.distribute_H();
        this.mApp.commands.executeCommand(tCmd);
    }
    _align_DistributeVertical() {
        let tSel = this.getAlignSelection();
        let tCmd = new AlignElements(this);
        tSel = tCmd.getSortedOrder_Z(tSel);
        tCmd.initialize(tSel);
        tCmd.distribute_V();
        this.mApp.commands.executeCommand(tCmd);
    }
    generateColorPreviewImage(pHexColor) {
        if (this.mColorPreviewCanvas == null) {
            this.mColorPreviewCanvas = document.createElement("canvas");
            this.mColorPreviewCanvas.width = 40;
            this.mColorPreviewCanvas.height = 40;
        }
        let tCtx = this.mColorPreviewCanvas.getContext("2d");
        if (tCtx != null) {
            tCtx.fillStyle = pHexColor;
            tCtx.fillRect(0, 0, this.mColorPreviewCanvas.width, this.mColorPreviewCanvas.height);
        }
        return (this.mColorPreviewCanvas.toDataURL("image/jpeg"));
    }
    group() {
        let tCmd = new Group(this, this.mApp.model.selection);
        this.mApp.commands.executeCommand(tCmd);
    }
    ungroup() {
        let tCmd = new UnGroup(this, this.mApp.model.selection);
        if (tCmd.isAvailable())
            this.mApp.commands.executeCommand(tCmd);
    }
    setEOX_catalog(pFileContent) {
        this.mEOX_CAT = pFileContent;
    }
    setEOX_materials(pFileContent) {
        this.mEOX_MAT = pFileContent;
    }
    setEOX_productInfo(pFileContent) {
        this.mEOX_PROD = pFileContent;
    }
    setElementEnabled(pEnabled) {
        if (this.app.model.elements.length > 0) {
            let tRE = this.app.model.elements[0];
            tRE.node.setEnabled(pEnabled);
            this.app.model.notifyModification();
            this.app.viewer.requestRenderFrame();
        }
    }
    setInitialTextureScaleValues(pMat, pMaterialSet) {
        if (!pMaterialSet.setInit) {
            if (pMat.albedoTexture != null) {
                const talbedoTexture = pMat.albedoTexture;
                pMaterialSet.scaleU_init = talbedoTexture.uScale;
                pMaterialSet.scaleU = talbedoTexture.uScale;
                pMaterialSet.scaleV_init = talbedoTexture.vScale;
                pMaterialSet.scaleV = talbedoTexture.vScale;
                pMaterialSet.setInit = true;
            }
        }
    }
    setInteractorState(pStateIdx) {
        this.mInteractorState = pStateIdx;
        this.mShapeKeepLineLength = this.mInteractorState === 1 ? true : false;
        if (this.mApp.model.selection.length !== 1)
            return;
        let tElement = this.mApp.model.selection[0];
        if (!(tElement instanceof RoomElement))
            return;
        this.mApp.model.setSelection([tElement]);
    }
    setInteractorState_CameraPath(pStateIdx) {
        this.mInteractorState_CameraPath = pStateIdx;
        if (this.mApp.model.selection.length !== 1) {
            return;
        }
        const tcamPath = this.getCameraPath();
        if (tcamPath != null && tcamPath.isSelected) {
            tcamPath.updateGeometry();
            this.mApp.model.setSelection([tcamPath]);
        }
    }
    setShapeKeepLineLength(pKeep) {
        this.mShapeKeepLineLength = pKeep;
    }
    shapeToolStarted() {
        this.mShapeToolStarted = true;
    }
    shapeToolStopped() {
        this.mShapeToolStarted = false;
    }
    setRefPointVisibility(pVisible) {
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "Spline" || tRE.type === "Line" || tRE.type === "Rectangle")
                tRE.setCrossHairVisibility(pVisible);
            tRE.updateGeometry(false);
        }
        this.cutOverlappingShapes();
        this.app.viewer.requestRenderFrame();
    }
    setWallsLoading(pLoading) {
        //this.monAfterLoad = pLoading;
    }
    finishWallLoading(pLoading) {
        this.monAfterLoad = pLoading;
        if (pLoading === true) {
            if (this.mWalls.length > 0) {
                this.cutOverlappingShapes(null, false, true);
            }
        }
    }
    finalCutAfterLoad() {
        this.mCutRequestDepth++;
        if (this.mCutRequestDepth === this.m2dShapesCount) {
            this.monAfterLoad = false;
            this.cutOverlappingShapes(null, false, true);
        }
    }
    findWallsInViewArea(pTriangle, pWall) {
        if (this.mView == null)
            return;
        this.calcCamLeftRight();
        this.mtmpTriangle = [];
        this.mtmpTriangle.push(pTriangle[0]);
        this.mtmpTriangle.push(pTriangle[1]);
        this.mtmpTriangle.push(this.mCamLeftRight[0]);
        this.mtmpTriangle.push(this.mCamLeftRight[1]);
        this.mtmpTriangle = Util.correctPolygonDirection2(this.mtmpTriangle);
        // check self-intersection
        let tPoints = new Array();
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mtmpTriangle.length; i++) {
            tPoints.push(new CSG.Vector2D(this.mtmpTriangle[i].x, this.mtmpTriangle[i].y));
        }
        let tCAGshape = CAG.fromPointsNoCheck(tPoints);
        if (tCAGshape.isSelfIntersecting())
            return;
        for (let tWall of this.mWalls) {
            if (tWall === pWall)
                continue;
            if (Util.wallInTriangle(tWall.startPoint, tWall.endPoint, this.mtmpTriangle)) {
                tWall.setHidden(true);
            }
        }
        this.mtmpTriangle = [];
    }
    countGrassMats(pCAGType) {
        for (let tRE of this.mREShapes) {
            if (!(tRE.type === "Line" && tRE.GridRE_REF != null)) {
                continue;
            }
            if (pCAGType === GrassMatCAGType.normal) {
                tRE.countGridFields(tRE.CAG);
            }
            if (pCAGType === GrassMatCAGType.notVisible) {
                tRE.countGridFields(tRE.CAG_notVisible);
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapesCAG(excludeAllSZ = false, excludeColoredSZForValues = false, excludeHedges = false) {
        if (!this.mApp.tools.isDefaultToolRunning())
            return;
        if (this.mREShapes.length === 0)
            return;
        let tRElist = new Array();
        let tRElist_notCut = new Array();
        // 5. check normal 2d shapes Level "manual"
        for (const tRE of this.mREShapes) {
            const tnodeH = Number(tRE.node.position.y.toFixed(3));
            if ((tRE.type === "Rectangle" || tRE.type === "Spline" ||
                tRE.type === "Line" || tRE.type === "Circle" || tRE.type === "Arena") &&
                (!Util.numbersEqual(tnodeH, RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y) &&
                    !Util.numbersEqual(tnodeH, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y) &&
                    tRE.shapeLevel === ShapeLevel.manual)) {
                tRElist_notCut.push(tRE);
                if (tRE.CAGoriginal != null) {
                    tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
            }
        }
        // 4. check normal 2d shapes Level "default"
        for (const tRE of this.mREShapes) {
            const tnodeH = Number(tRE.node.position.y.toFixed(3));
            if ((tRE.type === "Rectangle" || tRE.type === "Spline" ||
                tRE.type === "Line" || tRE.type === "Circle" || tRE.type === "Arena") &&
                (Util.numbersEqual(tnodeH, RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y)
                    && tRE.shapeLevel === ShapeLevel.default)) {
                tRElist.push(tRE);
                if (tRE.CAGoriginal != null) {
                    tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
                if (tRE.type === "Arena") {
                    if (tRE.CAG_Arena_PlayField != null) {
                        tRE.setCAG_notVisible_Arena_InField(tRE.CAG_Arena_PlayField);
                    }
                    if (tRE.CAG_Arena_SideSegments != null) {
                        tRE.setCAG_notVisible_Arena_SideSegments(tRE.CAG_Arena_SideSegments);
                    }
                    if (tRE.CAG_Arena_TrackLines != null) {
                        tRE.setCAG_notVisible_Arena_TrackLines(tRE.CAG_Arena_TrackLines);
                    }
                    if (tRE.CAG_Arena_RunningTracks != null) {
                        tRE.setCAG_notVisible_Arena_RunningTracks(tRE.CAG_Arena_RunningTracks);
                    }
                }
            }
            else {
                if ((tRE.type === "Rectangle" || tRE.type === "Spline" ||
                    tRE.type === "Line" || tRE.type === "Circle") &&
                    (tRE.CAGoriginal != null)) {
                    tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
                if (tRE.type === "Arena") {
                    if (tRE.CAG_Arena_PlayField != null) {
                        tRE.setCAG_notVisible_Arena_InField(tRE.CAG_Arena_PlayField);
                    }
                    if (tRE.CAG_Arena_SideSegments != null) {
                        tRE.setCAG_notVisible_Arena_SideSegments(tRE.CAG_Arena_SideSegments);
                    }
                    if (tRE.CAG_Arena_TrackLines != null) {
                        tRE.setCAG_notVisible_Arena_TrackLines(tRE.CAG_Arena_TrackLines);
                    }
                    if (tRE.CAG_Arena_RunningTracks != null) {
                        tRE.setCAG_notVisible_Arena_RunningTracks(tRE.CAG_Arena_RunningTracks);
                    }
                }
            }
        }
        // 3. check Safety zones
        if (this.mApp.model.layers.isLayerVisible("room_safetyzones")) {
            for (const tRE of this.mRoomElements) {
                if (tRE.type === "SafetyZone") {
                    tRElist.push(tRE);
                    if (tRE.CAGoriginal != null)
                        tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
            }
        }
        // 1. check road
        for (const tRE of this.mREShapes) {
            if (tRE.type === "Road") {
                const tposY = tRE.position.y;
                if (Util.numbersEqual(tposY, 0)) {
                    tRElist.push(tRE);
                }
                if (tRE.CAGoriginal != null)
                    tRE.setCAG_notVisible(tRE.CAGoriginal);
            }
        }
        // 2. check normal 2d shapes Level "OnTop"
        for (const tRE of this.mREShapes) {
            const tnodeH = Number(tRE.node.position.y.toFixed(3));
            if ((tRE.type === "Rectangle" || tRE.type === "Spline" ||
                tRE.type === "Line" || tRE.type === "Circle" || tRE.type === "Arena") &&
                (Util.numbersEqual(tnodeH, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y)
                    && tRE.shapeLevel === ShapeLevel.OnTop)) {
                if (!contains(tRElist, tRE)) {
                    tRElist.push(tRE);
                }
                if (tRE.CAGoriginal != null) {
                    tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
                if (tRE.CAG_Arena_PlayField != null) {
                    tRE.setCAG_notVisible_Arena_InField(tRE.CAG_Arena_PlayField);
                }
                if (tRE.CAG_Arena_SideSegments != null) {
                    tRE.setCAG_notVisible_Arena_SideSegments(tRE.CAG_Arena_SideSegments);
                }
                if (tRE.CAG_Arena_TrackLines != null) {
                    tRE.setCAG_notVisible_Arena_TrackLines(tRE.CAG_Arena_TrackLines);
                }
                if (tRE.CAG_Arena_RunningTracks != null) {
                    tRE.setCAG_notVisible_Arena_RunningTracks(tRE.CAG_Arena_RunningTracks);
                }
            }
        }
        // 0. check hedge
        if (!excludeHedges) {
            for (const tRE of this.mREShapes) {
                if (tRE.type === "Hedge") {
                    const tposY = tRE.position.y;
                    if (Util.numbersEqual(tposY, 0)) {
                        tRElist.push(tRE);
                    }
                    if (tRE.CAGoriginal != null)
                        tRE.setCAG_notVisible(tRE.CAGoriginal);
                }
            }
        }
        if (tRElist.length === 1) {
            tRElist[0].setCAG_notVisible(tRElist[0].CAGoriginal);
            return;
        }
        for (let i = 0; i < tRElist.length - 1; i++) {
            let tRE0 = tRElist[i];
            if (tRE0.type === "SafetyZone" && !this.mApp.model.layers.isLayerVisible("room_safetyzones")) {
                continue;
            }
            for (let j = i + 1; j < tRElist.length; j++) {
                let tRE1 = tRElist[j];
                if (tRE0 == null || tRE1 == null) {
                    continue;
                }
                if (tRE1.type === "SafetyZone" &&
                    (!this.mApp.model.layers.isLayerVisible("room_safetyzones")) ||
                    !tRE1.get_SafetyZone_Visibility()) {
                    continue;
                }
                if (excludeColoredSZForValues &&
                    (tRE1.type === "SafetyZone" && tRE1.materialSet.category === "color" &&
                        tRE0.type !== "SafetyZone")) {
                    continue;
                }
                if ((tRE0.type == "Line" && tRE0.GridRE_REF != null) &&
                    tRE1.type == "SafetyZone" &&
                    excludeAllSZ) {
                    continue;
                }
                let tRE0pos = new Vector3(0, 0, 0);
                let tRE1pos = new Vector3(0, 0, 0);
                let tRErot0 = new Vector3(0, 0, 0);
                let tRErot1 = new Vector3(0, 0, 0);
                let tRE0rotY = 0;
                let tRE1rotY = 0;
                if (tRE0.type === "SafetyZone" ||
                    (tRE0.type === "Line" && tRE0.GridRE_REF != null)) {
                    if (tRE0 != null) {
                        let tME = tRE0.getMainElement();
                        if (tME != null) {
                            let tTM = tRE0.node.getWorldMatrix();
                            tRE0pos = tTM.getTranslation();
                            if (tME.node.rotationQuaternion != null) {
                                tRErot0 = tME.node.rotationQuaternion.toEulerAngles();
                                tRE0rotY = tRErot0.y * (180 / Math.PI);
                            }
                        }
                    }
                }
                else {
                    tRE0pos = tRE0.position.clone();
                    if (tRE0.node.rotationQuaternion != null) {
                        tRErot0 = tRE0.node.rotationQuaternion.toEulerAngles();
                        tRE0rotY = tRErot0.y * (180 / Math.PI);
                    }
                }
                if (tRE1.type === "SafetyZone" ||
                    (tRE1.type === "Line" && tRE1.GridRE_REF != null)) {
                    if (tRE1 != null) {
                        let tME = tRE1.getMainElement();
                        if (tME != null) {
                            let tTM = tRE1.node.getWorldMatrix();
                            tRE1pos = tTM.getTranslation();
                            if (tME.node.rotationQuaternion != null) {
                                tRErot1 = tME.node.rotationQuaternion.toEulerAngles();
                                tRE1rotY = tRErot1.y * (180 / Math.PI);
                            }
                        }
                    }
                }
                else {
                    tRE1pos = tRE1.position.clone();
                    if (tRE1.node.rotationQuaternion != null) {
                        tRErot1 = tRE1.node.rotationQuaternion.toEulerAngles();
                        tRE1rotY = tRErot1.y * (180 / Math.PI);
                    }
                }
                if (tRE0.type !== "Arena" && tRE1.type !== "Arena") {
                    this.cutOverlappingShapesCAG_shape_shape(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type !== "Arena" && tRE1.type === "Arena") {
                    this.cutOverlappingShapesCAG_shape_arena(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type === "Arena" && tRE1.type !== "Arena") {
                    this.cutOverlappingShapesCAG_arena_shape(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type === "Arena" && tRE1.type === "Arena") {
                    this.cutOverlappingShapesCAG_arena_arena(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapes(pRE = null, pRefreshOnly = false, pfinalCutAfterLoad = false) {
        if (!this.mApp.tools.isDefaultToolRunning() ||
            this.mSceneIsLoading ||
            this.mCutShapesDepth > 0 ||
            !this.monAfterLoad) {
            return;
        }
        if (pfinalCutAfterLoad && this.mfinalCutAfterLoad) {
            return;
        }
        this.mfinalCutAfterLoad = true;
        this.mCutShapesDepth++;
        if (this.mREShapes.length === 0) {
            this.mCutShapesDepth = 0;
            return;
        }
        if (this.mREShapes.length === 1) {
            // let tShowOnly = (this.mREShapes[0].type === "Road" || this.mREShapes[0].type === "Hedge") ? true : false;
            let tShowOnly = this.mREShapes[0].type === "Hedge" ? true : false;
            this.mREShapes[0].updateGeometry(true, tShowOnly);
            this.mCutShapesDepth = 0;
            this.countGrassMats(GrassMatCAGType.normal);
            return;
        }
        // check only normal 2d shapes
        let tRElist = new Array();
        for (let tREshape of this.mREShapes) {
            if ((tREshape.type !== "SafetyZone" && tREshape.type !== "Road" && tREshape.type !== "Hedge")) {
                tRElist.push(tREshape);
                tREshape.clearCutCAG();
            }
        }
        this.mFindOverlappingShapes = true;
        if (pRefreshOnly) {
            for (let tRE of this.mREShapes) {
                tRE.updateGeometry(true, false);
            }
            this.mFindOverlappingShapes = false;
            this.mCutShapesDepth = 0;
            this.countGrassMats(GrassMatCAGType.normal);
            return;
        }
        if (pRE != null) {
            removeElement(tRElist, pRE);
            pRE.updateGeometry(true, false);
        }
        for (let tRE of tRElist) {
            tRE.updateGeometry(true, false);
        }
        for (let i = 0; i < tRElist.length - 1; i++) {
            for (let j = i + 1; j < tRElist.length; j++) {
                let tRE0 = tRElist[i];
                let tRE1 = tRElist[j];
                if (tRE0.type === "Line" && tRE0.GridRE_REF != null &&
                    tRE1.type === "Line" && tRE1.GridRE_REF != null &&
                    tRE0.GridRE_REF.Id === tRE1.GridRE_REF.Id) {
                    continue;
                }
                let tRE0pos = new Vector3(0, 0, 0);
                let tRE1pos = new Vector3(0, 0, 0);
                let tRErot0 = new Vector3(0, 0, 0);
                let tRErot1 = new Vector3(0, 0, 0);
                let tRE0rotY = 0;
                let tRE1rotY = 0;
                // tRE0 position/rotation
                tRE0pos = tRE0.position.clone();
                if (tRE0.type === "Line" && tRE0.GridRE_REF != null) {
                    tRE0pos = tRE0.GridRE_REF.position.clone();
                }
                if (tRE0.node.rotationQuaternion != null) {
                    tRErot0 = tRE0.node.rotationQuaternion.toEulerAngles();
                    tRE0rotY = tRErot0.y * (180 / Math.PI);
                }
                // tslint:disable-next-line:no-collapsible-if
                if (tRE0.type === "Line" && tRE0.GridRE_REF != null) {
                    if (tRE0.GridRE_REF.node.rotationQuaternion != null) {
                        tRErot0 = tRE0.GridRE_REF.node.rotationQuaternion.toEulerAngles();
                        tRE0rotY = tRErot0.y * (180 / Math.PI);
                    }
                }
                // tRE1 position/rotation
                tRE1pos = tRE1.position.clone();
                if (tRE1.type === "Line" && tRE1.GridRE_REF != null) {
                    tRE1pos = tRE1.GridRE_REF.position.clone();
                }
                if (tRE1.node.rotationQuaternion != null) {
                    tRErot1 = tRE1.node.rotationQuaternion.toEulerAngles();
                    tRE1rotY = tRErot1.y * (180 / Math.PI);
                }
                // tslint:disable-next-line:no-collapsible-if
                if (tRE1.type === "Line" && tRE1.GridRE_REF != null) {
                    if (tRE1.GridRE_REF.node.rotationQuaternion != null) {
                        tRErot1 = tRE1.GridRE_REF.node.rotationQuaternion.toEulerAngles();
                        tRE1rotY = tRErot1.y * (180 / Math.PI);
                    }
                }
                // make sure of equal height!
                if (!Util.numbersEqual(tRE0pos.y, tRE1pos.y, 0.001))
                    continue;
                if (tRE0.type !== "Arena" && tRE1.type !== "Arena") {
                    this.cutOverlappingShapes_shape_shape(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type !== "Arena" && tRE1.type === "Arena") {
                    this.cutOverlappingShapes_shape_arena(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type === "Arena" && tRE1.type !== "Arena") {
                    this.cutOverlappingShapes_arena_shape(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
                if (tRE0.type === "Arena" && tRE1.type === "Arena") {
                    this.cutOverlappingShapes_arena_arena(tRE0, tRE1, tRE0rotY, tRE1rotY, tRE0pos, tRE1pos);
                }
            }
        }
        for (let tRE of tRElist) {
            tRE.notifyChange(ElementChangedFlags.PropertyValues);
            tRE.notifyChange(ElementChangedFlags.Properties);
            tRE._buildCSGMesh();
            if (tRE.Params.terrainType !== "Flat") {
                tRE.updateGeometry(true, false);
            }
        }
        this.app.viewer.requestRenderFrame();
        this.mFindOverlappingShapes = false;
        this.mCutShapesDepth = 0;
        this.cutOverlappingShapes_Road();
        this.countGrassMats(GrassMatCAGType.normal);
    }
    cut_checkInterSection(pRE0, pRE1, pCAG0, pCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG0 = pCAG0;
        if (tCAG0 == null) {
            return (null);
        }
        if (tCAG0.sides.length === 0) {
            return (null);
        }
        tCAG0 = tCAG0.rotateZ(-pRE0RotY);
        tCAG0 = tCAG0.translate([pRE0Pos.x, pRE0Pos.z, pRE0Pos.y]);
        if (pCAG1 == null) {
            return (null);
        }
        let tCAG1x = pCAG1;
        tCAG1x = tCAG1x.rotateZ(-pRE1RotY);
        tCAG1x = tCAG1x.translate([pRE1Pos.x, pRE1Pos.z, pRE1Pos.y]);
        let tCAGintersect = tCAG1x.intersect([tCAG0]);
        // no cut no new CSG!
        if (tCAGintersect.sides.length > 0) {
            let tcutCAG = tCAG0.subtract(tCAGintersect);
            let tREtranslate = new Vector3(0, 0, 0);
            // set transform back
            tREtranslate = pRE0Pos.negate().clone();
            tcutCAG = tcutCAG.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
            tcutCAG = tcutCAG.rotateZ(pRE0RotY);
            tCAGintersect = tCAGintersect.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
            tCAGintersect = tCAGintersect.rotateZ(pRE0RotY);
            //store cut reference
            let tcutData = {};
            tcutData.CAG = tCAGintersect;
            tcutData.RoomElement = pRE0;
            pRE1.CutCAG.push(tcutData);
            return (tcutCAG);
        }
        return (null);
    }
    cutOverlappingShapes_shape_shape(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG0 = pRE0.CAG;
        let tCAG1 = pRE1.CAG;
        const tcutCAG_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_IF != null) {
            pRE0.setCAG(tcutCAG_IF);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapes_shape_arena(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG_IF = pRE1.CAG_Arena_PlayField;
        let tCAG_SS = pRE1.CAG_Arena_SideSegments;
        let tCAG_RT = pRE1.CAG_Arena_RunningTracks;
        let tCAG_TL = pRE1.CAG_Arena_TrackLines;
        let tCAG0 = pRE0.CAG;
        if (pRE1.Params.arenaPlayFieldVisible) {
            const tcutCAG_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_IF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_IF != null) {
                pRE0.setCAG(tcutCAG_IF);
            }
        }
        if (!pRE1.Params.arenaCircle && pRE1.Params.arenaSideSegmentsVisible) {
            tCAG0 = pRE0.CAG;
            const tcutCAG_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_SS != null) {
                pRE0.setCAG(tcutCAG_SS);
            }
        }
        tCAG0 = pRE0.CAG;
        const tcutCAG_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_RT != null) {
            pRE0.setCAG(tcutCAG_RT);
        }
        tCAG0 = pRE0.CAG;
        const tcutCAG_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_TL != null) {
            pRE0.setCAG(tcutCAG_TL);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapes_arena_shape(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG_IF = pRE0.CAG_Arena_PlayField;
        let tCAG_SS = pRE0.CAG_Arena_SideSegments;
        let tCAG_RT = pRE0.CAG_Arena_RunningTracks;
        let tCAG_TL = pRE0.CAG_Arena_TrackLines;
        let tCAG1 = pRE1.CAG;
        const tcutCAG_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG_IF, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_IF != null) {
            pRE0.setCAG_Arena_PlayField(tcutCAG_IF);
        }
        if (!pRE0.Params.arenaCircle) {
            const tcutCAG_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG_SS, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_SS != null) {
                pRE0.setCAG_Arena_SideSegments(tcutCAG_SS);
            }
        }
        const tcutCAG_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG_RT, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_RT != null) {
            pRE0.setCAG_Arena_RunningTracks(tcutCAG_RT);
        }
        const tcutCAG_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG_TL, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_TL != null) {
            pRE0.setCAG_Arena_TrackLines(tcutCAG_TL);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapes_arena_arena(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG0_PF = pRE0.CAG_Arena_PlayField;
        let tCAG0_SS = pRE0.CAG_Arena_SideSegments;
        let tCAG0_RT = pRE0.CAG_Arena_RunningTracks;
        let tCAG0_TL = pRE0.CAG_Arena_TrackLines;
        let tCAG1_PF = pRE1.CAG_Arena_PlayField;
        let tCAG1_SS = pRE1.CAG_Arena_SideSegments;
        let tCAG1_RT = pRE1.CAG_Arena_RunningTracks;
        let tCAG1_TL = pRE1.CAG_Arena_TrackLines;
        // playfield
        if (pRE1.Params.arenaPlayFieldVisible) {
            const tcutCAG0_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_IF != null) {
                pRE0.setCAG_Arena_PlayField(tcutCAG0_IF);
            }
            const tcutCAG0_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_SS != null) {
                pRE0.setCAG_Arena_SideSegments(tcutCAG0_SS);
            }
            const tcutCAG0_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_RT != null) {
                pRE0.setCAG_Arena_RunningTracks(tcutCAG0_RT);
            }
            const tcutCAG0_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_TL != null) {
                pRE0.setCAG_Arena_TrackLines(tcutCAG0_TL);
            }
        }
        // sidesegments
        if (!pRE1.Params.arenaCircle && pRE1.Params.arenaSideSegmentsVisible) {
            tCAG0_PF = pRE0.CAG_Arena_PlayField;
            tCAG0_SS = pRE0.CAG_Arena_SideSegments;
            tCAG0_RT = pRE0.CAG_Arena_RunningTracks;
            tCAG0_TL = pRE0.CAG_Arena_TrackLines;
            const tcutCAG1_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_IF != null) {
                pRE0.setCAG_Arena_PlayField(tcutCAG1_IF);
            }
            const tcutCAG1_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_SS != null) {
                pRE0.setCAG_Arena_SideSegments(tcutCAG1_SS);
            }
            const tcutCAG1_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_RT != null) {
                pRE0.setCAG_Arena_RunningTracks(tcutCAG1_RT);
            }
            const tcutCAG1_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_TL != null) {
                pRE0.setCAG_Arena_TrackLines(tcutCAG1_TL);
            }
        }
        // running track
        tCAG0_PF = pRE0.CAG_Arena_PlayField;
        tCAG0_SS = pRE0.CAG_Arena_SideSegments;
        tCAG0_RT = pRE0.CAG_Arena_RunningTracks;
        tCAG0_TL = pRE0.CAG_Arena_TrackLines;
        const tcutCAG2_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_IF != null) {
            pRE0.setCAG_Arena_PlayField(tcutCAG2_IF);
        }
        const tcutCAG2_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_SS != null) {
            pRE0.setCAG_Arena_SideSegments(tcutCAG2_SS);
        }
        const tcutCAG2_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_RT != null) {
            pRE0.setCAG_Arena_RunningTracks(tcutCAG2_RT);
        }
        const tcutCAG2_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_TL != null) {
            pRE0.setCAG_Arena_TrackLines(tcutCAG2_TL);
        }
        // track lines
        tCAG0_PF = pRE0.CAG_Arena_PlayField;
        tCAG0_SS = pRE0.CAG_Arena_SideSegments;
        tCAG0_RT = pRE0.CAG_Arena_RunningTracks;
        tCAG0_TL = pRE0.CAG_Arena_TrackLines;
        const tcutCAG3_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_IF != null) {
            pRE0.setCAG_Arena_PlayField(tcutCAG3_IF);
        }
        const tcutCAG3_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_SS != null) {
            pRE0.setCAG_Arena_SideSegments(tcutCAG3_SS);
        }
        const tcutCAG3_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_RT != null) {
            pRE0.setCAG_Arena_RunningTracks(tcutCAG3_RT);
        }
        const tcutCAG3_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_TL != null) {
            pRE0.setCAG_Arena_TrackLines(tcutCAG3_TL);
        }
    }
    cut_checkInterSection_CAG(pRE0, pRE1, pCAG0, pCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        let tCAG0 = pCAG0;
        let tCAG1 = pCAG1;
        if (tCAG0 == null || tCAG1 == null ||
            tCAG0.sides.length === 0 || tCAG1.sides.length === 0) {
            return (null);
        }
        tCAG0 = tCAG0.rotateZ(-pRE0RotY);
        tCAG0 = tCAG0.translate([pRE0Pos.x, pRE0Pos.z, pRE0Pos.y]);
        tCAG1 = tCAG1.rotateZ(-pRE1RotY);
        tCAG1 = tCAG1.translate([pRE1Pos.x, pRE1Pos.z, pRE1Pos.y]);
        let tCAGintersect = tCAG1.intersect([tCAG0]);
        // no cut no new CSG!
        if (tCAGintersect.sides.length === 0) {
            return (null);
        }
        let tcutCAG = tCAG0.subtract(tCAGintersect);
        let tREtranslate = new Vector3(0, 0, 0);
        if (pRE0.type === "SafetyZone" ||
            (pRE0.type === "Line" && pRE0.GridRE_REF != null)) {
            let tME = pRE0.getMainElement();
            if (tME != null) {
                tREtranslate = tME.node.position.clone().negate();
                tcutCAG = tcutCAG.translate([-pRE0Pos.x, -pRE0Pos.z, 0]);
                tcutCAG = tcutCAG.rotateZ(pRE0RotY);
                tCAGintersect = tCAGintersect.translate([-pRE0Pos.x, -pRE0Pos.z, 0]);
                tCAGintersect = tCAGintersect.rotateZ(pRE0RotY);
            }
        }
        else {
            tREtranslate = pRE0.position.negate().clone();
            tcutCAG = tcutCAG.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
            tcutCAG = tcutCAG.rotateZ(pRE0RotY);
            tCAGintersect = tCAGintersect.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
            tCAGintersect = tCAGintersect.rotateZ(pRE0RotY);
        }
        return (tcutCAG);
    }
    cutOverlappingShapesCAG_shape_shape(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        if (pRE0.CAG_notVisible == null || pRE1.CAG_notVisible == null) {
            return;
        }
        let tCAG0 = CAG.fromSides(pRE0.CAG_notVisible.sides);
        let tCAG1 = CAG.fromSides(pRE1.CAG_notVisible.sides);
        if (tCAG0 == null || tCAG1 == null ||
            tCAG0.sides.length === 0 || tCAG1.sides.length === 0) {
            return;
        }
        const tcutCAG_shape_shape = this.cut_checkInterSection_CAG(pRE0, pRE1, tCAG0, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_shape_shape != null) {
            pRE0.setCAG_notVisible(tcutCAG_shape_shape);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapesCAG_shape_arena(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        if (pRE0.CAG_notVisible == null ||
            pRE1.CAG_notVisible_Arena_PlayField == null ||
            pRE1.CAG_notVisible_Arena_SideSegments == null ||
            pRE1.CAG_notVisible_Arena_RunningTracks == null ||
            pRE1.CAG_notVisible_Arena_TrackLines == null) {
            return;
        }
        let tCAG0 = CAG.fromSides(pRE0.CAG_notVisible.sides);
        let tCAG_PF = CAG.fromSides(pRE1.CAG_notVisible_Arena_PlayField.sides);
        let tCAG_SS = CAG.fromSides(pRE1.CAG_notVisible_Arena_SideSegments.sides);
        let tCAG_RT = CAG.fromSides(pRE1.CAG_notVisible_Arena_RunningTracks.sides);
        let tCAG_TL = CAG.fromSides(pRE1.CAG_notVisible_Arena_TrackLines.sides);
        if (tCAG0 == null || tCAG0.sides.length === 0) {
            return;
        }
        if (pRE1.Params.arenaPlayFieldVisible && tCAG_PF != null && tCAG_PF.sides.length > 0) {
            const tcutCAG_PF = this.cut_checkInterSection_CAG(pRE0, pRE1, tCAG0, tCAG_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_PF != null) {
                pRE0.setCAG_notVisible(tcutCAG_PF);
            }
        }
        if (!pRE1.Params.arenaCircle && pRE1.Params.arenaSideSegmentsVisible &&
            tCAG_SS != null && tCAG_SS.sides.length > 0) {
            tCAG0 = CAG.fromSides(pRE0.CAG_notVisible.sides);
            const tcutCAG_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_SS != null) {
                pRE0.setCAG_notVisible(tcutCAG_SS);
            }
        }
        if (tCAG_RT != null && tCAG_RT.sides.length > 0) {
            tCAG0 = CAG.fromSides(pRE0.CAG_notVisible.sides);
            const tcutCAG_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_RT != null) {
                pRE0.setCAG_notVisible(tcutCAG_RT);
            }
        }
        if (tCAG_TL != null && tCAG_TL.sides.length > 0) {
            tCAG0 = CAG.fromSides(pRE0.CAG_notVisible.sides);
            const tcutCAG_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0, tCAG_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_TL != null) {
                pRE0.setCAG_notVisible(tcutCAG_TL);
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapesCAG_arena_shape(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        if (pRE1.CAG_notVisible == null ||
            pRE0.CAG_notVisible_Arena_PlayField == null ||
            pRE0.CAG_notVisible_Arena_SideSegments == null ||
            pRE0.CAG_notVisible_Arena_RunningTracks == null ||
            pRE0.CAG_notVisible_Arena_TrackLines == null) {
            return;
        }
        let tCAG1 = CAG.fromSides(pRE1.CAG_notVisible.sides);
        let tCAG_PF = CAG.fromSides(pRE0.CAG_notVisible_Arena_PlayField.sides);
        let tCAG_SS = CAG.fromSides(pRE0.CAG_notVisible_Arena_SideSegments.sides);
        let tCAG_RT = CAG.fromSides(pRE0.CAG_notVisible_Arena_RunningTracks.sides);
        let tCAG_TL = CAG.fromSides(pRE0.CAG_notVisible_Arena_TrackLines.sides);
        if (tCAG1 == null || tCAG1.sides.length === 0 ||
            tCAG_RT == null || tCAG_TL == null ||
            tCAG_RT.sides.length === 0 || tCAG_TL.sides.length === 0) {
            return;
        }
        if (pRE0.Params.arenaPlayFieldVisible && tCAG_PF != null && tCAG_PF.sides.length > 0) {
            const tcutCAG_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG_PF, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_IF != null) {
                pRE0.setCAG_notVisible_Arena_InField(tcutCAG_IF);
            }
        }
        if (!pRE0.Params.arenaCircle && pRE0.Params.arenaSideSegmentsVisible &&
            tCAG_SS != null && tCAG_SS.sides.length > 0) {
            const tcutCAG_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG_SS, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG_SS != null) {
                pRE0.setCAG_notVisible_Arena_SideSegments(tcutCAG_SS);
            }
        }
        const tcutCAG_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG_RT, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_RT != null) {
            pRE0.setCAG_notVisible_Arena_RunningTracks(tcutCAG_RT);
        }
        const tcutCAG_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG_TL, tCAG1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG_TL != null) {
            pRE0.setCAG_notVisible_Arena_TrackLines(tcutCAG_TL);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapesCAG_arena_arena(pRE0, pRE1, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos) {
        if (pRE1.CAG_notVisible_Arena_PlayField == null ||
            pRE1.CAG_notVisible_Arena_SideSegments == null ||
            pRE1.CAG_notVisible_Arena_RunningTracks == null ||
            pRE1.CAG_notVisible_Arena_TrackLines == null ||
            pRE0.CAG_notVisible_Arena_PlayField == null ||
            pRE0.CAG_notVisible_Arena_SideSegments == null ||
            pRE0.CAG_notVisible_Arena_RunningTracks == null ||
            pRE0.CAG_notVisible_Arena_TrackLines == null) {
            return;
        }
        let tCAG0_PF = CAG.fromSides(pRE0.CAG_notVisible_Arena_PlayField.sides);
        let tCAG0_SS = CAG.fromSides(pRE0.CAG_notVisible_Arena_SideSegments.sides);
        let tCAG0_RT = CAG.fromSides(pRE0.CAG_notVisible_Arena_RunningTracks.sides);
        let tCAG0_TL = CAG.fromSides(pRE0.CAG_notVisible_Arena_TrackLines.sides);
        let tCAG1_PF = CAG.fromSides(pRE1.CAG_notVisible_Arena_PlayField.sides);
        let tCAG1_SS = CAG.fromSides(pRE1.CAG_notVisible_Arena_SideSegments.sides);
        let tCAG1_RT = CAG.fromSides(pRE1.CAG_notVisible_Arena_RunningTracks.sides);
        let tCAG1_TL = CAG.fromSides(pRE1.CAG_notVisible_Arena_TrackLines.sides);
        if (tCAG1_RT == null || tCAG1_TL == null || tCAG0_RT == null || tCAG0_TL == null ||
            tCAG0_RT.sides.length === 0 || tCAG0_TL.sides.length === 0 ||
            tCAG1_RT.sides.length === 0 || tCAG1_TL.sides.length === 0) {
            return;
        }
        // playfield
        if (pRE1.Params.arenaPlayFieldVisible) {
            if (pRE0.Params.arenaPlayFieldVisible) {
                const tcutCAG0_PF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
                if (tcutCAG0_PF != null) {
                    pRE0.setCAG_notVisible_Arena_InField(tcutCAG0_PF);
                }
            }
            if (pRE0.Params.arenaSideSegmentsVisible) {
                const tcutCAG0_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
                if (tcutCAG0_SS != null) {
                    pRE0.setCAG_notVisible_Arena_SideSegments(tcutCAG0_SS);
                }
            }
            const tcutCAG0_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_RT != null) {
                pRE0.setCAG_notVisible_Arena_RunningTracks(tcutCAG0_RT);
            }
            const tcutCAG0_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_PF, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG0_TL != null) {
                pRE0.setCAG_notVisible_Arena_TrackLines(tcutCAG0_TL);
            }
        }
        // sidesegments
        if (!pRE1.Params.arenaCircle && pRE1.Params.arenaSideSegmentsVisible) {
            tCAG0_PF = CAG.fromSides(pRE0.CAG_notVisible_Arena_PlayField.sides);
            tCAG0_SS = CAG.fromSides(pRE0.CAG_notVisible_Arena_SideSegments.sides);
            tCAG0_RT = CAG.fromSides(pRE0.CAG_notVisible_Arena_RunningTracks.sides);
            tCAG0_TL = CAG.fromSides(pRE0.CAG_notVisible_Arena_TrackLines.sides);
            if (pRE0.Params.arenaPlayFieldVisible) {
                const tcutCAG1_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
                if (tcutCAG1_IF != null) {
                    pRE0.setCAG_notVisible_Arena_InField(tcutCAG1_IF);
                }
            }
            if (pRE0.Params.arenaSideSegmentsVisible) {
                const tcutCAG1_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
                if (tcutCAG1_SS != null) {
                    pRE0.setCAG_notVisible_Arena_SideSegments(tcutCAG1_SS);
                }
            }
            const tcutCAG1_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_RT != null) {
                pRE0.setCAG_notVisible_Arena_RunningTracks(tcutCAG1_RT);
            }
            const tcutCAG1_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_SS, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG1_TL != null) {
                pRE0.setCAG_notVisible_Arena_TrackLines(tcutCAG1_TL);
            }
        }
        // running track
        tCAG0_PF = CAG.fromSides(pRE0.CAG_notVisible_Arena_PlayField.sides);
        tCAG0_SS = CAG.fromSides(pRE0.CAG_notVisible_Arena_SideSegments.sides);
        tCAG0_RT = CAG.fromSides(pRE0.CAG_notVisible_Arena_RunningTracks.sides);
        tCAG0_TL = CAG.fromSides(pRE0.CAG_notVisible_Arena_TrackLines.sides);
        if (pRE0.Params.arenaPlayFieldVisible) {
            const tcutCAG2_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG2_IF != null) {
                pRE0.setCAG_notVisible_Arena_InField(tcutCAG2_IF);
            }
        }
        if (pRE0.Params.arenaSideSegmentsVisible) {
            const tcutCAG2_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG2_SS != null) {
                pRE0.setCAG_notVisible_Arena_SideSegments(tcutCAG2_SS);
            }
        }
        const tcutCAG2_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_RT != null) {
            pRE0.setCAG_notVisible_Arena_RunningTracks(tcutCAG2_RT);
        }
        const tcutCAG2_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_RT, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG2_TL != null) {
            pRE0.setCAG_notVisible_Arena_TrackLines(tcutCAG2_TL);
        }
        // track lines
        tCAG0_PF = CAG.fromSides(pRE0.CAG_notVisible_Arena_PlayField.sides);
        tCAG0_SS = CAG.fromSides(pRE0.CAG_notVisible_Arena_SideSegments.sides);
        tCAG0_RT = CAG.fromSides(pRE0.CAG_notVisible_Arena_RunningTracks.sides);
        tCAG0_TL = CAG.fromSides(pRE0.CAG_notVisible_Arena_TrackLines.sides);
        if (pRE0.Params.arenaPlayFieldVisible) {
            const tcutCAG3_IF = this.cut_checkInterSection(pRE0, pRE1, tCAG0_PF, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG3_IF != null) {
                pRE0.setCAG_notVisible_Arena_InField(tcutCAG3_IF);
            }
        }
        if (pRE0.Params.arenaSideSegmentsVisible) {
            const tcutCAG3_SS = this.cut_checkInterSection(pRE0, pRE1, tCAG0_SS, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
            if (tcutCAG3_SS != null) {
                pRE0.setCAG_notVisible_Arena_SideSegments(tcutCAG3_SS);
            }
        }
        const tcutCAG3_RT = this.cut_checkInterSection(pRE0, pRE1, tCAG0_RT, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_RT != null) {
            pRE0.setCAG_notVisible_Arena_RunningTracks(tcutCAG3_RT);
        }
        const tcutCAG3_TL = this.cut_checkInterSection(pRE0, pRE1, tCAG0_TL, tCAG1_TL, pRE0RotY, pRE1RotY, pRE0Pos, pRE1Pos);
        if (tcutCAG3_TL != null) {
            pRE0.setCAG_notVisible_Arena_TrackLines(tcutCAG3_TL);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingShapes_Road(pRE = null, pRefreshOnly = false) {
        if (!this.mApp.tools.isDefaultToolRunning() ||
            this.mSceneIsLoading ||
            this.mCutShapesDepth > 0 ||
            !this.monAfterLoad) {
            return;
        }
        this.mCutShapesDepth++;
        if (this.mREShapes.length === 0) {
            this.mCutShapesDepth = 0;
            return;
        }
        // check only normal 2d shapes
        let tRElist = new Array();
        for (let tREshape of this.mREShapes) {
            if (tREshape.type === "Road") {
                tRElist.push(tREshape);
                tREshape.clearCutCAG();
            }
        }
        this.mFindOverlappingShapes = true;
        if (pRefreshOnly) {
            for (let tRE of this.mREShapes) {
                tRE.updateGeometry(false, false);
            }
            this.mFindOverlappingShapes = false;
            this.mCutShapesDepth = 0;
            return;
        }
        if (pRE != null) {
            removeElement(tRElist, pRE);
            pRE.updateGeometry(false, false);
        }
        for (let tRE of tRElist)
            tRE.updateGeometry(false, false);
        for (let i = 0; i < tRElist.length - 1; i++) {
            for (let j = i + 1; j < tRElist.length; j++) {
                let tRE0 = tRElist[i];
                let tRE1 = tRElist[j];
                let tREpos0 = new Vector3(0, 0, 0);
                let tREpos1 = new Vector3(0, 0, 0);
                let tRErot0 = new Vector3(0, 0, 0);
                let tRErot1 = new Vector3(0, 0, 0);
                let tRE0rotY = 0;
                let tRE1rotY = 0;
                tREpos0 = tRE0.position.clone();
                if (tRE0.node.rotationQuaternion != null) {
                    tRErot0 = tRE0.node.rotationQuaternion.toEulerAngles();
                    tRE0rotY = tRErot0.y * (180 / Math.PI);
                }
                tREpos1 = tRE1.position.clone();
                if (tRE1.node.rotationQuaternion != null) {
                    tRErot1 = tRE1.node.rotationQuaternion.toEulerAngles();
                    tRE1rotY = tRErot1.y * (180 / Math.PI);
                }
                if (!Util.numbersEqual(tREpos0.y, tREpos1.y, 0.001))
                    continue;
                let tCAG0 = tRElist[i].CAG;
                let tCAG1 = tRElist[j].CAG;
                if (tCAG0 == null || tCAG1 == null)
                    continue;
                if (tCAG0.sides.length === 0)
                    continue;
                tCAG0 = tCAG0.rotateZ(-tRE0rotY);
                tCAG1 = tCAG1.rotateZ(-tRE1rotY);
                tCAG0 = tCAG0.translate([tREpos0.x, tREpos0.z, tREpos0.y]);
                tCAG1 = tCAG1.translate([tREpos1.x, tREpos1.z, tREpos1.y]);
                let tCAGintersect = tCAG1.intersect([tCAG0]);
                // no cut no new CSG!
                if (tCAGintersect.sides.length === 0)
                    continue;
                let tcutCAG = tCAG0.subtract(tCAGintersect);
                //let tcutCAG = tCAG1.subtract(tCAGintersect);
                let tREtranslate = new Vector3(0, 0, 0);
                // set transform back
                tREtranslate = tRE0.position.negate().clone();
                tcutCAG = tcutCAG.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
                tcutCAG = tcutCAG.rotateZ(tRE0rotY);
                tCAGintersect = tCAGintersect.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
                tCAGintersect = tCAGintersect.rotateZ(tRE0rotY);
                //store cut reference
                let tcutData = {};
                tcutData.CAG = tCAGintersect;
                tcutData.RoomElement = tRElist[i];
                tRElist[j].CutCAG.push(tcutData);
                tRElist[i].setCAG(tcutCAG);
            }
        }
        for (let tRE of tRElist) {
            tRE.notifyChange(ElementChangedFlags.PropertyValues);
            tRE.notifyChange(ElementChangedFlags.Properties);
            tRE._buildCSGMesh();
            if (tRE.Params.terrainType !== "Flat") {
                tRE.updateGeometry(true, false);
            }
        }
        this.app.viewer.requestRenderFrame();
        this.mFindOverlappingShapes = false;
        this.mCutShapesDepth = 0;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    cutOverlappingSZShapes(pRE = null) {
        if (!this.mApp.tools.isDefaultToolRunning())
            return;
        // check only safetyzones
        let tRElist = new Array();
        for (let tREshape of this.mREShapes) {
            if (tREshape.type === "SafetyZone") {
                tRElist.push(tREshape);
                tREshape.clearCutCAG();
            }
        }
        if (tRElist.length === 0)
            return;
        if (tRElist.length === 1) {
            tRElist[0].updateGeometry(false, false);
            return;
        }
        this.mFindOverlappingShapes = true;
        let tCSGlist = new Array();
        if (pRE != null) {
            removeElement(tRElist, pRE);
            pRE.updateGeometry(false, false);
        }
        for (let tRE of tRElist)
            tRE.updateGeometry(false, false);
        let tIntersectionExists = false;
        for (let i = 0; i < tRElist.length - 1; i++) {
            let tRE1 = tRElist[i];
            let tM1_3d = tRE1.get3dNode;
            if (tM1_3d == null)
                continue;
            let tM1_3d_bounds = tM1_3d.getBoundingInfo();
            let tdiagonalLength1 = tM1_3d_bounds.diagonalLength;
            if (tdiagonalLength1 === Infinity)
                continue;
            for (let j = i + 1; j < tRElist.length; j++) {
                let tRE2 = tRElist[j];
                let tM2_3d = tRE2.get3dNode;
                if (tM2_3d == null)
                    continue;
                let tM2_3d_bounds = tM2_3d.getBoundingInfo();
                let tdiagonalLength11 = tM2_3d_bounds.diagonalLength;
                if (tdiagonalLength11 === Infinity)
                    continue;
                //intersection prüfen
                if (tM1_3d.intersectsMesh(tM2_3d, true)) {
                    tIntersectionExists = true;
                    break;
                }
            }
            if (tIntersectionExists)
                break;
        }
        // no bouningbox intersection exists: return
        if (!tIntersectionExists)
            return;
        for (let i = 0; i < tRElist.length - 1; i++) {
            for (let j = i + 1; j < tRElist.length; j++) {
                let tRE0 = tRElist[i];
                let tRE1 = tRElist[j];
                if (!tRE1.get_SafetyZone_Visibility()) {
                    continue;
                }
                let tREpos0 = new Vector3(0, 0, 0);
                let tREpos1 = new Vector3(0, 0, 0);
                let tRErot0 = new Vector3(0, 0, 0);
                let tRErot1 = new Vector3(0, 0, 0);
                let tRE0rotY = 0;
                let tRE1rotY = 0;
                if (tRE0.type === "SafetyZone") {
                    if (tRE0 != null) {
                        let tME = tRE0.getMainElement();
                        if (tME != null) {
                            let tTM = tRE0.node.getWorldMatrix();
                            tREpos0 = tTM.getTranslation();
                            tREpos0.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
                            if (tME.node.rotationQuaternion != null) {
                                tRErot0 = tME.node.rotationQuaternion.toEulerAngles();
                                tRE0rotY = tRErot0.y * (180 / Math.PI);
                            }
                        }
                    }
                }
                else {
                    tREpos0 = tRE0.position.clone();
                    if (tRE0.node.rotationQuaternion != null) {
                        tRErot0 = tRE0.node.rotationQuaternion.toEulerAngles();
                        tRE0rotY = tRErot0.y * (180 / Math.PI);
                    }
                }
                if (tRE1.type === "SafetyZone") {
                    if (tRE1 != null) {
                        let tME = tRE1.getMainElement();
                        if (tME != null) {
                            let tTM = tRE1.node.getWorldMatrix();
                            tREpos1 = tTM.getTranslation();
                            tREpos1.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
                            if (tME.node.rotationQuaternion != null) {
                                tRErot1 = tME.node.rotationQuaternion.toEulerAngles();
                                tRE1rotY = tRErot1.y * (180 / Math.PI);
                            }
                        }
                    }
                }
                else {
                    tREpos1 = tRE1.position.clone();
                    if (tRE1.node.rotationQuaternion != null) {
                        tRErot1 = tRE1.node.rotationQuaternion.toEulerAngles();
                        tRE1rotY = tRErot1.y * (180 / Math.PI);
                    }
                }
                if (!Util.numbersEqual(tREpos0.y, tREpos1.y, 0.001))
                    continue;
                let tCAG0 = tRElist[i].CAG;
                let tCAG1 = tRElist[j].CAG;
                if (tCAG0 == null || tCAG1 == null)
                    continue;
                if (tCAG0.sides.length === 0 || tCAG1.sides.length === 0)
                    continue;
                tCAG0 = tCAG0.rotateZ(-tRE0rotY);
                tCAG1 = tCAG1.rotateZ(-tRE1rotY);
                tCAG0 = tCAG0.translate([tREpos0.x, tREpos0.z, 0]);
                tCAG1 = tCAG1.translate([tREpos1.x, tREpos1.z, 0]);
                let tCAGintersect = tCAG1.intersect([tCAG0]);
                // no cut no new CSG!
                if (tCAGintersect.sides.length === 0)
                    continue;
                let tcutCAG = tCAG0.subtract(tCAGintersect);
                //let tcutCAG = tCAG1.subtract(tCAGintersect);
                let tREtranslate = new Vector3(0, 0, 0);
                // set transform back
                if (tRE0.type === "SafetyZone") {
                    if (tRE0 != null) {
                        let tME = tRE0.getMainElement();
                        if (tME != null) {
                            tREtranslate = tME.node.position.clone().negate();
                            tcutCAG = tcutCAG.translate([-tREpos0.x, -tREpos0.z, 0]);
                            tcutCAG = tcutCAG.rotateZ(tRE0rotY);
                            tCAGintersect = tCAGintersect.translate([-tREpos0.x, -tREpos0.z, 0]);
                            tCAGintersect = tCAGintersect.rotateZ(tRE0rotY);
                        }
                    }
                }
                else {
                    tREtranslate = tRE0.position.negate().clone();
                    tcutCAG = tcutCAG.translate([tREtranslate.x, tREtranslate.z, 0]);
                    tcutCAG = tcutCAG.rotateZ(tRE0rotY);
                    tCAGintersect = tCAGintersect.translate([tREtranslate.x, tREtranslate.z, 0]);
                    tCAGintersect = tCAGintersect.rotateZ(tRE0rotY);
                }
                //store cut reference
                let tcutData = {};
                tcutData.CAG = tCAGintersect;
                tcutData.RoomElement = tRElist[i];
                tRElist[j].CutCAG.push(tcutData);
                tRElist[i].setCAG(tcutCAG);
            }
        }
        for (let tRE of tRElist) {
            tRE.notifyChange(ElementChangedFlags.PropertyValues);
            tRE.notifyChange(ElementChangedFlags.Properties);
            tRE._buildCSGMesh();
            tRE.updateGeometry(false, true);
        }
        this.app.viewer.requestRenderFrame();
        this.mFindOverlappingShapes = false;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async checkMaterial(pMaterial) {
        let tavailable = false;
        let tmatId = this.getMaterialId(pMaterial);
        let tMat = this.MatManager.getMaterial(tmatId);
        if (tMat != null) {
            tavailable = true;
        }
        let tmatStatus = await this._loadExternalMaterial(tmatId);
        tMat = this.MatManager.getMaterial(tmatId);
        if (tMat != null && (tmatStatus === 1 || tmatStatus === 2)) {
            if (!tavailable) {
                if (tMat.albedoTexture != null && !pMaterial.loaded) {
                    const talbedoTexture = tMat.albedoTexture;
                    pMaterial.scaleU_init = talbedoTexture.uScale;
                    pMaterial.scaleU = talbedoTexture.uScale;
                    pMaterial.scaleV_init = talbedoTexture.vScale;
                    pMaterial.scaleV = talbedoTexture.vScale;
                }
            }
            this.setInitialTextureScaleValues(tMat, pMaterial);
            if (pMaterial.rotation === 0 &&
                pMaterial.offsetU === pMaterial.offsetU_init && pMaterial.offsetV === pMaterial.offsetV_init &&
                pMaterial.scaleU === pMaterial.scaleU_init && pMaterial.scaleV === pMaterial.scaleV_init) {
                return (tmatStatus);
            }
            let tnewId = tMat.id + pMaterial.idAddOn;
            if (!this.MatManager.hasMaterial(tnewId)) {
                let tnewMat = tMat.clone(tMat.id + pMaterial.idAddOn);
                if (tMat.albedoTexture != null) {
                    let talbedoTexture = tMat.albedoTexture.clone();
                    tnewMat.albedoTexture = talbedoTexture;
                }
                if (tMat.bumpTexture != null) {
                    let tbumpTexture = tMat.bumpTexture.clone();
                    tnewMat.bumpTexture = tbumpTexture;
                }
                if (tMat.microSurfaceTexture != null) {
                    let tmicroSurfaceTexture = tMat.microSurfaceTexture.clone();
                    tnewMat.microSurfaceTexture = tmicroSurfaceTexture;
                }
                if (tMat.metallicTexture != null) {
                    let tmetallicTexture = tMat.metallicTexture.clone();
                    tnewMat.metallicTexture = tmetallicTexture;
                }
                this.MatManager.addMaterial(tMat.id + pMaterial.idAddOn, tnewMat);
            }
        }
        return (tmatStatus);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    addOverlappingShapes(pRE, pDeleteRoomElement = false) {
        if (pRE == null)
            return ([]);
        if (pRE.length === 0)
            return ([]);
        let tsortedRE = [];
        for (let i = this.mRoomElements.length - 1; i >= 0; i--) {
            if (contains(pRE, this.mRoomElements[i]))
                tsortedRE.push(this.mRoomElements[i]);
        }
        let tREforUpdate = [];
        let tcutDataSum = new Map();
        if (tsortedRE.length === 1 && (tsortedRE[0].type === "Spline" || tsortedRE[0].type === "Rectangle" || tsortedRE[0].type === "Line"
            || tsortedRE[0].type === "Circle" || tsortedRE[0].type === "Road" || tsortedRE[0].type === "Hedge"
            || tsortedRE[0].type === "Arena") &&
            tsortedRE[0].orderIndex === 0 && !pDeleteRoomElement) {
            tsortedRE[0].updateGeometry(false, false);
            tsortedRE[0].setMeshVisibility(0.5);
        }
        else {
            for (let tRE of tsortedRE) {
                for (let tcutCAG of tRE.CutCAG) {
                    let t_cutRE = tcutCAG.RoomElement;
                    let t_cutCAG = tcutCAG.CAG;
                    if (!contains(this.mRoomElements, t_cutRE)) {
                        continue;
                    }
                    // tslint:disable-next-line:no-collapsible-if
                    if (t_cutRE.type === "Arena") {
                        if (!contains(tREforUpdate, t_cutRE)) {
                            tREforUpdate.push(t_cutRE);
                        }
                    }
                    if (tRE.CAG != null) {
                        let tREpos0 = tRE.position.clone();
                        let tRE0rotY = 0;
                        let tRErot0 = new Vector3(0, 0, 0);
                        if (tRE.node.rotationQuaternion != null) {
                            tRErot0 = tRE.node.rotationQuaternion.toEulerAngles();
                            tRE0rotY = tRErot0.y * (180 / Math.PI);
                        }
                        let ttmpCAG = CAG.fromSides(tRE.CAG.sides);
                        ttmpCAG = ttmpCAG.rotateZ(-tRE0rotY);
                        ttmpCAG = ttmpCAG.translate([tREpos0.x, tREpos0.z, tREpos0.y]);
                        tREpos0 = t_cutRE.position.clone();
                        if (t_cutRE.node.rotationQuaternion != null) {
                            tRErot0 = t_cutRE.node.rotationQuaternion.toEulerAngles();
                            tRE0rotY = tRErot0.y * (180 / Math.PI);
                        }
                        let ttmpCAG1 = CAG.fromSides(t_cutCAG.sides);
                        ttmpCAG1 = ttmpCAG1.rotateZ(-tRE0rotY);
                        ttmpCAG1 = ttmpCAG1.translate([tREpos0.x, tREpos0.z, tREpos0.y]);
                        let t_subCAG = ttmpCAG1.subtract(ttmpCAG);
                        let t_visibleCAG = ttmpCAG1.subtract(t_subCAG);
                        let tREtranslate = t_cutRE.position.negate().clone();
                        t_visibleCAG = t_visibleCAG.translate([tREtranslate.x, tREtranslate.z, tREtranslate.y]);
                        t_visibleCAG = t_visibleCAG.rotateZ(tRE0rotY);
                        t_cutCAG = t_visibleCAG;
                    }
                    if (t_cutRE == null || t_cutCAG.sides.length === 0)
                        break;
                    let t_cutRE_CAG = t_cutRE.CAG;
                    if (t_cutRE_CAG == null)
                        break;
                    let tREId = t_cutRE.IdStr;
                    let tmapEntry = tcutDataSum.get(tREId);
                    if (tmapEntry != null) {
                        // add CAG
                        tmapEntry.CAG.push(t_cutCAG);
                    }
                    else {
                        // create new MAP entry
                        let tCAG = new Array();
                        tCAG.push(t_cutCAG);
                        let tmapEntry1 = tcutDataSum.set(tREId, { RoomElement: t_cutRE, CAG: tCAG });
                    }
                }
            }
            tcutDataSum.forEach((pRoomElement) => {
                let tRE = pRoomElement.RoomElement;
                let tcutCAG = pRoomElement.CAG;
                let tRE_CAG = tRE.CAG;
                if (tRE_CAG != null) {
                    tRE_CAG = tRE_CAG.union(tcutCAG);
                    tRE.setCAG(tRE_CAG);
                    tRE._buildCSGMesh();
                    tRE.clearCutCAG();
                }
            });
            if (!pDeleteRoomElement) {
                for (let tRE of tsortedRE) {
                    tRE.updateGeometry(false, false);
                    tRE.setMeshVisibility(0.5);
                }
            }
        }
        this.app.viewer.requestRenderFrame();
        return (tREforUpdate);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    collectMaterialofShapes(pLanguage = "en") {
        this.cutOverlappingShapesCAG(false, true);
        let tRes = new Array();
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "BackgroundImage" || tRE.type === "CustomBuilding" || tRE.type === "Building" || tRE.type === "Text" ||
                (tRE.type === "Line" && tRE.GridRE_REF != null) || tRE.type === "Grid") {
                continue;
            }
            if (tRE.type === "Arena") {
                const tMS_Arena = this._collectMaterialofArena(pLanguage, tRE);
                tRes = tRes.concat(tMS_Arena);
                continue;
            }
            let tMS = {};
            let tarea = tRE.area;
            if ((tRE.CAG_notVisible != null) && (tRE.CAG_notVisible.sides.length > 0) &&
                tRE.CAG_notVisible !== tRE.CAGoriginal) {
                let tareaTMP = tRE.CAG_notVisible.area();
                if (tareaTMP > 0)
                    tarea = tareaTMP;
            }
            tMS.area = tarea;
            tMS.area = Util.convertToAreaValue(this.mApp, tMS.area);
            tMS.perimeter = convertLengthUnit(tRE.perimeter, LengthUnit.Meter, this.mApp.i18n.lengthUnit);
            tMS.onsite = tRE.onsite;
            tMS.elementLabel = tRE.label;
            tMS.visible = tRE.get_SafetyZone_Visibility();
            if (tRE.materialSet.category === "color") {
                tMS.materialType = tRE.materialSet.category;
                tMS.materialName = tRE.materialSet.value;
                tMS.materialLabel = "Color: " + tRE.materialSet.value;
                tMS.colorValue = tRE.materialSet.value;
            }
            else {
                tMS.materialType = tRE.materialSet.category;
                tMS.materialLabel = this.getextMaterialText(tRE.materialSet, pLanguage);
                tMS.materialName = tRE.materialSet.value;
                tMS.colorValue = "";
            }
            if (tMS.area > 0)
                tRes.push(tMS);
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    collectMaterialofGrassMats(pLanguage = "en") {
        this.cutOverlappingShapesCAG(true, false);
        let tRes = new Array();
        for (let tRE of this.mRoomElements) {
            if (!(tRE.type === "Line" && tRE.GridRE_REF != null) || tRE.CAG == null) {
                continue;
            }
            let tmatGrassMats = {};
            const tgridFields = tRE.countGridFields(tRE.CAG_notVisible);
            if (tgridFields == null) {
                continue;
            }
            tmatGrassMats.area = tRE.CAG.area();
            tmatGrassMats.area = Util.convertToAreaValue(this.mApp, tmatGrassMats.area);
            tmatGrassMats.full = tgridFields.full;
            tmatGrassMats.diagonal = tgridFields.diagonal;
            tmatGrassMats.vertical = tgridFields.vertical;
            tmatGrassMats.horizontal = tgridFields.horizontal;
            tmatGrassMats.freeform = tgridFields.freeform;
            tmatGrassMats.totalfull = tgridFields.totalfull;
            tmatGrassMats.onsite = tRE.onsite;
            tmatGrassMats.elementLabel = tRE.label;
            tmatGrassMats.visible = tRE.get_SafetyZone_Visibility();
            if (tRE.materialSet.category === "color") {
                tmatGrassMats.materialType = tRE.materialSet.category;
                tmatGrassMats.materialName = tRE.materialSet.value;
                tmatGrassMats.materialLabel = "Color: " + tRE.materialSet.value;
                tmatGrassMats.colorValue = tRE.materialSet.value;
            }
            else {
                tmatGrassMats.materialType = tRE.materialSet.category;
                tmatGrassMats.materialLabel = this.getextMaterialText(tRE.materialSet, pLanguage);
                tmatGrassMats.materialName = tRE.materialSet.value;
                tmatGrassMats.colorValue = "";
            }
            if (tmatGrassMats.area > 0)
                tRes.push(tmatGrassMats);
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _collectMaterialofArena(pLanguage = "en", pArena) {
        let tRes = new Array();
        // track lane
        if (pArena.CAG_Arena_RunningTracks != null) {
            const tMS_trackLane = {};
            let tarea_TrackLane = pArena.CAG_Arena_RunningTracks.area();
            tMS_trackLane.area = Util.convertToAreaValue(this.mApp, tarea_TrackLane);
            tMS_trackLane.perimeter = convertLengthUnit(pArena.getPerimeterOfCAG(pArena.CAG_Arena_RunningTracks), LengthUnit.Meter, this.mApp.i18n.lengthUnit);
            tMS_trackLane.onsite = pArena.onsite;
            tMS_trackLane.elementLabel = this.resources.propertyMaterial_Arena_TrackLane;
            tMS_trackLane.visible = pArena.get_SafetyZone_Visibility();
            if (pArena.materialSet.category === "color") {
                tMS_trackLane.materialType = pArena.materialSet.category;
                tMS_trackLane.materialName = pArena.materialSet.value;
                tMS_trackLane.materialLabel = "Color: " + pArena.materialSet.value;
                tMS_trackLane.colorValue = pArena.materialSet.value;
            }
            else {
                tMS_trackLane.materialType = pArena.materialSet.category;
                tMS_trackLane.materialLabel = this.getextMaterialText(pArena.materialSet, pLanguage);
                tMS_trackLane.materialName = pArena.materialSet.value;
                tMS_trackLane.colorValue = "";
            }
            if (tMS_trackLane.area > 0)
                tRes.push(tMS_trackLane);
        }
        // playing field
        if (pArena.CAG_Arena_PlayField != null) {
            const tMS_PlayingField = {};
            let tarea_PlayingField = pArena.CAG_Arena_PlayField.area();
            tMS_PlayingField.area = Util.convertToAreaValue(this.mApp, tarea_PlayingField);
            tMS_PlayingField.perimeter = convertLengthUnit(pArena.getPerimeterOfCAG(pArena.CAG_Arena_PlayField), LengthUnit.Meter, this.mApp.i18n.lengthUnit);
            tMS_PlayingField.onsite = pArena.onsite;
            tMS_PlayingField.elementLabel = this.resources.propertyMaterial_Arena_PlayField;
            tMS_PlayingField.visible = pArena.get_SafetyZone_Visibility();
            if (pArena.materialSet_Arena_PlayField.category === "color") {
                tMS_PlayingField.materialType = pArena.materialSet_Arena_PlayField.category;
                tMS_PlayingField.materialName = pArena.materialSet_Arena_PlayField.value;
                tMS_PlayingField.materialLabel = "Color: " + pArena.materialSet_Arena_PlayField.value;
                tMS_PlayingField.colorValue = pArena.materialSet_Arena_PlayField.value;
            }
            else {
                tMS_PlayingField.materialType = pArena.materialSet_Arena_PlayField.category;
                tMS_PlayingField.materialLabel = this.getextMaterialText(pArena.materialSet_Arena_PlayField, pLanguage);
                tMS_PlayingField.materialName = pArena.materialSet_Arena_PlayField.value;
                tMS_PlayingField.colorValue = "";
            }
            if (tMS_PlayingField.area > 0)
                tRes.push(tMS_PlayingField);
        }
        // side segments
        if (pArena.CAG_Arena_SideSegments != null && !pArena.Params.arenaCircle) {
            const tMS_SideSegments = {};
            let tarea_PlayingField = pArena.CAG_Arena_SideSegments.area();
            tMS_SideSegments.area = Util.convertToAreaValue(this.mApp, tarea_PlayingField);
            tMS_SideSegments.perimeter = convertLengthUnit(pArena.getPerimeterOfCAG(pArena.CAG_Arena_SideSegments), LengthUnit.Meter, this.mApp.i18n.lengthUnit);
            tMS_SideSegments.onsite = pArena.onsite;
            tMS_SideSegments.elementLabel = this.resources.propertyMaterial_Arena_SideSegments;
            tMS_SideSegments.visible = pArena.get_SafetyZone_Visibility();
            if (pArena.materialSet_Arena_SideSegments.category === "color") {
                tMS_SideSegments.materialType = pArena.materialSet_Arena_SideSegments.category;
                tMS_SideSegments.materialName = pArena.materialSet_Arena_SideSegments.value;
                tMS_SideSegments.materialLabel = "Color: " + pArena.materialSet_Arena_SideSegments.value;
                tMS_SideSegments.colorValue = pArena.materialSet_Arena_SideSegments.value;
            }
            else {
                tMS_SideSegments.materialType = pArena.materialSet_Arena_SideSegments.category;
                tMS_SideSegments.materialLabel = this.getextMaterialText(pArena.materialSet_Arena_SideSegments, pLanguage);
                tMS_SideSegments.materialName = pArena.materialSet_Arena_SideSegments.value;
                tMS_SideSegments.colorValue = "";
            }
            if (tMS_SideSegments.area > 0)
                tRes.push(tMS_SideSegments);
        }
        // track line
        if (pArena.CAG_Arena_TrackLines != null) {
            const tMS_TrackLine = {};
            let tarea_TrackLines = pArena.CAG_Arena_TrackLines.area();
            tMS_TrackLine.area = Util.convertToAreaValue(this.mApp, tarea_TrackLines);
            tMS_TrackLine.perimeter = convertLengthUnit(pArena.getPerimeterOfCAG(pArena.CAG_Arena_TrackLines), LengthUnit.Meter, this.mApp.i18n.lengthUnit);
            tMS_TrackLine.onsite = pArena.onsite;
            tMS_TrackLine.elementLabel = this.resources.propertyMaterial_Arena_TrackLine;
            tMS_TrackLine.visible = pArena.get_SafetyZone_Visibility();
            if (pArena.materialSet_Arena_TrackLines.category === "color") {
                tMS_TrackLine.materialType = pArena.materialSet_Arena_TrackLines.category;
                tMS_TrackLine.materialName = pArena.materialSet_Arena_TrackLines.value;
                tMS_TrackLine.materialLabel = "Color: " + pArena.materialSet_Arena_TrackLines.value;
                tMS_TrackLine.colorValue = pArena.materialSet_Arena_TrackLines.value;
            }
            else {
                tMS_TrackLine.materialType = pArena.materialSet_Arena_TrackLines.category;
                tMS_TrackLine.materialLabel = this.getextMaterialText(pArena.materialSet_Arena_TrackLines, pLanguage);
                tMS_TrackLine.materialName = pArena.materialSet_Arena_TrackLines.value;
                tMS_TrackLine.colorValue = "";
            }
            if (tMS_TrackLine.area > 0)
                tRes.push(tMS_TrackLine);
        }
        return (tRes);
    }
    get routeDefaultMaterial() {
        return (this._getRouteColorMaterial(this.mWallColors[0]));
    }
    get visibilityMode() {
        return (this.mVisibility);
    }
    get dataPath() {
        return (this.mDataPath);
    }
    _deviceHasMouse() {
        return this._mediaQueryMatch("(pointer: fine), (any-pointer: fine)");
    }
    prepareValueLists() {
        this.createPropClasses();
        //this.createWallColors();
        this.createTextColors();
        this.prepareDoorTypesPropValues();
        this.prepareWallColorPropValues();
        this.prepareRouteWidthsPropValues();
        this.prepareTextColorPropValues();
        this.prepareYesNoPropValues();
        this.prepareOvalCirclePropValues();
        this.preparePictureFrameTypesPropValues();
        this.prepareTerrainTypePropList();
        this.prepareHillProfilePropList();
        this.prepareSpeedTypePropList();
        this.preparePropList_audio_local_effects();
        this.preparePropList_audio_background_effects();
        this.preparePropList_audio_background_music();
    }
    planningContainsHill() {
        let tRes = false;
        for (let tRE of this.mRoomElements) {
            if (tRE.Params.terrainType !== "Flat") {
                tRes = true;
                return (tRes);
            }
        }
        return (tRes);
    }
    toggloNoTriggerContentChanged(pEnable) {
        this.mNotTriggerContentChanged = pEnable;
    }
    contentChanged() {
        this.eventARFirstWallEdgeBottom.trigger(this);
        if (!this.mApp.tools.isDefaultToolRunning())
            return;
        if (!this.mNotTriggerContentChanged)
            this.eventRoomContentChanged.trigger(this);
    }
    getNearestWall(pPos, pExcludedWall) {
        let tMinDist = Number.MAX_VALUE;
        let tNearest = null;
        for (let tWall of this.mWalls) {
            if (tWall === pExcludedWall)
                continue;
            let tDist = tWall.distanceToPoint(pPos);
            if (tDist < tMinDist) {
                tMinDist = tDist;
                tNearest = tWall;
            }
        }
        return (tNearest);
    }
    beginCollectWallUpdates() {
        this.mWallUpdateDepth++;
    }
    endCollectWallUpdates(pFindRoom = false) {
        this.mWallUpdateDepth--;
        if (this.mWallUpdateDepth <= 0) {
            this.mWallUpdateDepth = 0;
            this.mPendingWallUpdates.forEach((pWall) => {
                pWall.updateGeometry();
            });
            this.mPendingWallUpdates.clear();
            this.mWallUpdateDepth = 0;
            if (pFindRoom)
                this.findRoom();
        }
    }
    beginUpdateOfmlArticle() {
        this.mUpdateOfmlArticles_active = true;
        // save SZ visibility status
        this.storeSZVisibility();
        this.removeSafetyZones();
        return (Promise.resolve(true));
    }
    endUpdateOfmlArticle() {
        this.mUpdateOfmlArticles_active = false;
        this.reCreateSafetyZones();
    }
    setTransformationDepth(pDepth) {
        this.mTransformationDepth = pDepth;
    }
    TransformShape() {
        this.mRE_transformedShape++;
    }
    TransformSafetyZone() {
        this.mRE_transformedSafetyZone++;
    }
    getNearestRoute(pPos, pExcludedRoute) {
        let tMinDist = Number.MAX_VALUE;
        let tNearest = null;
        for (let tRoute of this.mRoutes) {
            if (tRoute === pExcludedRoute)
                continue;
            let tDist = tRoute.distanceToPoint(pPos);
            if (tDist < tMinDist) {
                tMinDist = tDist;
                tNearest = tRoute;
            }
        }
        return (tNearest);
    }
    beginCollectRouteUpdates() {
        this.mRouteUpdateDepth++;
    }
    endCollectRouteUpdates() {
        this.mRouteUpdateDepth--;
        if (this.mRouteUpdateDepth <= 0) {
            this.mRouteUpdateDepth = 0;
            this.mPendingRouteUpdates.forEach((pRoute) => {
                pRoute.updateGeometry();
            });
            this.mPendingRouteUpdates.clear();
        }
    }
    beginInteraction(pDblClickOff = false) {
        if (pDblClickOff && this.mView != null) {
            this.mView.cameraControl.dblClickZoomToFitEnabled = false;
        }
        this.mInteractionDepth++;
        this.mApp.model.beginModification();
    }
    endInteraction() {
        if (this.mView != null) {
            this.mView.cameraControl.dblClickZoomToFitEnabled = true;
        }
        this.mInteractionDepth--;
        if (this.mInteractionDepth <= 0) {
            this.mInteractionDepth = 0;
            this.mApp.model.endModification();
            for (let tWall of this.mWalls)
                tWall._interactionFinished();
            for (let tRoute of this.mRoutes)
                tRoute._interactionFinished();
        }
    }
    moveWall() {
        if (this.mWalls.length < 1)
            return;
        for (let tWall of this.mWalls) {
            tWall.setStartPoint(Util.rotateVector(tWall.startPoint, 30));
            tWall.setEndPoint(Util.rotateVector(tWall.endPoint, 30));
            let tWallDirections = tWall._getConnectedWallDirections();
            tWall._handleFlippedWalls(tWallDirections);
            tWall._validateConnections(2);
        }
        this.mApp.rendering.notifyModelInteraction();
        if (this.mView != null)
            this.mView.viewer.requestRenderFrame();
    }
    reconnectWalls() {
        if (this.mWalls.length === 0)
            return;
        this.mWallUpdateDepth = 0;
        for (let tWall of this.mWalls)
            tWall.moveWall(new Vector2(0, 0));
        this.app.viewer.requestRenderFrame();
    }
    reconnectRoutes() {
        if (this.mRoutes.length === 0)
            return;
        this.mRouteUpdateDepth = 0;
        for (let tRoute of this.mRoutes)
            tRoute.moveRoute(new Vector2(0, 0));
        this.app.viewer.requestRenderFrame();
    }
    /**
     * Inserts a new opeing in the center of the currently selected wall.
     */
    insertWallOpening(pType, pWall = null, pPos = null) {
        if (this.mApp.model.selection.length !== 1 && pWall == null)
            return (Promise.resolve(null));
        let tWall = null;
        if (pWall == null) {
            let tElement = this.mApp.model.selection[0];
            if (tElement instanceof Wall)
                tWall = tElement;
            else if (tElement instanceof WallOpening)
                tWall = tElement.wall;
        }
        else {
            tWall = pWall;
        }
        if (tWall == null)
            return (Promise.resolve(null));
        let tOpening = new WallOpening(this, pType);
        if (tWall === undefined || tOpening == null)
            return (Promise.resolve(null));
        let tcheck = tWall.checkInsertWallElement(tOpening, true, pPos, null);
        if (!tcheck) {
            tOpening.dispose();
            this.eventNotEnoughWallSpaceLeft.trigger(this);
            return (Promise.resolve(null));
        }
        let tCmd = new InsertElements(this.mApp, [tOpening]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tOpening.updateGeometry();
            this.mApp.model.setSelection([tOpening]);
            return (tOpening);
        });
        this.contentChanged();
        return (tPromise);
    }
    /**
     * Inserts a new opeing in the center of the currently selected wall.
     */
    insertWallOpeningAR(pType, pWallIndex, pPos, pWidth, pHeight, pFloorOffset) {
        if ((this.mWalls.length === 0) || (pWallIndex > this.mWalls.length - 1))
            return (Promise.resolve(null));
        let tWall = this.mWalls[pWallIndex];
        let tElement = this.mApp.model.selection[0];
        if (tElement instanceof Wall)
            tWall = tElement;
        else if (tElement instanceof WallOpening)
            tWall = tElement.wall;
        if (tWall != null) {
            let tCenter = Vector2.Center(tWall.startPoint, tWall.endPoint);
            let tOpening = new WallOpening(this, pType);
            //tOpening.attachToWall(tWall, tCenter);
            tOpening.attachToWall(tWall, pPos);
            tOpening.setWidth(pWidth);
            tOpening.setHeight(pHeight);
            if (pType !== "Opening")
                tOpening.setFloorOffset(pFloorOffset, true);
            let tCmd = new InsertElements(this.mApp, [tOpening]);
            let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
                tOpening.updateGeometry();
                //this.mApp.model.setSelection([tOpening]);
                return (tOpening);
            });
            return (tPromise);
        }
        return (Promise.resolve(null));
    }
    /**
     * Inserts a new addition in the center/at the selected position of the currently selected wall.
     */
    // tslint:disable-next-line:cyclomatic-complexity
    insertWallAddition(pType, pWall = null, pPos = null, pWidth, pHeight, pKeepRatio = false, pImageType = 0) {
        if (this.mApp.model.selection.length !== 1 && pWall == null)
            return (Promise.resolve(null));
        let tWall = null;
        if (pWall == null) {
            let tElement = this.mApp.model.selection[0];
            if (tElement instanceof Wall)
                tWall = tElement;
            else if (tElement instanceof WallAddition)
                tWall = tElement.wall;
            else if (tElement instanceof WallOpening)
                tWall = tElement.wall;
        }
        else {
            tWall = pWall;
        }
        if (tWall == null)
            return (Promise.resolve(null));
        let tOpening = new WallAddition(this, pType, pWidth, pHeight, pKeepRatio, pImageType);
        if (tWall === undefined || tOpening == null)
            return (Promise.resolve(null));
        let tcheck = tWall.checkInsertWallElement(tOpening, false, pPos, null);
        if (!tcheck) {
            tOpening.dispose();
            this.eventNotEnoughWallSpaceLeft.trigger(this);
            return (Promise.resolve(null));
        }
        let tCmd = new InsertElements(this.mApp, [tOpening]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tOpening.updateGeometry();
            this.mApp.model.setSelection([tOpening]);
            return (tOpening);
        });
        this.contentChanged();
        return (tPromise);
    }
    /**
     * Inserts a new addition in the center/at the selected position of the currently selected wall.
     */
    // tslint:disable-next-line:cyclomatic-complexity
    insertPictureFrame(pWall = null, pPos = null, pWidth, pHeight, pKeepRatio = false, pImageType = 0, pFrameWidth, pFrameColor, pSuspensionHeight, pmatCategory, pmatValue) {
        if (this.mApp.model.selection.length !== 1 && pWall == null)
            return (Promise.resolve(null));
        let tWall = null;
        if (pWall == null) {
            let tElement = this.mApp.model.selection[0];
            if (tElement instanceof Wall)
                tWall = tElement;
            else if (tElement instanceof WallAddition)
                tWall = tElement.wall;
            else if (tElement instanceof WallOpening)
                tWall = tElement.wall;
        }
        else {
            tWall = pWall;
        }
        if (tWall == null)
            return (Promise.resolve(null));
        let tOpening = new WallAddition(this, "PictureFrame", pWidth, pHeight, pKeepRatio, pImageType, pFrameWidth, pFrameColor, pSuspensionHeight, pmatCategory, pmatValue);
        if (tWall === undefined || tOpening == null)
            return (Promise.resolve(null));
        let tcheck = tWall.checkInsertWallElement(tOpening, false, pPos, null);
        if (!tcheck) {
            tOpening.dispose();
            this.eventNotEnoughWallSpaceLeft.trigger(this);
            return (Promise.resolve(null));
        }
        let tCmd = new InsertElements(this.mApp, [tOpening]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tOpening.updateGeometry();
            this.mApp.model.setSelection([tOpening]);
            return (tOpening);
        });
        this.contentChanged();
        return (tPromise);
    }
    /**
     * Inserts a geo marker.
     * pMapRotation: in degree
     */
    async insertGeoMarker(pInitialPosition, pInitialGeoCoordinates, pMapRotation = 0, onTriggerAction) {
        this.createThemeColor();
        let tGeoMarker = this.getGeoMarker();
        if (tGeoMarker == null) {
            tGeoMarker = await this.insertRoomElement("GeoMarker");
            if (tGeoMarker == null) {
                return (Promise.resolve(null));
            }
        }
        if (onTriggerAction != null) {
            tGeoMarker.setOnTriggerGeoAction(onTriggerAction);
        }
        tGeoMarker.setGeoMapRotation(pMapRotation);
        tGeoMarker.setInitialGeoPosition(pInitialGeoCoordinates.latitude, pInitialGeoCoordinates.longitude);
        tGeoMarker.setInitialPosition(pInitialPosition);
        tGeoMarker.setInitialGeoPosition(pInitialGeoCoordinates.latitude, pInitialGeoCoordinates.longitude);
        return (Promise.resolve(tGeoMarker));
    }
    /**
     * Inserts a geo marker.
     */
    async insertGeoMarker1(pinitX, pinitZ, pInitialGeoCoordinates) {
        const tPos = new Vector3(pinitX, 0, pinitZ);
        const tRE = await this.insertGeoMarker(tPos, pInitialGeoCoordinates, 0, this._TestTrigger);
        return (Promise.resolve(tRE));
    }
    /**
     * Inserts a new room element.
     */
    insertRoomElement(pType) {
        this.mNotTriggerContentChanged = true;
        let tRoomElement = new RoomElement(this, pType);
        if (this.view != null) {
            const halfFloorSize = 100;
            const options = {
                placingAreaMin: new Vector2(-halfFloorSize, -halfFloorSize),
                placingAreaMax: new Vector2(halfFloorSize, halfFloorSize)
            };
            placeElementsInViewCenter([tRoomElement], this.view, this.mApp.model, options);
            if (tRoomElement != null) {
                let tPos = tRoomElement.position;
                if (tRoomElement.type === "Text") {
                    tPos.y = 0.02;
                }
                tRoomElement.setPosition(tPos);
            }
        }
        let tCmd = new InsertElements(this.mApp, [tRoomElement]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tRoomElement.updateGeometry();
            if (tRoomElement.type === "Arena") {
                this.cutOverlappingShapes();
            }
            this.mApp.model.setSelection([tRoomElement]);
            this.contentChanged();
            this.mNotTriggerContentChanged = false;
            return (tRoomElement);
        });
        return (tPromise);
    }
    /**
     * Inserts a new room element.
     */
    insertBuilding(pPolygon, pHeight) {
        let tRoomElement = new RoomElement(this, "Building");
        let tCmd = new InsertElements(this.mApp, [tRoomElement]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tRoomElement.setHeight(pHeight * 2.55, false);
            let tBuildingGeo = tRoomElement.geometry;
            tBuildingGeo.setPoints(pPolygon);
            this.mBuildings.push(tRoomElement);
            tRoomElement.updateGeometry();
            return (tRoomElement);
        });
        return (tPromise);
    }
    /**
     * Inserts a new room element.
     */
    insertCustomBuilding(pPolygon, pHeight = 2, pDrawStatus = 0, pId = "") {
        let tCustomBuilding = new RoomElement(this, "CustomBuilding");
        let tBuilding_BottomTop = new RoomElement(this, "Plane_BottomTop", null, true, 'CustomBuildingBottomTop');
        this.app.model.addElement(tBuilding_BottomTop);
        tBuilding_BottomTop.setRE_reference(tCustomBuilding);
        tCustomBuilding.setRE_reference(tBuilding_BottomTop);
        tBuilding_BottomTop.setHeight(pHeight * 2.55, false);
        let tCmd = new InsertElements(this.mApp, [tCustomBuilding]);
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            tCustomBuilding.setHeight(pHeight * 2.55, false);
            let tBuildingGeo = tCustomBuilding.geometry;
            tBuildingGeo.setPoints(pPolygon, true);
            tCustomBuilding.setOriginalElementParams(Vector3.Zero(), pHeight * 2.55);
            tCustomBuilding.updateGeometry();
            tBuilding_BottomTop.updateGeometry();
            if (pDrawStatus === 0 || pDrawStatus === 2) {
                // center polygon points
                let tWM = tCustomBuilding.node.getWorldMatrix().getRotationMatrix();
                let tREgeo = tCustomBuilding.geometry;
                let tPoints = tREgeo.getPoints();
                let tpolyCenter = Util.getCenterofPolygon(tPoints);
                let tCenter_rot = Vector3.TransformCoordinates(tpolyCenter, tWM);
                let tcurrPos = tCustomBuilding.position.clone();
                tPoints = Util.centerPolygon(tPoints);
                tREgeo.setPoints(tPoints, true);
                tCustomBuilding.setTransformStatus("");
                tCustomBuilding.updateGeometry(true, false);
                const tinitPos = tcurrPos.add(tCenter_rot);
                tCustomBuilding.setPosition(tinitPos);
                tCustomBuilding.Params.ElementOriginalPositionX = tinitPos.x;
                tCustomBuilding.Params.ElementOriginalPositionY = tinitPos.y;
                tCustomBuilding.Params.ElementOriginalPositionZ = tinitPos.z;
                if (pDrawStatus === 0) {
                    this.mBuildings.push(tCustomBuilding);
                    let tnewBuildings = {};
                    tnewBuildings.polygon = pPolygon.slice();
                    tnewBuildings.height = pHeight;
                    tnewBuildings.id = tCustomBuilding.IdStr;
                    this.minitialBuildings.set(tnewBuildings.id, { BuildingData: tnewBuildings });
                }
                if (pDrawStatus === 2) {
                    tCustomBuilding.setREId(pId);
                }
            }
            return (tCustomBuilding);
        });
        return (tPromise);
    }
    /**
     * Inserts a new floor.
     */
    insertFloor(pPoints) {
        this.mNotTriggerContentChanged = true;
        let tFloor = new RoomElement(this, "Floor");
        this.mApp.model.addElement(tFloor);
        // let tCmd = new InsertElements(this.mApp, [tFloor]);
        // let tPromise = this.mApp.commands.executeCommand(tCmd).then(() =>
        // {
        let tFloorGeo = tFloor.geometry;
        this._addFloor(tFloor);
        let tPolygonCenter = Util.getCenterofPolygon(Util.to3dArray(pPoints));
        let tcenteredPoints = Util.centerPolygon(Util.to3dArray(pPoints), new Vector3(0, 0, 0));
        //tFloorGeo.setPoints(pPoints);
        tFloorGeo.setPoints(Util.to2dArray(tcenteredPoints));
        tFloor.setPosition(tPolygonCenter);
        tFloor.updateGeometry();
        if (!this.mRoomFloorVisibility)
            tFloor.node.setEnabled(this.mRoomFloorVisibility);
        this.mNotTriggerContentChanged = false;
        //     return (tFloor);
        // });
        // return (tPromise);
        return (Promise.resolve(tFloor));
    }
    /**
     * Draw new spline.
     */
    _insertSpline(pPoints) {
        let tSpline = new RoomElement(this, "Spline");
        let tSplineGeo = tSpline.geometry;
        tSplineGeo.setPoints(pPoints);
        tSpline.updateGeometry();
        return (tSpline);
    }
    /**
     * Draw new Line.
     */
    _insertLine(pPoints, pMatCat, pMatValue) {
        let tLine = new RoomElement(this, "Line", null, undefined, undefined, undefined, pMatCat, pMatValue);
        let tLineGeo = tLine.geometry;
        tLineGeo.setPoints(pPoints);
        tLine.updateGeometry();
        return (tLine);
    }
    /**
     * Draw new SafetyZone.
     */
    _insertSafetyZone(pPoints, pFreespacePoints, pBasketId, pCAG) {
        let tSafetyZone = new RoomElement(this, "SafetyZone");
        let tDummySafetyZone = new RoomElement(this, "DummySafetyZone");
        if (tDummySafetyZone != null) {
            tSafetyZone.setRE_reference(tDummySafetyZone);
            tDummySafetyZone.setRE_reference(tSafetyZone);
            this.app.model.addElement(tDummySafetyZone);
        }
        let tLineGeo = tSafetyZone.geometry;
        let tCAGmeasure = Util.getCAGMeasure(pCAG);
        tSafetyZone.setMaterialId("color" + this.mMaterialSplitStr + this.mStandardColor);
        tSafetyZone.setWidth(tCAGmeasure.x, false, false);
        tSafetyZone.setDepth(tCAGmeasure.z, false, false);
        tSafetyZone.setHeight(tCAGmeasure.y, false);
        tSafetyZone.setCAG(pCAG);
        tSafetyZone.setCAGoriginal(pCAG);
        tLineGeo.setFreeSpacePoints(pFreespacePoints);
        tSafetyZone.setBasketId(pBasketId);
        tSafetyZone.updateGeometry(true, true);
        return (tSafetyZone);
    }
    /**
     * Inserts a new room element.
     */
    insertSafetyZone() {
        this.mNotTriggerContentChanged = true;
        let tRoomElement = new RoomElement(this, "SafetyZone");
        return (tRoomElement);
    }
    /**
     * Draw new rectangle.
     */
    _insertRectangle(pPoints) {
        let tRectangle = new RoomElement(this, "Rectangle");
        let tRectangleGeo = tRectangle.geometry;
        tRectangleGeo.setPoints(pPoints);
        tRectangle.updateGeometry();
        return (tRectangle);
    }
    /**
     * Draw new grid.
     */
    _insertGrid(pPoints, pGridId) {
        let tgridFound = null;
        for (let tRE of this.mRoomElements) {
            if (tRE.type != "Grid") {
                continue;
            }
            if (tRE.Id === pGridId) {
                tgridFound = tRE;
                break;
            }
        }
        if (tgridFound != null) {
            return (tgridFound);
        }
        let tGrid = new RoomElement(this, "Grid");
        let tGridGeo = tGrid.geometry;
        tGridGeo.setPoints(pPoints);
        tGrid.updateGeometry();
        tGrid.updateTransformation();
        return (tGrid);
    }
    /**
     * Draw new circle.
     */
    _insertCircle(pPoints) {
        let tCircle = new RoomElement(this, "Circle");
        let tCircleGeo = tCircle.geometry;
        tCircle.setPosition(pPoints[0], false);
        tCircleGeo.setPoints(pPoints);
        tCircle.updateGeometry();
        return (tCircle);
    }
    _insertSphere(pPoints, pAudioSphere = false) {
        let tmatSet;
        if (pAudioSphere) {
            tmatSet = {};
            tmatSet.category = 'color';
            tmatSet.value = '#ffffff';
            tmatSet.loadMatId = "";
            tmatSet.rotation = 0;
            tmatSet.offsetU = 0;
            tmatSet.offsetU_init = 0;
            tmatSet.offsetV = 0;
            tmatSet.offsetV_init = 0;
            tmatSet.scaleU = 1;
            tmatSet.scaleU_init = 1;
            tmatSet.scaleV = 1;
            tmatSet.scaleV_init = 1;
            tmatSet.setInit = false;
            tmatSet.idAddOn = '';
            tmatSet.loaded = false;
        }
        let tSphere = new RoomElement(this, "Sphere", null, undefined, undefined, tmatSet);
        tSphere.setAudioSphere(pAudioSphere);
        let tSphereGeo = tSphere.geometry;
        tSphere.setPosition(pPoints[0], false);
        tSphereGeo.setPoints(pPoints);
        tSphere.updateGeometry();
        tSphere.setAudioSphere(pAudioSphere);
        return (tSphere);
    }
    /**
     * Draw new Road.
     */
    _insertRoad(pPoints, pCurvedRoad = true) {
        let tRoad = new RoomElement(this, "Road", null, pCurvedRoad);
        let tLineGeo = tRoad.geometry;
        tLineGeo.setPoints(pPoints);
        tRoad.updateGeometry();
        return (tRoad);
    }
    /**
     * Draw new Hedge.
     */
    _insertHedge(pPoints, pCurvedHedge = true) {
        let tHedge = new RoomElement(this, "Hedge", null, pCurvedHedge);
        let tLineGeo = tHedge.geometry;
        tLineGeo.setPoints(pPoints);
        tHedge.updateGeometry();
        return (tHedge);
    }
    /**
     * Draw new Hedge.
     */
    _insertFence(pPoints, pCurvedHedge = true) {
        let tFence = new RoomElement(this, "Fence", null, pCurvedHedge);
        let tLineGeo = tFence.geometry;
        tLineGeo.setPoints(pPoints);
        tFence.updateGeometry();
        return (tFence);
    }
    cameraPathExists() {
        let tcamPath = null;
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "CameraPath") {
                tcamPath = tRE;
                break;
            }
        }
        return (tcamPath != null);
    }
    /**
    * Draw new camera path.
    */
    _insertCameraPath(pPoints) {
        let tcamPath = new RoomElement(this, "CameraPath");
        let tSplineGeo = tcamPath.geometry;
        tSplineGeo.setPoints(pPoints);
        tcamPath.updateGeometry();
        return (tcamPath);
    }
    insertPictureAsMaterial(pBASE64) {
        if (!isDataUri(pBASE64)) {
            return (Promise.resolve(null));
        }
        let tMaterialSet = {};
        let timgHash = Util.getHashOfString(pBASE64);
        let tName = "import" + this.materialSplitStr + timgHash;
        let tTexture = BJSTexture.CreateFromBase64String(pBASE64, tName, this.mScene, false, true, BJSTexture.TRILINEAR_SAMPLINGMODE);
        tTexture.anisotropicFilteringLevel = this.mScene.getEngine().getCaps().maxAnisotropy;
        let tMaterial = new PbrMaterial(tName, this.mMatManager);
        tMaterial.albedoTexture = tTexture;
        tMaterial.unlit = true;
        tMaterial.roughness = 0.999;
        tMaterial.metallic = 0.001;
        tMaterialSet.category = "import";
        tMaterialSet.value = timgHash;
        let timgExt = Util.getImgExt(pBASE64.trim());
        let talpha = timgExt === "png" ? true : false;
        let tFileName = timgHash + "." + timgExt;
        let tnc = ZipDocumentContainer.NewContainer();
        tnc.addFile(fromDataUri(pBASE64.trim()), tFileName);
        let tPromise = this.mMatManager.createResourceTexture(tName, tFileName, talpha, tnc).then((pTexture) => {
            if (pTexture != null) {
                tMaterial.albedoTexture = pTexture;
                tMaterial.useAlphaFromAlbedoTexture = true;
                this.mMatManager.addMaterial(tName, tMaterial);
            }
            return (Promise.resolve(tMaterialSet));
        });
        return (tPromise);
    }
    /**
     * Inserts a new room element.
     */
    async insertBackgroundImage(pURL, pWith, pHeight, pPosX, pPosZ) {
        let tselection = this.mApp.model.selection;
        if (tselection.length === 0) {
            let timgBk = await this._insertBackgroundImage(pURL, pWith, pHeight, pPosX, pPosZ);
            return (timgBk);
        }
        if (tselection.length === 1) {
            let telement = tselection[0];
            if (telement instanceof RoomElement) {
                if (telement.type === "Sphere") {
                    this.setSphereTexture(pURL);
                    return (Promise.resolve(telement));
                }
                let timgBk = await this._insertBackgroundImage(pURL, pWith, pHeight, pPosX, pPosZ);
                return (timgBk);
            }
            return (Promise.resolve(null));
        }
        return (Promise.resolve(null));
    }
    /**
     * Set an image for a selected wallpicture.
     */
    setWallpaperImage(pURL, pWith, pHeight, pPosX, pPosZ) {
        this.insertPictureAsMaterial(pURL).then((pResult) => {
            if (pResult != null) {
                let tResult = pResult;
                let tSel = this.mApp.model.selection;
                if (tSel.length === 1 &&
                    ((tSel[0] instanceof WallAddition) && tSel[0].type === "PictureFrame")) {
                    let tAddition = tSel[0];
                    tAddition.set_MaterialSet(tResult);
                    tAddition.setKeepRatio(false);
                    let tRatio = pWith / pHeight;
                    let tnewHeight = tAddition.width / tRatio;
                    tAddition.setHeight(tnewHeight, true);
                    tAddition.setKeepRatio(true);
                    let t_Promise = this.checkMaterial(tAddition.materialSet).then((pResult1) => {
                        tAddition.updateGeometry();
                        this.mApp.model.setSelection([tAddition]);
                    });
                    return (t_Promise);
                }
                return (Promise.resolve(null));
            }
            return (Promise.resolve(null));
        });
    }
    /**
     * Inserts wall side dummy objects for exact wall/room bounding box.
     */
    insertWallSide_DummyObjects() {
        let twallSide_Dummy = new RoomElement(this, "WallSideDummy");
        this.mApp.model.addElement(twallSide_Dummy);
        return (twallSide_Dummy);
    }
    /**
     * CreateARPicker
     */
    _createARpicker(pPoints) {
        let tRoomAR = new RoomElement(this, "RoomAR");
        let tARPicker = tRoomAR.geometry;
        tRoomAR.updateGeometry();
        return (tRoomAR);
    }
    firstextMaterialofCategory(pCategory) {
        let tRes = "";
        for (let textMat of this.mExtLoadedMaterialDefinitions) {
            if (textMat.category === pCategory) {
                tRes = textMat.id;
                break;
            }
        }
        return (tRes);
    }
    findextMaterial(pCategory, pValue) {
        for (let textMat of this.mExtLoadedMaterialDefinitions) {
            if (textMat.category.toLocaleLowerCase() === pCategory.toLocaleLowerCase() &&
                textMat.id.toLocaleLowerCase() === pValue.toLocaleLowerCase()) {
                return (textMat.id);
            }
        }
        return (null);
    }
    firstextSoundofCategory(pCategory) {
        let tRes = null;
        for (let textSound of this.mExtLoadedSoundEffects) {
            if (textSound.category === pCategory) {
                tRes = textSound;
                break;
            }
        }
        return (tRes);
    }
    _removeFloor() {
        this.removeFloors();
    }
    removeBuildings() {
        if (this.mBuildings.length === 0)
            return;
        let tCommand = new DeleteElements(this.app, this.mBuildings);
        if (tCommand.isAvailable()) {
            let tPromise = this.app.commands.executeCommand(tCommand);
            tPromise.catch((pError) => {
                Log.error("Failed to delete buildings", pError);
            });
            this.mBuildings = [];
            this.minitialBuildings = new Map();
        }
    }
    getBuilding(pId) {
        let tRes = null;
        for (let tBuilding of this.mRoomElements) {
            if (tBuilding.type === "CustomBuilding" && tBuilding.IdStr === pId) {
                tRes = tBuilding;
                break;
            }
        }
        return (tRes);
    }
    resetInitialBuildings() {
        this.minitialBuildings.forEach((pEntry) => {
            let tBuilding = this.getBuilding(pEntry.BuildingData.id);
            if (tBuilding != null) {
                tBuilding.resetCustomBuilding();
            }
            else {
                this.insertCustomBuilding(pEntry.BuildingData.polygon, pEntry.BuildingData.height, 2, pEntry.BuildingData.id);
            }
        });
    }
    setShapeHeight(shapeLevel, SelectionOnly = true) {
        let tSel = this.mApp.model.selection;
        if (!SelectionOnly) {
            tSel = this.mApp.model.elements;
        }
        if (tSel.length === 0) {
            return;
        }
        let tshapeSelected = false;
        for (let telem of tSel) {
            if (telem instanceof RoomElement) {
                tshapeSelected = true;
                telem.setShapeHeight(shapeLevel);
            }
        }
        if (tshapeSelected) {
            this.cutOverlappingShapes();
        }
    }
    set2dLines(pLines) {
        this.m2dLines = pLines;
    }
    setFloorColor(pColor) {
        this.mFloorColor = pColor;
        this.findRoom();
    }
    setFloorMaterialId(pWallIds, pMaterialSet) {
        let tfloorEntry = this.mFloorsPolygons.get(pWallIds);
        if (tfloorEntry !== undefined)
            tfloorEntry.materialSet = pMaterialSet;
    }
    /**
     * Sets visibility of the skybox (true/false)
     */
    setSkyboxVisibility(pVisible) {
        if (this.mSkyBox == null)
            return;
        this.mSkyBoxVisible = pVisible;
        this.mSkyBox.visibility = this.mSkyBoxVisible ? 1 : 0;
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Add buildings to central list (e.g.: in case of cloning)
     */
    addBuilding(pBuilding) {
        this.mBuildings.push(pBuilding);
    }
    /**
     * Add initial buildings to central list (e.g.: in case of cloning)
     */
    addInitialBuilding(pBuilding, pHeight = 2) {
        if (pBuilding.type !== "CustomBuilding")
            return;
        let tPolygon = pBuilding.Params.ElementOriginalPoints;
        let tnewBuildings = {};
        tnewBuildings.polygon = tPolygon.slice();
        tnewBuildings.height = pHeight;
        tnewBuildings.id = pBuilding.IdStr;
        this.minitialBuildings.set(tnewBuildings.id, { BuildingData: tnewBuildings });
    }
    /**
     * Sets the opacity of availible buildings
     * range: 0 - 1
     */
    setBuildingOpacity(pValue) {
        let tValue = clamp(pValue, 0.01, 1);
        if (eq(this.mBuildingOpacity, tValue))
            return;
        this.mBuildingOpacity = tValue;
        if (this.mBuildings.length === 0)
            return;
        for (let tBuilding of this.mBuildings) {
            tBuilding.setBuildingOpacity(this.mBuildingOpacity);
            tBuilding.updateGeometry();
        }
        this.mApp.viewer.requestRenderFrame();
    }
    setView() {
        if (this.mView == null) {
            let tView = this.view;
            if (tView != null) {
                this.mView = tView;
                this.app.viewer.scene.onBeforeCameraRenderObservable.add(this.onBeforeCameraChanged);
                this.mCurrentViewMode = this.mView.cameraControl.cameraMode;
                if (this.mView.cameraControl.cameraMode !== 7 && (this.mSkyBox != null)) {
                    this.mSkyBox.visibility = 0;
                }
                this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
            }
            return;
        }
        if (this.mSkyBox != null) {
            this.mSkyBox.position = this.mView.camera.position;
        }
    }
    setVisibilityMode(pMode) {
        this.mVisibility = pMode;
        for (let tWall of this.mWalls) {
            tWall._updateVisibility();
        }
        for (let tRoute of this.mRoutes) {
            tRoute._updateVisibility();
        }
        this.onBeforeCameraChanged();
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Automatic fade out of wall in front of users view
     */
    setHiddenWalls(pEnable) {
        this.mHiddenWallsStatus = pEnable;
        this._triggerHiddenWalls(true, this.mHiddenWallsStatus);
    }
    /**
     * Set visivility of all existing audio spheres
     */
    setAudioSpheresVisibility(pVisible) {
        this.mAudioSpheresVisible = pVisible;
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "Sphere" && tRE.Params.audioSphere) {
                this.mApp.model.removeFromSelection(tRE);
                tRE.node.setEnabled(pVisible);
            }
        }
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Mute / unmute existing audio spheres
     */
    muteAudioSpheres(pMute) {
        this.mAudioSpheresMute = pMute;
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "Sphere" && tRE.Params.audioSphere) {
                if (pMute) {
                    tRE.setAudioVolume(0, true);
                }
                else {
                    tRE.setAudioVolume(tRE.audioVolume, true);
                }
            }
        }
    }
    /**
     * Set visivility of all existing floors
     */
    setFloorsVisibility(pTransparent) {
        for (let tFloor of this.mFloors) {
            if (tFloor == null)
                continue;
            tFloor.Params.floorTransparent = pTransparent;
        }
        this.mFloorsPolygons.forEach((pEntry) => {
            if (pEntry.floorRE != null) {
                pEntry.transparent = pEntry.floorRE.Params.floorTransparent;
            }
        });
        for (let tFloor of this.mFloors) {
            if (tFloor == null)
                continue;
            tFloor.updateGeometry();
        }
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Set visivility of route
     */
    setRoutesVisibility(pVisible) {
        this.mRoutesVisible = pVisible;
        for (let tRoute of this.mRoutes) {
            this.mApp.model.removeFromSelection(tRoute);
            tRoute.node.setEnabled(pVisible);
        }
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Set visivility of dimension lines
     */
    setDimensionLineVisibility(pVisible) {
        this.mMeasureVisible = pVisible;
        for (let tMeasure of this.mMeasures) {
            this.mApp.model.removeFromSelection(tMeasure);
            tMeasure.setVisibility(pVisible);
        }
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Set visivility of dimension lines
     */
    setRoomFloorVisibility(pVisible) {
        this.mRoomFloorVisibility = pVisible;
        for (let tRE of this.RoomElements) {
            if (tRE.type === "Floor") {
                this.mApp.model.removeFromSelection(tRE);
                tRE.node.setEnabled(pVisible);
            }
        }
        if (pVisible)
            this.findRoom();
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Set visivility of mirror interactor (wall openings)
     */
    setMirrorInteractorVisibility(pVisible) {
        this.mShowOpeningMirrorInteractor = pVisible;
    }
    /**
     * Assign new thickness to all walls.
     */
    setWallThickness(pThickness) {
        let tCmd = new EditWalls(this);
        this.beginCollectWallUpdates();
        for (let tWall of this.mWalls)
            tWall.setThickness(pThickness);
        this.endCollectWallUpdates();
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            this.findRoom();
            this.mApp.viewer.requestRenderFrame();
        });
        return (tPromise);
    }
    /**
     * Assign new hidden status to a wall.
     */
    setWallisHidden(pwallId) {
        for (let tWall of this.mWalls) {
            if (tWall.id.toString() === pwallId) {
                tWall.setHidden(true);
                break;
            }
        }
    }
    /**
     * Assign wall opacity value
     * range: 0 - 1
     */
    setWallOpacity(pValue) {
        let tValue = clamp(pValue, 0.01, 1);
        if (tValue === this.mWallOpacitiy)
            return;
        this.mWallOpacitiy = tValue;
        for (let tWall of this.mWalls)
            tWall.updateGeometry();
        this.mApp.viewer.requestRenderFrame();
    }
    /**
     * Assign new height to all walls.
     */
    setWallHeight(pHeight) {
        let tCmd = new EditWalls(this);
        this.beginCollectWallUpdates();
        for (let tWall of this.mWalls)
            tWall.setHeight(pHeight);
        this.endCollectWallUpdates();
        let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
            this.findRoom();
            this.mApp.viewer.requestRenderFrame();
        });
        return (tPromise);
    }
    setSafetyZoneData(pBasketId, pMaterialIndex, pColor, pMaterialCategory, pMaterialValue, pMaterialId) {
        let tEntry = this.mSafetyZoneData.get(pBasketId);
        if (tEntry !== undefined) {
            tEntry.Color = pColor;
            tEntry.MaterialCategory = pMaterialCategory;
            tEntry.MaterialIndex = pMaterialIndex;
            tEntry.MaterialId = pMaterialId;
            tEntry.MaterialValue = pMaterialValue;
        }
        else {
            if (pBasketId === undefined || (pBasketId !== undefined && pBasketId.trim() === ""))
                return;
            this.mSafetyZoneData.set(pBasketId, { MaterialIndex: pMaterialIndex, Color: pColor, MaterialCategory: pMaterialCategory,
                MaterialValue: pMaterialValue, MaterialId: pMaterialId });
        }
    }
    setWallSelectionColor(pColor) {
        this.mWall2dMaterialSelected.albedoColor = Color3.FromHexString(pColor);
        this.mApp.viewer.requestRenderFrame();
    }
    setEGRLayerVisibility(pVisible) {
        this.mEGRLayerVisibility = pVisible;
    }
    setManufacturerSeries(pList) {
        if (pList.length === 0)
            return;
        this.mSeriesList = [];
        for (let tSerie of pList) {
            if (tSerie.trim() === "")
                continue;
            this.mSeriesList.push(tSerie);
        }
        this.setSZLayerVisibility(false);
    }
    finishedMaterialLoading() {
        this.mMaterialLoading = false;
    }
    setProjectManufacturer(pManufacturer) {
        if (pManufacturer.trim() !== "") {
            this.mProjectManufacturer = pManufacturer.trim();
        }
    }
    setSZ_standard(pSZstandard) {
        if (pSZstandard.trim() !== "") {
            this.mSZ_standards_selected = pSZstandard.trim();
        }
        this.regenerateSZ();
    }
    setSZ_standards(pList) {
        if (pList.length === 0)
            return;
        this.mSZ_standards = [];
        for (let tStandard of pList) {
            if (tStandard.trim() === "")
                continue;
            this.mSZ_standards.push(tStandard);
        }
        this.setSZLayerVisibility(false);
    }
    async getMaterialList() {
        if (!this.mextMaterialisLoading) {
            this.mextMaterialisLoading = true;
            this.mExtLoadedMaterialDefinitions = [];
            this.mExtLoadedMaterialCategory = [];
            await this.loadMaterialDefinitions().then(() => {
                this.mExtLoadedMaterials = [];
                this.mExtMaterialsMap = new Map();
                this.prepareArea2DMaterialPropValues();
                this.prepareExtMaterialCategoryPropValues();
                this.mextMaterialisLoading = false;
            });
        }
    }
    async getSoundEffectList() {
        if (!this.mextSoundEffectisLoading) {
            this.mextSoundEffectisLoading = true;
            this.mExtLoadedSoundEffects = [];
            this.mExtLoadedSoundCategory = [];
            await this.loadSoundFiles().then(() => {
                this.prepareExtSoundEffectsPropValues();
                this.preparePropList_audio_local_effects();
                this.preparePropList_audio_background_effects();
                this.preparePropList_audio_background_music();
                this.mextSoundEffectisLoading = false;
            });
        }
    }
    async _loadExternalMaterial(pMaterialId) {
        if (this.mMatManager.hasMaterial(pMaterialId)) {
            return (Promise.resolve(2));
        }
        let t_entry = null;
        for (let tentry of this.mExtLoadedMaterialDefinitions) {
            if (tentry.materialValue === pMaterialId) {
                t_entry = tentry;
                break;
            }
        }
        if (t_entry == null) {
            let tmatId = pMaterialId.split(this.mMaterialSplitStr);
            let tmatValue = tmatId.pop();
            let tmatCategory = tmatId.pop();
            if ((tmatCategory === "color" || tmatCategory === "") && tmatValue !== undefined) {
                this.createColorMaterial(tmatValue);
            }
            return (Promise.resolve(1));
        }
        if (t_entry.matzFile !== "") {
            let tPromise = await MATZLoader.getMaterialOfMATZ(t_entry.matzFile, t_entry.id, t_entry, this, this.mMatManager);
            return (tPromise);
        }
        return (Promise.resolve(0));
    }
    createCamLogo() {
        this.mcamLogo = new PbrMaterial("", this.mMatManager);
        this.mcamLogo.albedoColor = new Color3(0.8, 0.8, 0.8).toLinearSpace();
        this.mcamLogo.albedoTexture = new BJSTexture(this.mDataPath + "textures/camlogo.png" + "?" + this.mRandomString, this.mScene);
        // this.mcamLogo.albedoTexture = new Texture(this.mDataPath + "textures/camlogo.png" + "?" + this.mRandomString, this.mScene);
        this.mcamLogo.albedoTexture.hasAlpha = true;
        this.mcamLogo.metallic = 0;
        this.mcamLogo.roughness = 0.2;
        this.mcamLogo.useAlphaFromAlbedoTexture = true;
        this.mcamLogo.unlit = true;
        this.mMatManager.addMaterial('camLogo', this.mcamLogo);
    }
    createEOX_XML() {
        if (document.implementation.createDocument &&
            document.implementation.createDocumentType != null) {
            let tdomDocument = document.implementation.createDocument("", "", null);
            // root node
            let tAttribsList = new Array();
            tAttribsList.push({ qualifiedName: "xsi:noNamespaceSchemaLocation", value: "EOX_1.1-PRE4.xsd" });
            tAttribsList.push({ qualifiedName: "xmlns:xsi", value: "http://www.w3.org/2001/XMLSchema-instance" });
            let trootNode = Util._xmlAddRootNode(tdomDocument, "eoxObjects", tAttribsList);
            for (let tWall of this.mWalls) {
                tWall.getEOXDescription(tdomDocument, trootNode);
            }
            for (let tMeasure of this.mMeasures) {
                tMeasure.getEOXDescription(tdomDocument, trootNode);
            }
            for (let tRE of this.mRoomElements) {
                if (tRE.type !== "DummySafetyZone")
                    tRE.getEOXDescription(tdomDocument, trootNode);
            }
            //convert XML to string
            let tserializer = new XMLSerializer();
            let txmlString = tserializer.serializeToString(tdomDocument);
            txmlString = '<?xml version="1.0" encoding="utf-8"?>' + txmlString;
            return (txmlString);
        }
        return ("");
    }
    createEOX_XML_structure() {
        if (document.implementation.createDocument &&
            document.implementation.createDocumentType != null) {
            let tdomDocument = document.implementation.createDocument("", "", null);
            // root node
            let tAttribsList = new Array();
            tAttribsList.push({ qualifiedName: "xsi:noNamespaceSchemaLocation", value: "EOX_1.1-PRE4.xsd" });
            tAttribsList.push({ qualifiedName: "xmlns:xsi", value: "http://www.w3.org/2001/XMLSchema-instance" });
            let trootNode = Util._xmlAddRootNode(tdomDocument, "eoxObjects", tAttribsList);
            for (let tWall of this.mWalls) {
                tWall.getEOXDescription(tdomDocument, trootNode);
            }
            for (let tMeasure of this.mMeasures) {
                tMeasure.getEOXDescription(tdomDocument, trootNode);
            }
            for (let tRE of this.mRoomElements) {
                tRE.getEOXDescription(tdomDocument, trootNode);
            }
            //convert XML to string
            let tserializer = new XMLSerializer();
            let txmlString = tserializer.serializeToString(tdomDocument);
            txmlString = '<?xml version="1.0" encoding="utf-8"?>' + txmlString;
            return (txmlString);
        }
        return ("");
    }
    createEOX_XML_objects() {
        if (document.implementation.createDocument &&
            document.implementation.createDocumentType != null) {
            let tdomDocument = document.implementation.createDocument("", "", null);
            // root node
            let tAttribsList = new Array();
            tAttribsList.push({ qualifiedName: "xsi:noNamespaceSchemaLocation", value: "EOX_1.1-PRE4.xsd" });
            tAttribsList.push({ qualifiedName: "xmlns:xsi", value: "http://www.w3.org/2001/XMLSchema-instance" });
            let trootNode = Util._xmlAddRootNode(tdomDocument, "eoxObjects", tAttribsList);
            for (let tWall of this.mWalls) {
                tWall.getEOXDescription_objects(tdomDocument, trootNode);
            }
            //convert XML to string
            let tserializer = new XMLSerializer();
            let txmlString = tserializer.serializeToString(tdomDocument);
            txmlString = '<?xml version="1.0" encoding="utf-8"?>' + txmlString;
            return (txmlString);
        }
        return ("");
    }
    createEOX() {
        let txmlString = this.createEOX_XML_structure();
        let blob = new Blob([txmlString], {
            type: "text/xml"
        });
        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "structure.xml";
        a.click();
    }
    createEOX_structure() {
        let txmlString = this.createEOX_XML_structure();
        let blob = new Blob([txmlString], {
            type: "text/xml"
        });
        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "structure.xml";
        a.click();
    }
    createEOX_objects() {
        let txmlString = this.createEOX_XML_objects();
        let blob = new Blob([txmlString], {
            type: "text/xml"
        });
        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "objects.xml";
        a.click();
    }
    /** @internal */
    getSoundEffectPropValue(pCat, pSoundEffectId) {
        const tkey = pCat + RoomManager.CATVALUE_SPLITTER + pSoundEffectId;
        const tsndEffect = this.mextSoundEffectsPropValueMap.get(String(tkey));
        let tRes = null;
        if (tsndEffect != null) {
            tRes = tsndEffect;
        }
        return (tRes);
    }
    getAnimationStart() {
        let tRes = null;
        const tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            return (null);
        }
        if (tcamPath.Params.CamPointTargetsCalculated.length === 0) {
            return (null);
        }
        const tstart = tcamPath.Params.CamPointTargetsCalculated[0];
        const tPos = Vector3.TransformCoordinates(tstart.camPosition, tcamPath.transform);
        tRes = { position: tPos, target: tstart.target };
        return (tRes);
    }
    getAllRotations(pParent) {
        let tRes = new Quaternion(0, 0, 0, 1);
        if (pParent != null) {
            if (pParent.rotationQuaternion != null)
                tRes = pParent.rotationQuaternion;
            if (pParent.parent != null)
                tRes = tRes.multiply(this.getAllRotations(pParent.parent));
        }
        return (tRes);
    }
    getSoundEffectsById(pId) {
        for (const tSEData of this.mExtLoadedSoundEffects) {
            const tid = tSEData.category + RoomManager.CATVALUE_SPLITTER + tSEData.id;
            if (tid === pId) {
                return (tSEData);
            }
        }
        return (null);
    }
    getSoundEffectsTextById(pId) {
        for (const tSEData of this.mExtLoadedSoundEffects) {
            if (tSEData.id === pId) {
                return (tSEData.fileURL);
            }
        }
        return (null);
    }
    getSubArticle(pMesh) {
        let tRes = "";
        if (pMesh.parent != null && (pMesh.parent.parent != null)) {
            tRes = pMesh.parent.parent.id;
        }
        return (tRes);
    }
    getMaterialCategoryValue(pList, pElementMaterial) {
        let tRes = pList[0];
        for (let tProp of pList) {
            let tcat = pElementMaterial.category === "" ? "color" : pElementMaterial.category;
            if (tProp.value === tcat) {
                tRes = tProp;
                break;
            }
        }
        return (tRes);
    }
    getMaterialValue(pMaterial, pMatList) {
        if (pMaterial.category.trim() === "") {
            return (this._getWallColorPropValue(pMaterial.value));
        }
        {
            let tRes = null;
            if (pMatList.length === 0)
                return (null);
            if (pMaterial.value.trim() === "") {
                tRes = pMatList[0];
                return (tRes);
            }
            let tcurrentMat = pMaterial.category + this.materialSplitStr + pMaterial.value;
            for (let tProp of pMatList) {
                let tcolorVal = "color" + this.materialSplitStr + tProp.value;
                if (tProp.value === tcurrentMat || tcolorVal === tcurrentMat) {
                    tRes = tProp;
                    break;
                }
            }
            if (tRes == null) {
                tRes = pMatList[0];
                let tValue = this.mTextColorPropValueMap.get(pMaterial.value);
                if (tValue == null) {
                    tValue = new PropertyValue(pMaterial.value);
                    tValue.smallIcon = this.generateColorPreviewImage(pMaterial.value);
                    tValue.text = this.resources.propertyCustomColor;
                }
                return (tValue);
            }
            return (tRes);
        }
    }
    getMaterialId(pMaterial) {
        let tRes = this._wallColors[0];
        let ttmp = pMaterial.category + this.materialSplitStr + pMaterial.value;
        if (ttmp.indexOf(this.materialSplitStr) === 0)
            tRes = "color" + this.materialSplitStr + pMaterial.value;
        else
            tRes = ttmp;
        //this.checkMaterial(tRes);
        return (tRes);
    }
    getMaterial_Categories(pType) {
        const tType = pType.toLocaleLowerCase();
        let tRes = new Array();
        let tKey = "";
        let tfilter_include = "";
        let tFilter_exclude = "";
        if (this.mExtLoadedMaterialCategory.length === 0) {
            tKey = "color";
            tRes.push(this._getextMaterialCategoryPropValue(tKey));
        }
        else {
            for (let tmatCat of this.mExtLoadedMaterialCategory) {
                tKey = tmatCat.id;
                tfilter_include = tmatCat.filter_include.trim().toLocaleLowerCase();
                tFilter_exclude = tmatCat.filter_exclude.trim().toLocaleLowerCase();
                if ((tfilter_include === "" || tfilter_include.indexOf(tType) > -1) &&
                    (tFilter_exclude === "" || tFilter_exclude.indexOf(tType) === -1)) {
                    tRes.push(this._getextMaterialCategoryPropValue(tKey));
                }
            }
        }
        return (tRes);
    }
    getMaterialsOfCategory(pMaterial, pType) {
        let tList = new Array();
        this._getextMaterialOfCategory(pMaterial.category, tList, pType);
        if (pMaterial.category.trim() === "" || pMaterial.category.trim() === "color") {
            this.mWallColorPropValueMap.forEach((pEntry) => {
                tList.push(pEntry);
            });
        }
        return (tList);
    }
    getTerrainTypePropValue(pValue) {
        let tRes = null;
        for (let tProp of this.mTerrainTypePropList) {
            const tPropValue = StringProperty.ConvertValueToString(tProp.value);
            if (tPropValue === pValue) {
                tRes = tProp;
                break;
            }
        }
        return (tRes);
    }
    getHillProfilePropValue(pValue) {
        let tRes = null;
        for (let tProp of this.mHillProfilePropList) {
            const tPropValue = StringProperty.ConvertValueToString(tProp.value);
            if (tPropValue === pValue) {
                tRes = tProp;
                break;
            }
        }
        return (tRes);
    }
    getHillMaterial(pMat) {
        const tMatId = pMat.id;
        const tHillMat = this.mHillMaterials.get(tMatId + '_hill');
        const tnormalMat = this.MatManager.getMaterial(tMatId);
        if (tnormalMat != null && tHillMat != null) {
            return (tHillMat);
        }
        if (tHillMat != null) {
            tHillMat.dispose();
            this.mHillMaterials.delete(tMatId);
        }
        return (null);
    }
    addHillMaterial(pId, pMat) {
        const tmat = this.mHillMaterials.get(pId + '_hill');
        if (tmat == null) {
            this.mHillMaterials.set(pId + '_hill', pMat);
            return (true);
        }
        return (false);
    }
    prepareTerrainTypePropList() {
        this.mTerrainTypePropList = new Array();
        let tVal_Flat = new PropertyValue();
        tVal_Flat.value = "Flat";
        tVal_Flat.text = this.resources.propertyTerrainType_Flat;
        this.mTerrainTypePropList.push(tVal_Flat);
        let tVal_Bottom = new PropertyValue();
        tVal_Bottom.value = "Bottom";
        tVal_Bottom.text = this.resources.propertyTerrainType_Bottom;
        this.mTerrainTypePropList.push(tVal_Bottom);
        let tVal_Top = new PropertyValue();
        tVal_Top.value = "Top";
        tVal_Top.text = this.resources.propertyTerrainType_Top;
        this.mTerrainTypePropList.push(tVal_Top);
    }
    prepareHillProfilePropList() {
        this.mHillProfilePropList = new Array();
        let tVal_Hard = new PropertyValue();
        tVal_Hard.value = "Hard";
        tVal_Hard.text = "Linear";
        tVal_Hard.smallIcon = this.mDataPath + "images/hard.png" + Util.URLrandomAddOn();
        tVal_Hard.largeIcon = this.mDataPath + "images/hard.png" + Util.URLrandomAddOn();
        this.mHillProfilePropList.push(tVal_Hard);
        let tVal_Medium = new PropertyValue();
        tVal_Medium.value = "Medium";
        tVal_Medium.text = "Round";
        tVal_Medium.smallIcon = this.mDataPath + "images/medium.png" + Util.URLrandomAddOn();
        tVal_Medium.largeIcon = this.mDataPath + "images/medium.png" + Util.URLrandomAddOn();
        this.mHillProfilePropList.push(tVal_Medium);
        let tVal_Soft = new PropertyValue();
        tVal_Soft.value = "Soft";
        tVal_Soft.text = "Soft";
        tVal_Soft.smallIcon = this.mDataPath + "images/soft.png" + Util.URLrandomAddOn();
        tVal_Soft.largeIcon = this.mDataPath + "images/soft.png" + Util.URLrandomAddOn();
        this.mHillProfilePropList.push(tVal_Soft);
    }
    prepareSpeedTypePropList() {
        this.mSpeedTypePropList = new Array();
        let tVal_Slow = new PropertyValue();
        tVal_Slow.value = 'slow';
        tVal_Slow.text = this.resources.propertySpeed_Slow;
        this.mSpeedTypePropList.push(tVal_Slow);
        let tVal_Medium = new PropertyValue();
        tVal_Medium.value = 'medium';
        tVal_Medium.text = this.resources.propertySpeed_Medium;
        this.mSpeedTypePropList.push(tVal_Medium);
        let tVal_Fast = new PropertyValue();
        tVal_Fast.value = 'fast';
        tVal_Fast.text = this.resources.propertySpeed_Fast;
        this.mSpeedTypePropList.push(tVal_Fast);
    }
    preparePropList_audio_local_effects() {
        this.mAudio_local_effects = new Array();
        let tLang = this.getLocaleLanguage();
        for (const tsoundEffect of this.mExtLoadedSoundEffects) {
            if (tsoundEffect.category != RoomManager.AUDIO_LOCAL_EFFECT) {
                continue;
            }
            let tdescr = this.getSoundEffectsTextByEntry(tsoundEffect, tLang);
            const tsoundEffectProp = new PropertyValue();
            tsoundEffectProp.value = tsoundEffect.category + RoomManager.CATVALUE_SPLITTER + tsoundEffect.id;
            tsoundEffectProp.text = tdescr;
            this.mAudio_local_effects.push(tsoundEffectProp);
        }
    }
    preparePropList_audio_background_effects() {
        this.mAudio_PropVal_background_effect = new Array();
        let tLang = this.getLocaleLanguage();
        for (const tsoundEffect of this.mExtLoadedSoundEffects) {
            if (tsoundEffect.category != RoomManager.AUDIO_BACKGROUND_EFFECT) {
                continue;
            }
            let tdescr = this.getSoundEffectsTextByEntry(tsoundEffect, tLang);
            const tsoundEffectProp = new PropertyValue();
            tsoundEffectProp.value = tsoundEffect.category + RoomManager.CATVALUE_SPLITTER + tsoundEffect.id;
            tsoundEffectProp.text = tdescr;
            this.mAudio_PropVal_background_effect.push(tsoundEffectProp);
        }
    }
    preparePropList_audio_background_music() {
        this.mAudio_PropVal_background_music = new Array();
        let tLang = this.getLocaleLanguage();
        for (const tsoundEffect of this.mExtLoadedSoundEffects) {
            if (tsoundEffect.category != RoomManager.AUDIO_BACKGROUND_MUSIC) {
                continue;
            }
            let tdescr = this.getSoundEffectsTextByEntry(tsoundEffect, tLang);
            const tsoundEffectProp = new PropertyValue();
            tsoundEffectProp.value = tsoundEffect.category + RoomManager.CATVALUE_SPLITTER + tsoundEffect.id;
            tsoundEffectProp.text = tdescr;
            this.mAudio_PropVal_background_music.push(tsoundEffectProp);
        }
    }
    getMainNode(pParent) {
        let tRes = null;
        if (pParent != null) {
            tRes = pParent;
            if (pParent.parent != null)
                tRes = this.getMainNode(pParent.parent);
        }
        return (tRes);
    }
    hideSafetyZonesOfSelection() {
        let tSel = this.mApp.model.selection;
        if (tSel.length < 1)
            return;
        for (let tObj of tSel) {
            if (tObj instanceof MainArticleElement) {
                let tMA = tObj;
                let tchildNodes = tMA.node.getChildTransformNodes();
                for (let tnode of tchildNodes) {
                    if (tnode.name === "room:SafetyZone") {
                        let tRE = SceneElement.FromNode(tnode, false);
                        if (tRE != null && tRE.type === "SafetyZone") {
                            //tRE.node.setEnabled(false);
                            tRE.set_SafetyZone_Visibility(false);
                        }
                    }
                }
            }
        }
        this.cutOverlappingSZShapes();
    }
    setCameraPathVisibility(visible) {
        let tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            return;
        }
        if (tcamPath.isSelected) {
            this.mApp.model.removeFromSelection(tcamPath);
        }
        this.mCamPathVisibility = visible;
        tcamPath.node.setEnabled(visible);
        this.mApp.viewer.requestRenderFrame();
    }
    showCamPathView(pIndex) {
        if (this.mView == null) {
            return;
        }
        let tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            return;
        }
        if (tcamPath.geometry == null) {
            return;
        }
        if (this.mView.cameraControl.cameraMode !== 7) {
            this.mView.cameraControl.cameraMode = 7;
        }
        const tcamPathGeo = tcamPath.geometry;
        let tcamTargets = tcamPath.Params.CamPointTargetsCalculated;
        let tcamPosIndex = tcamPathGeo.getBoxPosIndex();
        if (pIndex != null) {
            let ttmpIdx = clamp(pIndex, 0, tcamTargets.length - 1);
            tcamPosIndex = ttmpIdx;
        }
        let tcamPos = tcamPathGeo.getBoxPos(false, false, tcamPosIndex);
        tcamPos = Vector3.TransformCoordinates(tcamPos, tcamPath.transform);
        // tcamPos.y = tcamPath.height;
        let tcamTarget = tcamTargets[tcamPosIndex].target.clone();
        // tcamTarget = Vector3.TransformCoordinates(tcamTarget, tcamPath.transform);
        const tDir = (tcamTarget.subtract(tcamPos)).normalize().scale(0.125);
        tcamPos.addInPlace(tDir);
        if (tcamPosIndex === 0 && this.mAnimationStatus != null) {
            this.mAnimationStatus.stop();
            this.eventAnimationEnded.trigger(this);
            this.mAnimationStatus = null;
        }
        this.mApp.viewer.view.cameraControl.setPosition(tcamPos);
        this.mApp.viewer.view.cameraControl.setTarget(tcamTarget);
        tcamPathGeo.setBoxPosByIndex(tcamPosIndex);
        this._addCamData();
        tcamPath.updateGeometry();
        this.mApp.viewer.requestRenderFrame();
    }
    showSafetyZonesOfSelection() {
        let tSel = this.mApp.model.selection;
        if (tSel.length < 1)
            return;
        for (let tObj of tSel) {
            if (tObj instanceof MainArticleElement) {
                let tMA = tObj;
                let tchildNodes = tMA.node.getChildTransformNodes();
                for (let tnode of tchildNodes) {
                    if (tnode.name === "room:SafetyZone") {
                        let tRE = SceneElement.FromNode(tnode, false);
                        if (tRE != null && tRE.type === "SafetyZone") {
                            tRE.node.setEnabled(true);
                            tRE.set_SafetyZone_Visibility(true);
                        }
                    }
                }
            }
        }
        this.cutOverlappingSZShapes();
    }
    isBasketIdRegistered(pBasketId) {
        let tRes = null;
        for (let tItem of this.mArticleLayerMeshes) {
            if (tItem.basketId === pBasketId) {
                tRes = tItem;
                break;
            }
        }
        return (tRes);
    }
    createPropClasses() {
        this.mPropClasses = [];
        //base properties
        let tpc_base = new PropertyClass();
        tpc_base.key = "base";
        tpc_base.name = this.resources.propertyPropClass_base;
        this.mPropClasses.push(tpc_base);
        //wall side a
        let tpc_WallSide_a = new PropertyClass();
        tpc_WallSide_a.key = "wallSide_a";
        tpc_WallSide_a.name = this.resources.propertyWallSideA;
        this.mPropClasses.push(tpc_WallSide_a);
        //wall side b
        let tpc_WallSide_b = new PropertyClass();
        tpc_WallSide_b.key = "wallSide_b";
        tpc_WallSide_b.name = this.resources.propertyWallSideB;
        this.mPropClasses.push(tpc_WallSide_b);
        //door leaf
        let tpc_DoorLeaf = new PropertyClass();
        tpc_DoorLeaf.key = "doorleaf";
        tpc_DoorLeaf.name = this.resources.propertyDoorLeaf;
        this.mPropClasses.push(tpc_DoorLeaf);
        //door frame
        let tpc_DoorFrame = new PropertyClass();
        tpc_DoorFrame.key = "doorframe";
        tpc_DoorFrame.name = this.resources.propertyDoorFrame;
        this.mPropClasses.push(tpc_DoorFrame);
        //door sill
        let tpc_DoorSill = new PropertyClass();
        tpc_DoorSill.key = "doorsill";
        tpc_DoorSill.name = this.resources.propertyDoorSill;
        this.mPropClasses.push(tpc_DoorSill);
        //window sash
        let tpc_WindowSash = new PropertyClass();
        tpc_WindowSash.key = "sash";
        tpc_WindowSash.name = this.resources.propertySash;
        this.mPropClasses.push(tpc_WindowSash);
        //window sill
        let tpc_WindowSill = new PropertyClass();
        tpc_WindowSill.key = "sill";
        tpc_WindowSill.name = this.resources.propertySill;
        this.mPropClasses.push(tpc_WindowSill);
        //window glass
        let tpc_WindowPane = new PropertyClass();
        tpc_WindowPane.key = "windowpane";
        tpc_WindowPane.name = this.resources.propertyWindowPane;
        this.mPropClasses.push(tpc_WindowPane);
        //material
        let tpc_Material = new PropertyClass();
        tpc_Material.key = "material";
        tpc_Material.name = this.resources.propertyMaterial;
        this.mPropClasses.push(tpc_Material);
        //material arena track lane
        let tpc_Material_Arena_Track_Lane = new PropertyClass();
        tpc_Material_Arena_Track_Lane.key = "material_arena_tracklane";
        tpc_Material_Arena_Track_Lane.name = this.resources.propertyMaterial_Arena_TrackLane;
        this.mPropClasses.push(tpc_Material_Arena_Track_Lane);
        //material arena playing field
        let tpc_Material_Arena_PlayField = new PropertyClass();
        tpc_Material_Arena_PlayField.key = "material_arena_playfield";
        tpc_Material_Arena_PlayField.name = this.resources.propertyMaterial_Arena_PlayField;
        this.mPropClasses.push(tpc_Material_Arena_PlayField);
        //material arena side segments
        let tpc_Material_Arena_SideSegments = new PropertyClass();
        tpc_Material_Arena_SideSegments.key = "material_arena_sidesegments";
        tpc_Material_Arena_SideSegments.name = this.resources.propertyMaterial_Arena_SideSegments;
        this.mPropClasses.push(tpc_Material_Arena_SideSegments);
        //material arena track line
        let tpc_Material_Arena_TrackLine = new PropertyClass();
        tpc_Material_Arena_TrackLine.key = "material_arena_trackline";
        tpc_Material_Arena_TrackLine.name = this.resources.propertyMaterial_Arena_TrackLine;
        this.mPropClasses.push(tpc_Material_Arena_TrackLine);
        //terrain
        let tpc_Terrain = new PropertyClass();
        tpc_Terrain.key = "terrain";
        tpc_Terrain.name = this.resources.propertyTerrain;
        this.mPropClasses.push(tpc_Terrain);
        //fanlight
        let tpc_FanLight = new PropertyClass();
        tpc_FanLight.key = "fanlight";
        tpc_FanLight.name = this.resources.propertyFanLight;
        this.mPropClasses.push(tpc_FanLight);
        //glazing bar
        let tpc_GlazingBars = new PropertyClass();
        tpc_GlazingBars.key = "glazingbars";
        tpc_GlazingBars.name = this.resources.propertyGlazingBars;
        this.mPropClasses.push(tpc_GlazingBars);
        //fence: posts
        let tpc_Fence_Posts = new PropertyClass();
        tpc_Fence_Posts.key = "fence_posts";
        tpc_Fence_Posts.name = this.resources.propertyClass_Fence_Posts;
        this.mPropClasses.push(tpc_Fence_Posts);
        //fence: field
        let tpc_Fence_Field = new PropertyClass();
        tpc_Fence_Field.key = "fence_field";
        tpc_Fence_Field.name = this.resources.propertyClass_Fence_Field;
        this.mPropClasses.push(tpc_Fence_Field);
        //additional property
        let tpc_Additional = new PropertyClass();
        tpc_Additional.key = "additional";
        tpc_Additional.name = this.resources.propertyClass_Additional;
        this.mPropClasses.push(tpc_Additional);
        //local sound property
        let tpc_SoundLocal = new PropertyClass();
        tpc_SoundLocal.key = "sound";
        tpc_SoundLocal.name = this.resources.propertyClass_Sound;
        this.mPropClasses.push(tpc_SoundLocal);
        //cam path targets
        for (let i = 0; i < 500; i++) {
            let tpc_camCustomTarget = new PropertyClass();
            tpc_camCustomTarget.key = "POI" + "_" + i.toString();
            tpc_camCustomTarget.name = this.resources.propertyPointOfInterest + ' ' + i.toString();
            this.mPropClasses.push(tpc_camCustomTarget);
        }
    }
    addSafetyZoneToMainArticle(pMainArticle, pPropChanged = false) {
        let tRoomElement = this.insertSafetyZone();
        // main article properties were changed
        if (pPropChanged) {
            if (tRoomElement != null) {
                let tDummySafetyZone = new RoomElement(this, "DummySafetyZone");
                if (tDummySafetyZone != null) {
                    tRoomElement.setRE_reference(tDummySafetyZone);
                    tDummySafetyZone.setRE_reference(tRoomElement);
                    this.app.model.addElement(tDummySafetyZone);
                }
            }
            return (tRoomElement);
        }
        if (tRoomElement != null) {
            let tDummySafetyZone = new RoomElement(this, "DummySafetyZone");
            if (tDummySafetyZone != null) {
                tRoomElement.setRE_reference(tDummySafetyZone);
                tDummySafetyZone.setRE_reference(tRoomElement);
                this.app.model.addElement(tDummySafetyZone);
            }
            this._addRoomElement(tRoomElement);
            tRoomElement.node.parent = pMainArticle.node;
            tRoomElement.setMainElement(pMainArticle);
            pMainArticle.eventPropertiesChanged.addListener(() => this.mainArticlePropertiesChanged(pMainArticle, tRoomElement));
            pMainArticle.eventRemoved.addListener(() => this.removeRE(pMainArticle));
            return (tRoomElement);
        }
        return (null);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    removeRE(pMainArticle = null) {
        let tRE = this.getREofMainArticle(pMainArticle);
        if (tRE === null)
            return;
        if (tRE.node !== undefined) {
            if (pMainArticle != null) {
                removeElement(this.mAddedMainArticles, pMainArticle);
            }
            if (tRE.type === "SafetyZone") {
                this._removeRoomElement(tRE);
            }
            else {
                let tCommand = new DeleteElements(this.app, [tRE]);
                if (tCommand.isAvailable()) {
                    let tPromise = this.app.commands.executeCommand(tCommand).then((pRoomElement) => {
                        if (pRoomElement != null) {
                            this._removeRoomElement(tRE);
                        }
                    });
                    tPromise.catch((pError) => {
                        Log.error("Failed to delete selection", pError);
                    });
                }
            }
        }
        else {
            if (tRE.type === "SafetyZone") {
                if (pMainArticle != null) {
                    removeElement(this.mAddedMainArticles, pMainArticle);
                }
                if (tRE.type === "SafetyZone")
                    this._removeRoomElement(tRE);
            }
            tRE.dispose();
        }
    }
    removeWallSideDummy(pRE) {
        if (pRE == null)
            return;
        if (pRE.node !== undefined) {
            let tCommand = new DeleteElements(this.app, [pRE]);
            if (tCommand.isAvailable()) {
                let tPromise = this.app.commands.executeCommand(tCommand);
                tPromise.catch((pError) => {
                    Log.error("Failed to delete selection", pError);
                });
            }
        }
        else {
            pRE.dispose();
        }
    }
    mainArticlePropertiesChanged(pMainArticle, pRE) {
        if (this.mSafetyZoneGenerationLevel !== 0 || this.mUpdateOfmlArticles_active)
            return;
        this.mSafetyZoneGenerationLevel++;
        // cleanup RoomElements (safety zones)
        for (let t_RE of this.mRoomElements) {
            if (t_RE.type !== "SafetyZone")
                continue;
            let tMAofRE = t_RE.getMainElement();
            if (tMAofRE instanceof RoomElement) {
                this._removeRoomElement(t_RE);
                t_RE.dispose();
            }
        }
        let tRE = this.addSafetyZoneToMainArticle(pMainArticle, true);
        if (tRE != null) {
            let tbasketId = "";
            if (pMainArticle.basketId != null)
                tbasketId = pMainArticle.basketId;
            let tMaterialId = this.getMaterialIdForBasketId(tbasketId);
            this._addRoomElement(tRE);
            tRE.node.parent = pMainArticle.node;
            tRE.setMainElement(pMainArticle);
            tRE.collectSafetyZoneMeshes(pMainArticle, true);
            let tnewMaterial = tMaterialId === "" ? "color_-_#999999" : tMaterialId;
            tRE.setMaterialId(tnewMaterial);
            tRE.updateGeometry();
            pMainArticle.updateTransformation();
            this.cutOverlappingSZShapes();
            this.mSafetyZoneGenerationLevel--;
        }
    }
    _safetyzonesOFF() {
        this.mSafetyZonesVisible = false;
        let tSel = this.mApp.model.selection;
        this.mApp.model.setSelection([]);
        this.mApp.model.layers.setLayerVisible("room_safetyzones", false);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLine_Layer", false);
        this.mApp.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", false);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
        for (let tRE of this.mRoomElements) {
            if (tRE.type !== "SafetyZone")
                continue;
            tRE.notifyChange(ElementChangedFlags.BoundingBox);
        }
        this.setSZLayerVisibility(false);
        this.mApp.viewer.requestRenderFrame();
        this.mApp.model.setSelection(tSel);
    }
    _safetyzonesON() {
        this.mSafetyZonesVisible = true;
        let tSel = this.mApp.model.selection;
        this.mApp.model.setSelection([]);
        this.mApp.model.layers.setLayerVisible("room_safetyzones", true);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLine_Layer", true);
        this.mApp.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", true);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
        for (let tRE of this.mRoomElements) {
            if (tRE.type !== "SafetyZone")
                continue;
            tRE.notifyChange(ElementChangedFlags.BoundingBox);
        }
        this.cutOverlappingSZShapes();
        this.mApp.viewer.requestRenderFrame();
        //this.mSafetyZoneImportData = new Map();
        this.mApp.model.setSelection(tSel);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    get _wallColors() {
        return (this.mWallColors);
    }
    /** @internal */
    get _textColors() {
        let tRes = new Array();
        for (let tTC of this.mTextColors) {
            tRes.push(tTC.hex);
        }
        return (tRes);
    }
    /** @internal */
    get ExtLoadedMaterials() {
        return (this.mExtLoadedMaterials);
    }
    /** @internal */
    get _extMaterialCategories() {
        return (this.mExtLoadedMaterialCategory);
    }
    /** @internal */
    get _YesNo() {
        return (this.mYesNo);
    }
    /** @internal */
    get _openingMaterial() {
        return (this.mOpeningMaterial);
    }
    /** @internal */
    get _routeWidths() {
        return (this.mRouteWidths);
    }
    /** @internal */
    get gridFieldDim() {
        return (this.mgridFieldDimension);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    async _clear() {
        this.mVisibility = VisibilityMode.Show3d;
        this.mHiddenWallsStatus = false;
        this.mWallUpdateDepth = 0;
        this.mRouteUpdateDepth = 0;
        this.mWallOpacitiy = 1;
        this.monAfterLoad = this.mLoadingScene ? false : true;
        this.mfinalCutAfterLoad = false;
        this.mWallLoadedDepth = 0;
        // this.mBGSound?.dispose();
        if (this.mApp.rendering.shadowGenerators.length > 0) {
            for (let tSG of this.mApp.rendering.shadowGenerators) {
                let tStat = tSG.enabled;
                tSG.setEnabled(false);
                tSG.setEnabled(tStat);
            }
            this.mApp.viewer.requestRenderFrame();
        }
        for (let tRE of this.mRoomElements) {
            if (tRE.isDisposed !== true) {
                tRE.dispose();
            }
        }
        this.mRoomElements = new Array();
        for (let tWall of this.mWalls) {
            tWall.dispose();
        }
        this.mWalls = new Array();
        for (let tFloor of this.mFloors) {
            tFloor.dispose();
        }
        this.mFloors = new Array();
        this.mWallIdCounter = 0;
        this.mRouteIdCounter = 0;
        this.mREIdCounter = 0;
        this.mREGridIdCounter = 0;
        this.mRoutes = new Array();
        this.mMeasures = new Array();
        this.m2dLines = new Array();
        this.m2dPoints = new Array();
        this.mFloorsPolygons = new Map();
        if (this.mPanoDome != null) {
            this.mPanoDome.dispose();
            this.mPanoDome = null;
        }
        for (let tRE of this.mREShapes) {
            this.mApp.model.removeElement(tRE);
        }
        this.mREShapes = new Array();
        this.m2dShapesCount = 0;
        for (let tMesh of this.mOverlappingShapes) {
            this.mApp.model.removeElement(tMesh);
        }
        this.mOverlappingShapes = new Array();
        for (let tBuilding of this.mBuildings) {
            this.mApp.model.removeElement(tBuilding);
        }
        this.mBuildings = new Array();
        this.minitialBuildings = new Map();
        this.setSZLayerVisibility(false);
        this.mApp.model.layers.setLayerVisible("room_safetyzones", false);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLine_Layer", false);
        this.mApp.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", false);
        this.mApp.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
        this.mSZshapeVisibility = new Map();
        this.mHillMaterials.forEach((pVal, pKey) => {
            pVal.dispose();
        });
        this.mHillMaterials = new Map();
        this.createStandardColor();
        this.app.model.notifyModification();
        this.mApp.viewer.requestRenderFrame();
    }
    /** @internal */
    _addWall(pWall) {
        if (pWall == null)
            return;
        if (!contains(this.mWalls, pWall))
            this.mWalls.push(pWall);
    }
    /** @internal */
    _removeWall(pWall) {
        if (pWall == null)
            return (false);
        if (pWall.openings.length > 0) {
            let tSel = new Array();
            for (let tOpening of pWall.openings) {
                tSel.push(tOpening);
            }
            let tCommand = new DeleteElements(this.app, tSel);
            if (tCommand.isAvailable()) {
                let tPromise = this.app.commands.executeCommand(tCommand);
                tPromise.catch((pError) => {
                    Log.error("Failed to delete selection", pError);
                });
            }
        }
        if (pWall.additions.length > 0) {
            let tSel = new Array();
            for (let tAdditon of pWall.additions) {
                tSel.push(tAdditon);
            }
            let tCommand = new DeleteElements(this.app, tSel);
            if (tCommand.isAvailable()) {
                let tPromise = this.app.commands.executeCommand(tCommand);
                tPromise.catch((pError) => {
                    Log.error("Failed to delete selection", pError);
                });
            }
        }
        let twallRemoved = removeElement(this.mWalls, pWall);
        if (this.mWalls.length === 0)
            this.eventEmptyWalls.trigger(this);
        this.contentChanged();
        return (twallRemoved);
    }
    /** @internal */
    _addFloor(pFloor) {
        if (pFloor == null)
            return;
        if (!contains(this.mFloors, pFloor))
            this.mFloors.push(pFloor);
    }
    /** @internal */
    _addMeasure(pMeasure) {
        if (pMeasure == null)
            return;
        if (!contains(this.mMeasures, pMeasure))
            this.mMeasures.push(pMeasure);
    }
    /** @internal */
    _removeMeasure(pMeasure) {
        if (pMeasure == null)
            return (false);
        pMeasure.removeMeasure();
        return (removeElement(this.mMeasures, pMeasure));
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _addRoomElement(pRoomElement) {
        if (pRoomElement == null)
            return;
        if (!contains(this.mRoomElements, pRoomElement) &&
            (pRoomElement.type !== "DummySafetyZone" && pRoomElement.type !== "DummyElement")) {
            this.mRoomElements.push(pRoomElement);
        }
        if (pRoomElement.type === "DummyElement")
            return;
        let tType = pRoomElement.geometry.getType();
        if ((tType === "Spline" || tType === "Rectangle" || tType === "Line" || tType === "Circle" || tType === "SafetyZone" ||
            tType === "Road" || tType === "Hedge" || tType === "Sphere" || tType === "Arena") &&
            (!contains(this.mREShapes, pRoomElement))) {
            this.mREShapes.push(pRoomElement);
            if (tType === "Line" && pRoomElement.GridRE_REF != null) {
                pRoomElement.addDummy_REF();
                pRoomElement.GridRE_REF.setREforGrid_REF([pRoomElement]);
            }
        }
        if (tType === "Spline" || tType === "Rectangle" || tType === "Line" || tType === "Circle" || tType === "Arena") {
            this.m2dShapesCount++;
        }
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _removeRoomElement(pRoomElement, pDispose = false) {
        if (pRoomElement.type === "DummyElement")
            return (true);
        let tType = pRoomElement.geometry.getType();
        let tremoved = false;
        if (tType === "Spline" || tType === "Rectangle" || (tType === "Line") ||
            tType === "Circle" || tType === "SafetyZone" ||
            tType === "Road" || tType === "Hedge" || tType === "Sphere" || tType === "CameraPath" || tType === "Arena") {
            removeElement(this.mREShapes, pRoomElement);
            if (tType === "Sphere" && pRoomElement.Params.audioSphere) {
                pRoomElement.removeAudio();
            }
            if (tType === "CameraPath") {
                this.mcamPath_ResetView = false;
                this.eventCameraPathRemoved.trigger(this);
            }
            if (tType === "Line") {
                const tlineGEO = pRoomElement.geometry;
                if (tlineGEO != null) {
                    tlineGEO.hideCornerAngles();
                }
            }
        }
        if (tType === "Spline" || tType === "Rectangle" || (tType === "Line") ||
            tType === "Circle" || tType === "Arena") {
            this.m2dShapesCount--;
        }
        if (tType === "Building") {
            removeElement(this.mBuildings, pRoomElement);
        }
        if (tType === "Sphere" && this.mPanoDome != null) {
            this.mPanoDome.dispose();
            this.mPanoDome = null;
        }
        if (pRoomElement.type === "SafetyZone" && pRoomElement.RE_reference != null) {
            removeElement(this.mRoomElements, pRoomElement.RE_reference);
            this.app.model.removeElement(pRoomElement.RE_reference, true, true);
        }
        if (pRoomElement.type === "CustomBuilding" && pRoomElement.RE_reference != null) {
            removeElement(this.mRoomElements, pRoomElement.RE_reference);
            this.app.model.removeElement(pRoomElement.RE_reference, true, true);
        }
        if (pRoomElement.type === "Line" && pRoomElement.GridRE_REF != null) {
            if (pRoomElement.GridRE_REF.group2d != null) {
                pRoomElement.GridRE_REF.group2d.setEnabled(true);
            }
            pRoomElement.removeDummy_REF();
            pRoomElement.showInteractors(false);
            pRoomElement.GridRE_REF.removeREFromGrid_REF([pRoomElement]);
            this.mApp.model.setSelection([]);
        }
        if (pRoomElement.type === "Grid") {
            if (pRoomElement.REforGrid_REF != null) {
                while (pRoomElement.REforGrid_REF.length > 0) {
                    const tRE = pRoomElement.REforGrid_REF[0];
                    removeElement(this.mREShapes, tRE);
                    this.app.model.removeElement(tRE, true, true);
                    pRoomElement.removeREFromGrid_REF([tRE]);
                }
            }
            removeElement(this.mRoomElements, pRoomElement);
        }
        tremoved = removeElement(this.mRoomElements, pRoomElement);
        if (pDispose)
            this.app.model.removeElement(pRoomElement, true, true);
        return (tremoved);
    }
    /** @internal */
    _requestWallUpdate(pWall) {
        if (pWall == null)
            return;
        this.mPendingWallUpdates.add(pWall);
    }
    /** @internal */
    _getNextWallId() {
        if (this.mWallIdCounter >= Number.MAX_SAFE_INTEGER)
            this.mWallIdCounter = Number.MIN_SAFE_INTEGER;
        return (this.mWallIdCounter++);
    }
    /** @internal */
    _getNextREId() {
        if (this.mREIdCounter >= Number.MAX_SAFE_INTEGER)
            this.mREIdCounter = Number.MIN_SAFE_INTEGER;
        return (this.mREIdCounter++);
    }
    /** @internal */
    _getNextREGridId() {
        if (this.mREGridIdCounter >= Number.MAX_SAFE_INTEGER)
            this.mREGridIdCounter = Number.MIN_SAFE_INTEGER;
        return (this.mREGridIdCounter++);
    }
    /** @internal */
    _getNextArea2DId() {
        if (this.mArea2DIdCounter >= Number.MAX_SAFE_INTEGER)
            this.mArea2DIdCounter = Number.MIN_SAFE_INTEGER;
        return (this.mArea2DIdCounter++);
    }
    /** @internal */
    _addRoute(pRoute) {
        if (pRoute == null)
            return;
        if (!contains(this.mRoutes, pRoute))
            this.mRoutes.push(pRoute);
    }
    /** @internal */
    _removeRoute(pRoute) {
        if (pRoute == null)
            return (false);
        return (removeElement(this.mRoutes, pRoute));
    }
    /** @internal */
    _requestRouteUpdate(pRoute) {
        if (pRoute == null)
            return;
        this.mPendingRouteUpdates.add(pRoute);
    }
    /** @internal */
    _getNextRouteId() {
        if (this.mRouteIdCounter >= Number.MAX_SAFE_INTEGER)
            this.mRouteIdCounter = Number.MIN_SAFE_INTEGER;
        return (this.mRouteIdCounter++);
    }
    /** @internal */
    _notifyWallLoaded(pWall, pIOData) {
        if (pWall == null || this.mLoadedWalls == null)
            return;
        this.mLoadedWalls.set(pIOData.id, { wall: pWall, ioData: pIOData });
    }
    /** @internal */
    _notifyWallLoaded_deserialize(pContext, pIOData, pStart, pEnd) {
        if (this.mLoadedWalls_deserialize == null)
            return;
        let twallDeserializeData = {};
        twallDeserializeData.Context = pContext;
        twallDeserializeData.ioData = pIOData;
        twallDeserializeData.start = pStart;
        twallDeserializeData.end = pEnd;
        this.mLoadedWalls_deserialize.push(twallDeserializeData);
    }
    /** @internal */
    _getLoadedWallById(pId) {
        if (this.mLoadedWalls == null)
            return (null);
        let tEntry = this.mLoadedWalls.get(pId);
        if (tEntry != null)
            return (tEntry.wall);
        return (null);
    }
    /** @internal */
    _getLoadedRouteById(pId) {
        if (this.mLoadedRoutes == null)
            return (null);
        let tEntry = this.mLoadedRoutes.get(pId);
        if (tEntry != null)
            return (tEntry.route);
        return (null);
    }
    /** @internal */
    _notifyRouteLoaded(pRoute, pIOData) {
        if (pRoute == null || this.mLoadedRoutes == null)
            return;
        this.mLoadedRoutes.set(pIOData.id, { route: pRoute, ioData: pIOData });
    }
    /** @internal */
    _getWallColorMaterial(pHexColor) {
        if (isNullOrEmpty(pHexColor))
            return (this.wallDefaultMaterial);
        let tMat = this.mWallColorMaterialMap.get(pHexColor);
        if (tMat == null) {
            let tmatId = "color" + this.mMaterialSplitStr + pHexColor;
            let tcheckMat = this.mMatManager.getMaterial(tmatId);
            if (tcheckMat != null) {
                this.mWallColorMaterialMap.set(pHexColor, tcheckMat);
                return (tcheckMat);
            }
            tMat = new PbrMaterial(tmatId, this.mMatManager);
            tMat.albedoColor = Color3.FromHexString(pHexColor).toLinearSpace();
            tMat.metallic = 0.001;
            tMat.roughness = 0.999;
            tMat.backFaceCulling = false;
            tMat.freeze();
            this.mMatManager.addMaterial(tmatId, tMat);
            this.mWallColorMaterialMap.set(pHexColor, tMat);
        }
        return (tMat);
    }
    /** @internal */
    _getWallColorPropValue(pHexColor) {
        let tValue = this.mWallColorPropValueMap.get(pHexColor);
        if (tValue == null) {
            tValue = new PropertyValue(pHexColor);
            tValue.smallIcon = this.generateColorPreviewImage(pHexColor);
            tValue.largeIcon = this.generateColorPreviewImage(pHexColor);
            this.mWallColorPropValueMap.set(pHexColor, tValue);
        }
        return (tValue);
    }
    /** @internal */
    _getTextColorPropValue(pHexColor, pGetColor = false) {
        let tValue = this.mTextColorPropValueMap.get(pHexColor);
        if (tValue == null) {
            tValue = new PropertyValue(pHexColor);
            tValue.smallIcon = this.generateColorPreviewImage(pHexColor);
            if (!pGetColor) {
                this.mTextColorPropValueMap.set(pHexColor, tValue);
            }
            else {
                tValue.text = this.resources.propertyCustomColor;
            }
        }
        return (tValue);
    }
    /** @internal */
    _getArea2DMaterialPropValue(pIdx) {
        let tValue = this.mArea2DMaterialPropValueMap.get(pIdx.toString());
        if (tValue == null) {
            tValue = new PropertyValue(pIdx);
            tValue.smallIcon = this.mExtLoadedMaterialDefinitions[pIdx].smallIcon;
            tValue.largeIcon = this.mExtLoadedMaterialDefinitions[pIdx].smallIcon;
            this.mArea2DMaterialPropValueMap.set(pIdx.toString(), tValue);
        }
        return (tValue);
    }
    /** @internal */
    _getextMaterialCategoryPropValue(pKey) {
        let tValue = null;
        if (this.mextMaterialCategoryPropValueMap !== undefined) {
            tValue = this.mextMaterialCategoryPropValueMap.get(pKey);
        }
        if (tValue == null) {
            tValue = new PropertyValue(pKey);
            tValue.text = "";
            tValue.largeIcon = null;
            tValue.smallIcon = null;
            this.mArea2DMaterialPropValueMap.set("0", tValue);
        }
        return (tValue);
    }
    /** @internal */
    _getFirstextMaterialOfCategory(pCategory) {
        let tRes = "";
        if (pCategory === "color") {
            return ("#aaaaaa");
        }
        for (let tTxt of this.mExtLoadedMaterialDefinitions) {
            if (tTxt.category === pCategory) {
                tRes = tTxt.id;
                break;
            }
        }
        return (tRes);
    }
    /** @internal */
    _getFirstDefaultextMaterialOfCategory(pCategory) {
        let tRes = "";
        for (let tTxt of this.mExtLoadedMaterialDefinitions) {
            if (tTxt.category === pCategory) {
                if (tRes === "") {
                    tRes = tTxt.id;
                }
                if (tTxt.default) {
                    tRes = tTxt.id;
                    break;
                }
                break;
            }
        }
        if (tRes === "" && pCategory === "color")
            tRes = "#999999";
        return (tRes);
    }
    /** @internal */
    _getArea2DColorPropValue(pColor) {
        let tValue = this.mArea2DMaterialPropValueMap.get(pColor);
        if (tValue != null) {
            tValue.smallIcon = this.generateColorPreviewImage(pColor);
            tValue.largeIcon = this.generateColorPreviewImage(pColor);
            tValue.text = this.resources.propertyColor;
            this.mArea2DMaterialPropValueMap.set(pColor, tValue);
        }
        else {
            tValue = new PropertyValue(pColor);
            tValue.text = this.resources.propertyColor;
            tValue.smallIcon = this.generateColorPreviewImage(pColor);
            tValue.largeIcon = this.generateColorPreviewImage(pColor);
            this.mArea2DMaterialPropValueMap.set(pColor, tValue);
        }
        return (tValue);
    }
    /** @internal */
    _getextMaterialOfCategory(pCategory, pPropValueArray, pType) {
        let tMatIdx = -1;
        let tLang = this.getLocaleLanguage();
        for (let i = 0; i < this.mExtLoadedMaterialDefinitions.length; i++) {
            let textMat = this.mExtLoadedMaterialDefinitions[i];
            let tfilter_include = this.mExtLoadedMaterialDefinitions[i].filter_include.trim().toLocaleLowerCase();
            let tFilter_exclude = this.mExtLoadedMaterialDefinitions[i].filter_exclude.trim().toLocaleLowerCase();
            if (textMat.category === pCategory &&
                (tfilter_include === "" || tfilter_include.indexOf(pType) > -1) &&
                (tFilter_exclude === "" || tFilter_exclude.indexOf(pType) === -1)) {
                let tValue = new PropertyValue(pCategory + this.mMaterialSplitStr + textMat.id);
                tValue.smallIcon = textMat.smallIcon;
                tValue.largeIcon = textMat.smallIcon;
                tValue.text = this.getMaterialText(i, tLang);
                pPropValueArray.push(tValue);
            }
        }
    }
    /** @internal */
    _getextSoundOfCategory(pCategory, pPropValueArray, pType) {
        let tMatIdx = -1;
        let tLang = this.getLocaleLanguage();
        for (let i = 0; i < this.mExtLoadedSoundEffects.length; i++) {
            let textMat = this.mExtLoadedSoundEffects[i];
            let tfilter_include = this.mExtLoadedSoundEffects[i].filter_include.trim().toLocaleLowerCase();
            let tFilter_exclude = this.mExtLoadedSoundEffects[i].filter_exclude.trim().toLocaleLowerCase();
            if (textMat.category === pCategory &&
                (tfilter_include === "" || tfilter_include.indexOf(pType) > -1) &&
                (tFilter_exclude === "" || tFilter_exclude.indexOf(pType) === -1)) {
                let tValue = new PropertyValue(pCategory + this.mMaterialSplitStr + textMat.id);
                tValue.text = this.getMaterialText(i, tLang);
                pPropValueArray.push(tValue);
            }
        }
    }
    /** @internal */
    _getYesNoPropValue(pAnswerString) {
        let tValue = this.mYesNoPropValueMap.get(String(pAnswerString));
        if (tValue == null) {
            tValue = new PropertyValue(pAnswerString);
            this.mYesNoPropValueMap.set(String(pAnswerString), tValue);
        }
        else {
            if (!pAnswerString)
                tValue.text = this.resources.propertyValueNo;
            if (pAnswerString)
                tValue.text = this.resources.propertyValueYes;
        }
        return (tValue);
    }
    /** @internal */
    _getGridSize(pgridSizeStr) {
        for (let tDim of this.mgridFieldDimension) {
            const tdimStr = Util.convertVector2ToDimStr(tDim);
            if (tdimStr == pgridSizeStr) {
                return (tDim);
            }
        }
        return (null);
    }
    /** @internal */
    _getArenaShapePropValue(pCircle) {
        if (pCircle) {
            let tValueC = this.mArenaOvalCirclePropValueMap.get('circle');
            if (tValueC == null) {
                tValueC = new PropertyValue('circle', this.resources.propertyArenaCircle);
                this.mArenaOvalCirclePropValueMap.set('circle', tValueC);
            }
            return (tValueC);
        }
        let tValueO = this.mArenaOvalCirclePropValueMap.get('oval');
        if (tValueO == null) {
            tValueO = new PropertyValue('oval', this.resources.propertyArenaOval);
            this.mArenaOvalCirclePropValueMap.set('oval', tValueO);
        }
        return (tValueO);
    }
    /** @internal */
    _getOvalCirclePropValue() {
        let tRes = [];
        this.mArenaOvalCirclePropValueMap.forEach((pEntry) => {
            if (pEntry != null) {
                tRes.push(pEntry);
            }
        });
        return (tRes);
    }
    /** @internal */
    _getNumberListPropValue(pCount) {
        let tRes = [];
        for (let i = 1; i <= pCount; i++) {
            let tValue = new PropertyValue(i);
            tValue.text = i.toString();
            tValue.value = i;
            tRes.push(tValue);
        }
        return (tRes);
    }
    /** @internal */
    __getNumberofBool(pDouble) {
        let tValue = new PropertyValue(1, "1");
        if (!pDouble) {
            return (tValue);
        }
        tValue.value = 2;
        tValue.text = "2";
        return (tValue);
    }
    /** @internal */
    _getDoorTypesPropValue(pDoorType) {
        let tValue = this.mDoorTypesValueMap.get(String(pDoorType));
        if (tValue == null) {
            tValue = new PropertyValue(pDoorType);
            tValue.value = pDoorType;
            tValue.text = pDoorType;
            this.mYesNoPropValueMap.set(String(pDoorType), tValue);
        }
        return (tValue);
    }
    /** @internal */
    _getMeasureTextChoicePropValue(pAnswerString) {
        let tValue = this.mYesNoPropValueMap.get(String(pAnswerString));
        if (tValue == null) {
            tValue = new PropertyValue(pAnswerString);
            this.mYesNoPropValueMap.set(String(pAnswerString), tValue);
        }
        if (!pAnswerString)
            tValue.text = this.resources.propertyCurrentLength;
        if (pAnswerString)
            tValue.text = this.resources.propertyAlternativeText;
        return (tValue);
    }
    /** @internal */
    _getRouteColorMaterial(pHexColor) {
        if (isNullOrEmpty(pHexColor))
            return (this.routeDefaultMaterial);
        let tMat = this.mRouteColorMaterialMap.get(pHexColor);
        if (tMat == null) {
            tMat = new PbrMaterial("route color " + pHexColor, this.mMatManager);
            tMat.albedoColor = Color3.FromHexString(pHexColor).toLinearSpace();
            tMat.metallic = 0.001;
            tMat.roughness = 0.999;
            tMat.freeze();
            this.mRouteColorMaterialMap.set(pHexColor, tMat);
        }
        return (tMat);
    }
    /** @internal */
    _getRouteWidthsPropValue(pWidth) {
        let tValue = this.mRouteWidthsPropValueMap.get(pWidth);
        if (tValue == null) {
            tValue = new PropertyValue(pWidth);
            tValue.text = pWidth;
            this.mRouteWidthsPropValueMap.set(pWidth, tValue);
        }
        return (tValue);
    }
    /** @internal */
    _transformationStarted() {
        return (this.mTransformationDepth > 0);
    }
    /** @internal */
    _addTransformedElement(pElement) {
        this.mTransformedElements.add(pElement);
    }
    /** @internal */
    _removeTransformedElement(pElement) {
        this.mTransformedElements.delete(pElement);
    }
    /** @internal */
    _wasElementTransformed(pElement) {
        return (this.mTransformedElements.has(pElement));
    }
    concatElements() {
        let tElements = this.app.model.elements;
        let tMainA = tElements[0];
        let tRoomE = tElements[1];
        tRoomE.node.parent = tMainA.node;
        tRoomE.getMainElement = () => tMainA;
    }
    _findWallsConnection() {
        this.removeFloors();
        for (let tWall of this.mWalls) {
            tWall._findNewConnection(0);
            tWall._findNewConnection(1);
        }
        this.findRoom();
        this.app.viewer.requestRenderFrame();
    }
    _findWallsConnection_afterLoad() {
        this.mWallLoadedDepth++;
        if (this.mWallLoadedDepth < this.mLoadedWalls_deserialize.length)
            return;
        this.mWallLoadedDepth = 0;
        this.removeFloors();
        for (let tWall of this.mWalls) {
            tWall._findNewConnection(0);
            tWall._findNewConnection(1);
        }
        this.findRoom();
        this.app.viewer.requestRenderFrame();
    }
    _findRoutesConnection() {
        for (let tRoute of this.mRoutes) {
            tRoute._findNewConnection(0);
            tRoute._findNewConnection(1);
        }
        this.app.viewer.requestRenderFrame();
    }
    _mediaQueryMatch(query) {
        return window.matchMedia(query).matches;
    }
    _isTouchDevice() {
        const prefixes = " -webkit- -moz- -o- -ms- ".split(" ");
        if (("ontouchstart" in window) ||
            // tslint:disable-next-line:no-any
            (window.DocumentTouch && document instanceof window.DocumentTouch) ||
            navigator.maxTouchPoints > 0) {
            return true;
        }
        const query = ["(", prefixes.join("touch-enabled),("), "heartz", ")"].join("");
        return this._mediaQueryMatch(query);
    }
    detectTouchDevice() {
        let tresult = false;
        if ("ontouchstart" in window || navigator.maxTouchPoints) {
            tresult = true;
        }
        else {
            tresult = false;
        }
        /*
        if (window.PointerEvent && ("maxTouchPoints" in navigator)) {
            // if Pointer Events are supported, just check maxTouchPoints
            if (navigator.maxTouchPoints > 0) {
                tresult = true;
            }
        } else {
            // no Pointer Events...
            if (window.matchMedia && window.matchMedia("(any-pointer:coarse)").matches) {
                // check for any-pointer:coarse which mostly means touchscreen
                tresult = true;
            } else if ("ontouchstart" in window) {
                // last resort - check for exposed touch events API / event handler
                tresult = true;
            }
        }
        */
        this.mTouchDevice = tresult;
    }
    sortNumber(a, b) {
        return a - b;
    }
    createSkyMaterials() {
        let tMat1 = new PbrMaterial("skyBox", this.mMatManager);
        tMat1.albedoTexture = new Texture(this.mDataPath + "textures/pano1.jpg" + "?" + this.mRandomString, this.mScene);
        tMat1.albedoTexture.vScale = -1;
        tMat1.unlit = true;
        tMat1.backFaceCulling = true;
        this.mSkyBoxMaterials.push(tMat1);
        // let tMat2 = new PbrMaterial("skyBox", this.mMatManager);
        // tMat2.albedoTexture = new Texture(this.mDataPath + "textures/pano1.jpg" + "?" + this.mRandomString, this.mScene);
        // //(tMat1.albedoTexture as Texture).vScale = -0.6;
        // tMat2.unlit = true;
        // tMat2.backFaceCulling = true;
        // this.mSkyBoxMaterials.push(tMat2);
    }
    reOrderSZIndex() {
        let tRes = false;
        // set order index of new created safety zones
        for (let tRE of this.mRoomElements) {
            if (tRE.type !== "SafetyZone" || tRE.BasketID === "")
                continue;
            tRes = true;
            let tBasketId = tRE.BasketID;
            let torderIndex = this.mSafetyZoneImportData.get(tBasketId);
            if (torderIndex !== undefined) {
                // let tmatGroup = torderIndex.MaterialId.split(this.mMaterialSplitStr);
                // if (tmatGroup != undefined) {
                //     let tmatValue = tmatGroup.pop();
                //     let tmatCategory = tmatGroup.pop();
                //     if (tmatCategory != undefined && tmatValue != undefined) {
                //         tRE.setMaterialCategory(tmatCategory, tmatValue, true, tRE.materialSet);
                //     }
                // }
                tRE.setRE_orderIndex(torderIndex.OrderIndex);
                if (!torderIndex.visible) {
                    tRE.set_SafetyZone_Visibility(false);
                }
            }
        }
        return (tRes);
    }
    createSafetyZonesAfterLoad() {
        let tME = this.mApp.model.elements;
        for (let tArticle of tME) {
            if (tArticle instanceof MainArticleElement) {
                if (contains(this.mAddedMainArticles, tArticle))
                    return;
                if (tArticle.basketId != null) {
                    this.mAddedMainArticles.push(tArticle);
                    //let tRE = this.generateSafetyZones(tMainArticle.basketId);
                    let tRE = this.addSafetyZoneToMainArticle(tArticle);
                    if (tRE != null) {
                        tRE.collectSafetyZoneMeshes(tArticle, false);
                        let tmatID = "color" + this.mMaterialSplitStr + "#999999";
                        let tRElabel;
                        let tSZ_data = this.mSafetyZoneImportData.get(tArticle.basketId);
                        if (tSZ_data !== undefined) {
                            tmatID = tSZ_data.MaterialId;
                            tRElabel = tSZ_data.label;
                        }
                        tRE.setRELabel(tRElabel);
                        tRE.setMaterialId(tmatID);
                        tRE.setBasketId(tArticle.basketId);
                        tRE.updateGeometry();
                        tArticle.updateTransformation();
                        tArticle.notifyChange(ElementChangedFlags.BoundingBox);
                    }
                }
            }
        }
    }
    prepareElementsForGLBExport() {
        this.cutOverlappingShapesCAG();
        for (let tRE of this.mREShapes) {
            if (tRE.type === "SafetyZone" || tRE.type === "Road") {
                if (tRE.group2d != null) {
                    tRE.group2d.position.y = 0;
                }
                if (tRE.group3d != null) {
                    tRE.group3d.position.y = 0;
                }
            }
            tRE.updateTransformation();
            if (tRE.Params.terrainType !== "Flat") {
                tRE.updateGeometry(false, false, true);
            }
            else {
                tRE.updateGeometry(false, true, true);
            }
        }
        this.app.viewer.requestRenderFrame();
    }
    resetGeometries() {
        for (let tRE of this.mREShapes) {
            if (tRE.type === "SafetyZone" || tRE.type === "Road") {
                if (tRE.group2d != null) {
                    tRE.group2d.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
                }
                if (tRE.group3d != null) {
                    tRE.group3d.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
                }
            }
            tRE.updateTransformation();
            tRE.updateGeometry(false, false);
        }
        this.cutOverlappingShapes();
    }
    prepareFenceObjects() {
        let tfences = new Array();
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "Fence")
                tfences.push(tRE);
        }
        if (tfences.length === 0)
            return;
        for (let tFence of tfences) {
            let tFieldMeshes = tFence.getFence_FieldMeshes();
            let tPostMeshes = tFence.getFence_PostMeshes();
            for (let tFieldMesh of tFieldMeshes) {
                let tDummyFence = new RoomElement(this, "DummyElement", null, true, 'fenceField');
                tDummyFence.setMaterialId(tFence.materialId);
                this.app.model.addElement(tDummyFence);
                tDummyFence.setRE_reference(tFence);
                tDummyFence.setRE_DummyMesh(tFieldMesh);
            }
            for (let tPostMesh of tPostMeshes) {
                let tDummyPost = new RoomElement(this, "DummyElement", null, true, 'fencePost');
                tDummyPost.setMaterialId(tFence.Params.FencePostMATCategory +
                    this.materialSplitStr + tFence.Params.FencePostMATValue);
                this.app.model.addElement(tDummyPost);
                tDummyPost.setRE_reference(tFence);
                tDummyPost.setRE_DummyMesh(tPostMesh);
            }
        }
    }
    cleanFenceObjects() {
        let tDummyfences = new Array();
        for (let tElement of this.mApp.model.elements) {
            if (tElement instanceof RoomElement) {
                let tRE = tElement;
                if (tRE.type === "DummyElement")
                    tDummyfences.push(tRE);
            }
        }
        if (tDummyfences.length === 0)
            return;
        for (let tFence of tDummyfences) {
            this.app.model.removeElement(tFence, true, true);
        }
    }
    getLocaleLanguage() {
        let tLang = "en";
        if (this.mApp.i18n.locale != null) {
            let ttmpLang = this.mApp.i18n.locale;
            if (ttmpLang.indexOf("-") !== -1) {
                let tlangGrp = ttmpLang.split("-");
                if (tlangGrp.length >= 1)
                    tLang = tlangGrp[0];
            }
            else if (ttmpLang.trim() !== "")
                tLang = ttmpLang.trim();
        }
        return (tLang);
    }
    getREforBasketId(pBasketId) {
        let tRes = null;
        for (let tRE of this.mRoomElements) {
            let tMainArticle = tRE.getMainElement();
            if (tMainArticle instanceof ArticleElement && (tMainArticle != null)) {
                let tbasketID = tMainArticle.basketId;
                if (tbasketID != null && (tbasketID === pBasketId)) {
                    tRes = tRE;
                    break;
                }
            }
        }
        return (tRes);
    }
    getREofMainArticle(pMainArticle) {
        let tRes = null;
        if (pMainArticle === null)
            return (tRes);
        for (let tRE of this.mRoomElements) {
            if (tRE.type !== "SafetyZone")
                continue;
            if (tRE.getMainElement() === pMainArticle) {
                tRes = tRE;
                break;
            }
        }
        return (tRes);
    }
    getMaterialIdForBasketId(pBasketId) {
        let tRes = "color" + this.mMaterialSplitStr + "#999999";
        let tEntry = this.mSafetyZoneData.get(pBasketId);
        if (tEntry !== undefined)
            tRes = tEntry.MaterialId;
        return (tRes);
    }
    _removeSZ(pRE) {
        if (pRE === null)
            return;
        if (pRE.node !== undefined) {
            if (pRE.type === "SafetyZone") {
                this._removeRoomElement(pRE);
            }
        }
        else if (pRE.type === "SafetyZone" && pRE.type === "SafetyZone") {
            this._removeRoomElement(pRE);
        }
        pRE.dispose();
    }
    regenerateSZ() {
        let tRElist = new Array();
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "SafetyZone") {
                let tMainElement = tRE.getMainElement();
                if (tMainElement instanceof MainArticleElement) {
                    let tbasketId = "";
                    if (tMainElement.basketId != null)
                        tbasketId = tMainElement.basketId;
                    let tMaterialId = this.getMaterialIdForBasketId(tbasketId);
                    tRE.collectSafetyZoneMeshes(tMainElement, true);
                    let tnewMaterial = tMaterialId === "" ? "color" + this.mMaterialSplitStr + "#999999" : tMaterialId;
                    tRE.setMaterialId(tnewMaterial);
                    tRE.updateGeometry();
                    tMainElement.updateTransformation();
                    this.cutOverlappingSZShapes();
                }
            }
        }
        this.mApp.viewer.requestRenderFrame();
    }
    reOrderShapes() {
        let tIdx = 0;
        while (tIdx < this.mRoomElements.length) {
            let tRE = this.mRoomElements[tIdx];
            if (tRE.type === "SafetyZone") {
                removeElement(this.mRoomElements, tRE);
            }
            else {
                tIdx++;
            }
        }
        for (let tRE of this.mREShapes) {
            if (tRE.type === "SafetyZone") {
                this.mRoomElements.push(tRE);
            }
        }
    }
    async _insertBackgroundImage(pURL, pWith, pHeight, pPosX, pPosZ) {
        let tpicMatRes = await this.insertPictureAsMaterial(pURL);
        if (tpicMatRes == null) {
            return (Promise.resolve(null));
        }
        let tResult = tpicMatRes;
        let tRoomElement = new RoomElement(this, "BackgroundImage");
        tRoomElement.set_MaterialSet(tResult);
        let t_Promise = this.checkMaterial(tRoomElement.materialSet).then((pResult1) => {
            if (this.mView != null && this.mView.cameraControl.cameraMode === 7) {
                tRoomElement.node.addRotation(-Math.PI / 2, 0, 0);
            }
            tRoomElement.setImageSize(pWith, pHeight, false);
            tRoomElement.updateGeometry();
            if (this.view != null) {
                let tOptions = {};
                tOptions.placingAreaMin = new Vector2(-200, -200);
                tOptions.placingAreaMax = new Vector2(200, 200);
                if (pPosX === 0 && pPosZ === 0) {
                    placeElementsInViewCenter([tRoomElement], this.view, this.mApp.model, tOptions);
                }
                else {
                    placeElementsAtFreePosition([tRoomElement], new Vector2(pPosX, pPosZ), this.mApp.model);
                }
            }
            let tCmd = new InsertElements(this.mApp, [tRoomElement]);
            let tPromise = this.mApp.commands.executeCommand(tCmd).then(() => {
                this.mApp.model.setSelection([tRoomElement]);
                return (tRoomElement);
            });
            return (tRoomElement);
        });
        return (Promise.resolve(t_Promise));
    }
    isTextureAlreadyAvailable(pLoadedTexture) {
        let tRes = false;
        for (let tTxtData of this.mExtLoadedMaterialDefinitions) {
            if (tTxtData != null) {
                let tTxtID = tTxtData.category + "__" + tTxtData.id;
                if (tTxtID === pLoadedTexture.category + "__" + pLoadedTexture.id) {
                    tRes = true;
                    break;
                }
            }
        }
        return (tRes);
    }
    isSoundEffectAlreadyAvailable(pLoadedSoundEffect) {
        let tRes = false;
        for (let tSoundData of this.mExtLoadedSoundEffects) {
            if (tSoundData != null) {
                const tnewSet = pLoadedSoundEffect.category + RoomManager.CATVALUE_SPLITTER + pLoadedSoundEffect.id;
                const texSet = tSoundData.category + RoomManager.CATVALUE_SPLITTER + tSoundData.id;
                if (tnewSet === texSet) {
                    tRes = true;
                    break;
                }
            }
        }
        return (tRes);
    }
    createDefaultMaterials() {
        let tMat_DarkGrey = this.createColorMaterial("#999999", true);
        if (tMat_DarkGrey != null) {
            this.mWall2dMaterialDarkGrey = tMat_DarkGrey;
        }
        let tMat_Grey = this.createColorMaterial("#cccccc", true);
        if (tMat_Grey != null) {
            this.mWall2dMaterialGrey = tMat_Grey;
        }
        let tMat_Selected = this.createColorMaterial(this.mApp.theme.mainColorBright, true);
        if (tMat_Selected != null) {
            this.mWall2dMaterialSelected = tMat_Selected;
        }
        let tMat_Route2d = this.createColorMaterialFromInts(140, 177, 221, true);
        if (tMat_Route2d != null) {
            this.mRoute2dMaterial = tMat_Route2d;
        }
        this.mMat_white = new StandardMaterial("white", this.mScene);
        this.mMat_white.ambientColor = Color3.White();
        this.mMat_white.diffuseColor = Color3.White();
        this.mMat_white.emissiveColor = Color3.White();
    }
    createThemeColor() {
        if (this.mMat_theme_bright == null) {
            this.mMat_theme_bright = new StandardMaterial(this.mApp.theme.mainColorBright, this.mScene);
            this.mMat_theme_bright.roughness = 1;
            this.mMat_theme_bright.emissiveColor = Color3.FromHexString(this.mApp.theme.mainColorBright);
            this.mMat_theme_bright.diffuseColor = Color3.FromHexString(this.mApp.theme.mainColorBright);
            this.mMat_theme_bright.specularColor = Color3.FromHexString('#000000');
        }
    }
    createColorMaterial(pColor, pDisableLighting = false) {
        let tunlit = pDisableLighting ? "u" : "";
        let tMatId = "color" + this.materialSplitStr + pColor + tunlit;
        if (!this.mMatManager.hasMaterial(tMatId)) {
            // create new color material
            let tnewMaterial = new PbrMaterial(tMatId, this.mMatManager);
            tnewMaterial.albedoColor = Color3.FromHexString(pColor).toLinearSpace();
            tnewMaterial.backFaceCulling = false;
            tnewMaterial.metallic = 0.001;
            tnewMaterial.roughness = 0.999;
            if (pDisableLighting)
                tnewMaterial.unlit = true;
            tnewMaterial.freeze();
            if (tnewMaterial != null && (!this.MatManager.hasMaterial(tMatId))) {
                this.mMatManager.addMaterial(tMatId, tnewMaterial);
            }
        }
        return (this.MatManager.getMaterial(tMatId));
    }
    createStandardColor() {
        let tMatId = "color" + this.mMaterialSplitStr + "#999999";
        let tMatNew = new PbrMaterial(tMatId, this.MatManager);
        tMatNew.albedoColor = Color3.FromHexString("#999999").toLinearSpace();
        tMatNew.metallic = 0;
        tMatNew.roughness = 1;
        tMatNew.backFaceCulling = false;
        tMatNew.freeze();
        this.mExtMaterialsMap.set(tMatId, tMatNew);
        let tMat = this.mMatManager.getMaterial(tMatId);
        if (tMat == null)
            this.mMatManager.addMaterial(tMatId, tMatNew);
    }
    createColorMaterialFromInts(R, G, B, pDisableLighting = false) {
        let tMatId = "color" + this.materialSplitStr + R.toString() + G.toString() + B.toString();
        if (!this.mMatManager.hasMaterial(tMatId)) {
            // create new color material
            let tnewMaterial = new PbrMaterial(tMatId, this.mMatManager);
            tnewMaterial.albedoColor = Color3.FromInts(R, G, B).toLinearSpace();
            tnewMaterial.backFaceCulling = false;
            tnewMaterial.metallic = 0.001;
            tnewMaterial.roughness = 0.999;
            if (pDisableLighting)
                tnewMaterial.unlit = true;
            tnewMaterial.freeze();
            if (tnewMaterial != null && (!this.MatManager.hasMaterial(tMatId))) {
                this.mMatManager.addMaterial(tMatId, tnewMaterial);
            }
        }
        return (this.MatManager.getMaterial(tMatId));
    }
    isTextureCategoryAlreadyAvailable(pLoadedTextureCategory) {
        let tRes = false;
        for (let tTxtCats of this.mExtLoadedMaterialCategory) {
            if (tTxtCats != null) {
                let tTxtCat = tTxtCats.id;
                if (tTxtCat === pLoadedTextureCategory.id) {
                    tRes = true;
                    break;
                }
            }
        }
        return (tRes);
    }
    isSoundCategoryAlreadyAvailable(pLoadedSoundCategory) {
        let tRes = false;
        for (let tSoundCats of this.mExtLoadedSoundCategory) {
            if (tSoundCats != null) {
                let tSoundCat = tSoundCats.id;
                if (tSoundCat === pLoadedSoundCategory.id) {
                    tRes = true;
                    break;
                }
            }
        }
        return (tRes);
    }
    async _loadJSON(pURL) {
        try {
            return await ajax("GET", pURL + Util.URLrandomAddOn());
        }
        catch (e) {
            console.log('No JSON was found!');
        }
        finally {
            //...
        }
        return Promise.resolve(null);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async loadMaterialDefinitions() {
        let tProjectId = Util.HashString(this.mProjectId.toLocaleLowerCase().trim());
        let tUrl = "";
        if (this.mMaterialJsonURL != null && this.mMaterialJsonURL.trim() !== "") {
            tUrl = this.mMaterialJsonURL;
        }
        else {
            tUrl = this.mDataPath + "extTextures/textures_" + tProjectId + ".json";
        }
        //let tJsonData: any = await ajax("GET", tUrl + Util.URLrandomAddOn());
        let tJsonData = await this._loadJSON(tUrl);
        // check on "EGR" json
        if (tJsonData == null) {
            let ttmpProjectId = Util.HashString("egr");
            tJsonData = await this._loadJSON(this.mDataPath + "extTextures/textures_" + ttmpProjectId + ".json");
        }
        if (tJsonData != null) {
            let tJSstr = JSON.stringify(tJsonData);
            let tJSobj = JSON.parse(tJSstr);
            if (tJSobj.hasOwnProperty("category")) {
                for (let tkeyCategory of tJSobj["category"]) {
                    if (tkeyCategory.hasOwnProperty("id") &&
                        tkeyCategory.hasOwnProperty("translation")) {
                        let tTxtCategory = {};
                        tTxtCategory.id = tkeyCategory["id"];
                        if (tkeyCategory.hasOwnProperty("smallIcon")) {
                            tTxtCategory.smallIcon = tkeyCategory["smallIcon"];
                        }
                        else {
                            tTxtCategory.smallIcon = "";
                        }
                        if (tkeyCategory.hasOwnProperty("filter_include")) {
                            tTxtCategory.filter_include = tkeyCategory["filter_include"];
                        }
                        else {
                            tTxtCategory.filter_include = "";
                        }
                        if (tkeyCategory.hasOwnProperty("filter_exclude")) {
                            tTxtCategory.filter_exclude = tkeyCategory["filter_exclude"];
                        }
                        else {
                            tTxtCategory.filter_exclude = "";
                        }
                        tTxtCategory.translation = new Array();
                        for (let tTrans of tkeyCategory["translation"]) {
                            if (tTrans.hasOwnProperty("lang") &&
                                tTrans.hasOwnProperty("text")) {
                                let tarrTrans = {};
                                tarrTrans.lang = tTrans["lang"];
                                tarrTrans.text = tTrans["text"];
                                tTxtCategory.translation.push(tarrTrans);
                            }
                        }
                        let tTXTcatexists = this.isTextureCategoryAlreadyAvailable(tTxtCategory);
                        if (!tTXTcatexists)
                            this.mExtLoadedMaterialCategory.push(tTxtCategory);
                    }
                }
            }
            if (tJSobj.hasOwnProperty("textures")) {
                for (let tkeyTextures of tJSobj["textures"]) {
                    if (tkeyTextures.hasOwnProperty("category") &&
                        tkeyTextures.hasOwnProperty("id") &&
                        tkeyTextures.hasOwnProperty("translation")) {
                        let tTxtData = {};
                        tTxtData.category = tkeyTextures["category"];
                        tTxtData.id = tkeyTextures["id"];
                        if (tkeyTextures.hasOwnProperty("matz")) {
                            if (tkeyTextures["matz"].indexOf("http") < 0)
                                tTxtData.matzFile = this.mDataPath + "extTextures/" + tkeyTextures["matz"];
                            else
                                tTxtData.matzFile = tkeyTextures["matz"];
                        }
                        else
                            tTxtData.matzFile = "";
                        if (tkeyTextures.hasOwnProperty("filter_include")) {
                            tTxtData.filter_include = tkeyTextures["filter_include"];
                        }
                        else {
                            tTxtData.filter_include = "";
                        }
                        if (tkeyTextures.hasOwnProperty("filter_exclude")) {
                            tTxtData.filter_exclude = tkeyTextures["filter_exclude"];
                        }
                        else {
                            tTxtData.filter_exclude = "";
                        }
                        if (tkeyTextures.hasOwnProperty("albedocolor"))
                            tTxtData.albedocolor = tkeyTextures["albedocolor"];
                        else
                            tTxtData.albedocolor = "";
                        if (tkeyTextures.hasOwnProperty("texture")) {
                            if (tkeyTextures["texture"].indexOf("http") < 0) {
                                tTxtData.textureMap = this.mDataPath + "extTextures/" + tkeyTextures["texture"];
                            }
                            else
                                tTxtData.textureMap = "";
                        }
                        else
                            tTxtData.textureMap = "";
                        if (tkeyTextures.hasOwnProperty("roughnessMap")) {
                            if (tkeyTextures["roughnessMap"].indexOf("http") < 0) {
                                tTxtData.roughnessMap = this.mDataPath + "extTextures/" + tkeyTextures["roughnessMap"];
                            }
                            else
                                tTxtData.roughnessMap = "";
                        }
                        else
                            tTxtData.roughnessMap = "";
                        if (tkeyTextures.hasOwnProperty("roughnessMap"))
                            tTxtData.roughnessMap = this.mDataPath + "extTextures/" + tkeyTextures["texture"];
                        else
                            tTxtData.roughnessMap = "";
                        if (tkeyTextures.hasOwnProperty("smallIcon")) {
                            if (tkeyTextures["smallIcon"].indexOf("http") < 0)
                                tTxtData.smallIcon = this.mDataPath + "extTextures/" + tkeyTextures["smallIcon"];
                            else
                                tTxtData.smallIcon = tkeyTextures["smallIcon"];
                        }
                        else if (tTxtData.albedocolor !== "") {
                            tTxtData.smallIcon = this.generateColorPreviewImage(tTxtData.albedocolor);
                        }
                        else {
                            tTxtData.smallIcon = "";
                        }
                        if (tkeyTextures.hasOwnProperty("texture_uScale"))
                            tTxtData.texture_uScale = tkeyTextures["texture_uScale"];
                        else
                            tTxtData.texture_uScale = -1;
                        if (tkeyTextures.hasOwnProperty("texture_vScale"))
                            tTxtData.texture_vScale = tkeyTextures["texture_vScale"];
                        else
                            tTxtData.texture_vScale = -1;
                        if (tkeyTextures.hasOwnProperty("roughness"))
                            tTxtData.roughness = tkeyTextures["roughness"];
                        else
                            tTxtData.roughness = 0.999;
                        if (tkeyTextures.hasOwnProperty("metallic"))
                            tTxtData.metallic = tkeyTextures["metallic"];
                        else
                            tTxtData.metallic = 0.001;
                        if (tkeyTextures.hasOwnProperty("bump"))
                            tTxtData.bumpMap = this.mDataPath + "extTextures/" + tkeyTextures["bump"];
                        else
                            tTxtData.bumpMap = "";
                        if (tTxtData.bumpMap !== "") {
                            if (tkeyTextures.hasOwnProperty("bump_uScale"))
                                tTxtData.bump_uScale = tkeyTextures["bump_uScale"];
                            else
                                tTxtData.bump_uScale = -1;
                            if (tkeyTextures.hasOwnProperty("bump_vScale"))
                                tTxtData.bump_vScale = tkeyTextures["bump_vScale"];
                            else
                                tTxtData.bump_vScale = -1;
                        }
                        else {
                            tTxtData.bump_uScale = 0;
                            tTxtData.bump_vScale = 0;
                        }
                        if (tkeyTextures.hasOwnProperty("specular"))
                            tTxtData.specular = this.mDataPath + "extTextures/" + tkeyTextures["specular"];
                        else
                            tTxtData.specular = "";
                        if (tTxtData.specular !== "") {
                            if (tkeyTextures.hasOwnProperty("specular_uScale"))
                                tTxtData.specular_uScale = tkeyTextures["specular_uScale"];
                            else
                                tTxtData.specular_uScale = -1;
                            if (tkeyTextures.hasOwnProperty("specular_vScale"))
                                tTxtData.specular_vScale = tkeyTextures["specular_vScale"];
                            else
                                tTxtData.specular_vScale = -1;
                        }
                        else {
                            tTxtData.specular_uScale = 0;
                            tTxtData.specular_vScale = 0;
                        }
                        if (tkeyTextures.hasOwnProperty("specularcolor"))
                            tTxtData.specular_color = tkeyTextures["specularcolor"];
                        else
                            tTxtData.specular_color = "";
                        if (tkeyTextures.hasOwnProperty("specular_power"))
                            tTxtData.specular_power = tkeyTextures["specular_power"];
                        else
                            tTxtData.specular_power = -1;
                        if (tkeyTextures.hasOwnProperty("reflectivity"))
                            tTxtData.reflectivity = tkeyTextures["reflectivity"];
                        else
                            tTxtData.reflectivity = -1;
                        tTxtData.translation = new Array();
                        for (let tTrans of tkeyTextures["translation"]) {
                            if (tTrans.hasOwnProperty("lang") &&
                                tTrans.hasOwnProperty("text")) {
                                let tarrTrans = {};
                                tarrTrans.lang = tTrans["lang"];
                                tarrTrans.text = tTrans["text"];
                                tTxtData.translation.push(tarrTrans);
                            }
                        }
                        if (tTxtData.matzFile === "" && tTxtData.albedocolor !== "") {
                            this.addWallColorPropValues(tTxtData);
                        }
                        else {
                            tTxtData.materialValue = tTxtData.category + this.mMaterialSplitStr + tTxtData.id;
                            let tTXTexists = this.isTextureAlreadyAvailable(tTxtData);
                            if (!tTXTexists)
                                this.mExtLoadedMaterialDefinitions.push(tTxtData);
                        }
                    }
                }
            }
        }
        return;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async loadSoundFiles() {
        let tUrl = "";
        if (this.mSoundsJsonURL != null && this.mSoundsJsonURL.trim() !== "") {
            tUrl = this.mSoundsJsonURL;
        }
        else {
            tUrl = this.mDataPath + "soundeffects/soundeffects.json";
        }
        //let tJsonData: any = await ajax("GET", tUrl + Util.URLrandomAddOn());
        let tJsonData = await this._loadJSON(tUrl);
        if (tJsonData == null) {
            return (Promise.resolve(false));
        }
        let tJSstr = JSON.stringify(tJsonData);
        let tJSobj = JSON.parse(tJSstr);
        if (tJSobj.hasOwnProperty("category")) {
            for (let tkeyCategory of tJSobj["category"]) {
                if (tkeyCategory.hasOwnProperty("id") &&
                    tkeyCategory.hasOwnProperty("translation")) {
                    let tSndCategory = {};
                    tSndCategory.id = tkeyCategory["id"];
                    if (tkeyCategory.hasOwnProperty("filter_include")) {
                        tSndCategory.filter_include = tkeyCategory["filter_include"];
                    }
                    else {
                        tSndCategory.filter_include = "";
                    }
                    if (tkeyCategory.hasOwnProperty("filter_exclude")) {
                        tSndCategory.filter_exclude = tkeyCategory["filter_exclude"];
                    }
                    else {
                        tSndCategory.filter_exclude = "";
                    }
                    tSndCategory.translation = new Array();
                    for (let tTrans of tkeyCategory["translation"]) {
                        if (tTrans.hasOwnProperty("lang") &&
                            tTrans.hasOwnProperty("text")) {
                            let tarrTrans = {};
                            tarrTrans.lang = tTrans["lang"];
                            tarrTrans.text = tTrans["text"];
                            tSndCategory.translation.push(tarrTrans);
                        }
                    }
                    let tSndCatexists = this.isSoundCategoryAlreadyAvailable(tSndCategory);
                    if (!tSndCatexists)
                        this.mExtLoadedSoundCategory.push(tSndCategory);
                }
            }
        }
        if (tJSobj.hasOwnProperty("soundeffects")) {
            for (let tkeySoundEffects of tJSobj["soundeffects"]) {
                if (tkeySoundEffects.hasOwnProperty("category") &&
                    tkeySoundEffects.hasOwnProperty("id") &&
                    tkeySoundEffects.hasOwnProperty("fileURL") &&
                    tkeySoundEffects.hasOwnProperty("translation")) {
                    let tSoundData = {};
                    tSoundData.category = tkeySoundEffects["category"];
                    tSoundData.id = tkeySoundEffects["id"];
                    tSoundData.fileURL = tkeySoundEffects["fileURL"];
                    if (tSoundData.fileURL.length > 0 && tSoundData.fileURL.indexOf('http') != 0) {
                        tSoundData.fileURL = this.dataPath + tSoundData.fileURL;
                    }
                    if (tkeySoundEffects.hasOwnProperty("description")) {
                        tSoundData.description = tkeySoundEffects["description"];
                    }
                    else {
                        tSoundData.description = "";
                    }
                    tSoundData.translation = new Array();
                    for (let tTrans of tkeySoundEffects["translation"]) {
                        if (tTrans.hasOwnProperty("lang") &&
                            tTrans.hasOwnProperty("text")) {
                            let tarrTrans = {};
                            tarrTrans.lang = tTrans["lang"];
                            tarrTrans.text = tTrans["text"];
                            tSoundData.translation.push(tarrTrans);
                        }
                    }
                    let tTXTexists = this.isSoundEffectAlreadyAvailable(tSoundData);
                    if (!tTXTexists) {
                        this.mExtLoadedSoundEffects.push(tSoundData);
                    }
                }
            }
        }
        return (Promise.resolve(true));
    }
    getFPS() {
        return (this.mApp.engine.getFps());
    }
    getGridSubSelection(pRE) {
        if (pRE.type != "Grid" || !this.mApp.model.hasSubElementSelection()) {
            return (pRE);
        }
        const tgridChild = this.mApp.model.getSubElementSelection(pRE);
        if (tgridChild == null) {
            return (pRE);
        }
        return tgridChild;
    }
    _triggerHiddenWalls(pForce = false, pEnabled = false) {
        if ((!pForce && !this.mHiddenWallsStatus) || this.mView === null)
            return;
        for (let tWall of this.mWalls) {
            tWall.setHidden(false);
            tWall.checkActiveMesh();
        }
        if (!pForce) {
            if (this.mView.cameraControl.cameraMode === 0) {
                return;
            }
            for (let tWall of this.mWalls) {
                tWall.shotRay();
                tWall.checkHideWalls();
            }
        }
        for (let tWall of this.mWalls)
            tWall._updateVisibility(false);
        this.mApp.viewer.requestRenderFrame();
    }
    setLayerVisibility_EGR(pVisible) {
        this.mApp.model.layers.setLayerVisible("72_EGR_OFFICE2_D3_BENUTZERFLAECHE", pVisible);
        this.mApp.model.layers.setLayerVisible("72_EGR_OFFICE2_D3_FUNKTIONSFLAECHE", pVisible);
        this.mApp.model.layers.setLayerVisible("72_EGR_OFFICE2_D3_BENUTZERLINIE", pVisible);
        let tMA = this.mArticleManager.getAllMainArticles();
        for (let tElement of tMA) {
            if (tElement instanceof MainArticleElement) {
                tElement.notifyChange(ElementChangedFlags.BoundingBox);
            }
        }
        this.mApp.model.notifyModification();
        this.mApp.viewer.requestRenderFrame();
    }
    setLayerVisibility_Buildings(pVisible) {
        let tSel = this.mApp.model.selection;
        let tBuildings = new Array();
        for (let tElement of tSel) {
            if ((tElement instanceof RoomElement) && (tElement.type === "CustomBuilding")) {
                tBuildings.push(tElement);
            }
        }
        for (let tElement of tBuildings) {
            this.mApp.model.removeFromSelection(tElement);
        }
        this.mApp.model.layers.setLayerVisible("room:Building_Layer", pVisible);
        this.eventBuildingLayerVisibilityChanged.trigger({});
        this.mApp.model.notifyModification();
        this.mApp.viewer.requestRenderFrame();
    }
    setCheckREIntersection(pList) {
        for (let tRE of pList) {
            if (this.mCheckIntersectRoomElements.indexOf(tRE) === -1)
                this.mCheckIntersectRoomElements.push(tRE);
        }
    }
    setClickedWOmove(pValue) {
        this.mClickedWOmove = pValue;
    }
    setFoundREIntersection(pList) {
        for (let tRE of pList) {
            if (this.mFoundIntersectRoomElements.indexOf(tRE) === -1)
                this.mFoundIntersectRoomElements.push(tRE);
        }
    }
    /**
     * Changes the texture of inserted sphere.
     */
    setSphereTexture(pURL) {
        this.insertPictureAsMaterial(pURL).then((pResult) => {
            if (pResult != null) {
                let tResult = pResult;
                let tId = tResult.category + this.mMaterialSplitStr + tResult.value;
                let tMat = this.mMatManager.getMaterial(tId);
                if (tMat == null)
                    return;
                tMat.albedoTexture.vScale = -1;
                tMat.albedoTexture.notifyTextureMatrixChanged();
                this.mSkyBoxMaterials[0] = tMat;
                let tSel = this.mApp.model.selection;
                if (tSel.length === 1 &&
                    ((tSel[0] instanceof RoomElement) && tSel[0].type === "Sphere")) {
                    let tSphere = tSel[0];
                    tSphere.updateGeometry();
                    return;
                }
                return;
            }
            return;
        });
    }
    getDirectionToCamera(pPos, pView) {
        let tCameraVec;
        if (pView.cameraControl.cameraMode === CameraMode.Perspective) {
            tCameraVec = pView.cameraControl.position.subtract(pPos);
            tCameraVec.normalize();
        }
        else
            tCameraVec = pView.cameraControl.direction.negate();
        return (tCameraVec);
    }
    prepareWallColorPropValues() {
        this.mWallColorPropValueMap = new Map();
        let tBaseText = this.resources.propertyColor;
        for (let i = 0; i < this.mWallColors.length; ++i) {
            let tCol = this.mWallColors[i];
            this.createColorMaterial(tCol);
            let tPropValue = new PropertyValue(tCol, tBaseText + " " + (i + 1));
            tPropValue.smallIcon = this.generateColorPreviewImage(tCol);
            tPropValue.largeIcon = this.generateColorPreviewImage(tCol);
            this.mWallColorPropValueMap.set(tCol, tPropValue);
        }
    }
    prepareDoorTypesPropValues() {
        this.mDoorTypesValueMap = new Map();
        let tPropLabel = this.resources.propertyDoorType_solid;
        for (let tDoorType of this.mDoorTypes) {
            switch (tDoorType) {
                case "solid":
                case "solidDouble":
                    tPropLabel = this.resources.propertyDoorType_solid;
                    break;
                case "inlay":
                case "inlayDouble":
                    tPropLabel = this.resources.propertyDoorType_inlay;
                    break;
                case "glass":
                case "glassDouble":
                    tPropLabel = this.resources.propertyDoorType_glass;
                    break;
                default:
                    tPropLabel = this.resources.propertyDoorType_solid;
                    break;
            }
            let tPropValue = new PropertyValue(tDoorType, tPropLabel);
            this.mDoorTypesValueMap.set(tDoorType, tPropValue);
        }
    }
    preparePictureFrameTypesPropValues() {
        this.mPictureFrameTypesArr = [];
        // type 0: 3:2
        let tPropValue_0 = new PropertyValue(0, "3:2");
        this.mPictureFrameTypesArr.push(tPropValue_0);
        // type 1: 2:3
        let tPropValue_1 = new PropertyValue(1, "2:3");
        this.mPictureFrameTypesArr.push(tPropValue_1);
        // type 2: 1:1
        let tPropValue_2 = new PropertyValue(2, "1:1");
        this.mPictureFrameTypesArr.push(tPropValue_2);
        // type 3: whiteboard
        let tPropValue_3 = new PropertyValue(3, this.resources.propertyWhiteBoard);
        this.mPictureFrameTypesArr.push(tPropValue_3);
        // type 4: individual
        let tPropValue_4 = new PropertyValue(4, this.resources.propertyPictureFrameCustom);
        this.mPictureFrameTypesArr.push(tPropValue_4);
        // type 5: chalkboard
        let tPropValue_5 = new PropertyValue(5, this.resources.propertyChalkboard);
        this.mPictureFrameTypesArr.push(tPropValue_5);
    }
    getTextFromMaterialData(pMaterialData) {
        let tRes = "color";
        if (pMaterialData.translation.length === 0)
            return (tRes);
        for (let tEntry of pMaterialData.translation) {
            if (tEntry.lang === this.mAppLanguage) {
                tRes = tEntry.text;
                break;
            }
        }
        return (tRes);
    }
    addWallColorPropValues(pMaterialData) {
        //this.mWallColorPropValueMap = new Map();
        let tBaseText = this.getTextFromMaterialData(pMaterialData);
        let tCol = pMaterialData.albedocolor;
        this.createColorMaterial(tCol);
        let tPropValue = new PropertyValue(tCol, tBaseText);
        tPropValue.smallIcon = this.generateColorPreviewImage(tCol);
        tPropValue.largeIcon = this.generateColorPreviewImage(tCol);
        this.mWallColorPropValueMap.set(tCol, tPropValue);
    }
    createTextColors() {
        this.mTextColors = new Array();
        this.mTextColors.push({ name: this.resources.propertyColor_Black, hex: "#000000" });
        this.mTextColors.push({ name: this.resources.propertyColor_DarkRed, hex: "#9b0600" });
        this.mTextColors.push({ name: this.resources.propertyColor_Red, hex: "#f20000" });
        this.mTextColors.push({ name: this.resources.propertyColor_Orange, hex: "#fe9900" });
        this.mTextColors.push({ name: this.resources.propertyColor_Yellow, hex: "#ffed00" });
        this.mTextColors.push({ name: this.resources.propertyColor_Green, hex: "#95c11f" });
        this.mTextColors.push({ name: this.resources.propertyColor_LightBlue, hex: "#00b8ee" });
        this.mTextColors.push({ name: this.resources.propertyColor_DarkBlue, hex: "#004699" });
        this.mTextColors.push({ name: this.resources.propertyColor_Purple, hex: "#78269a" });
        this.mTextColors.push({ name: this.resources.propertyColor_Pink, hex: "#e5007d" });
    }
    prepareTextColorPropValues() {
        this.mTextColorPropValueMap = new Map();
        let tBaseText = this.resources.propertyColor;
        for (let tTxtCol of this.mTextColors) {
            tBaseText = tTxtCol.name;
            let tCol = tTxtCol.hex;
            let tPropValue = new PropertyValue(tCol, tBaseText);
            tPropValue.smallIcon = this.generateColorPreviewImage(tCol);
            tPropValue.largeIcon = this.generateColorPreviewImage(tCol);
            this.mTextColorPropValueMap.set(tCol, tPropValue);
        }
    }
    prepareRouteWidthsPropValues() {
        this.mRouteWidthsPropValueMap = new Map();
        for (let tWidth of this.mRouteWidths) {
            let tPropValue = new PropertyValue(tWidth, tWidth);
            this.mRouteWidthsPropValueMap.set(tWidth, tPropValue);
        }
    }
    getMaterialText(pIdx, pLang) {
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mExtLoadedMaterialDefinitions[pIdx].translation.length; i++) {
            tRes = this.mExtLoadedMaterialDefinitions[pIdx].translation[0].text;
            if (this.mExtLoadedMaterialDefinitions[pIdx].translation[i].lang === pLang) {
                tlangExists = true;
                tRes = this.mExtLoadedMaterialDefinitions[pIdx].translation[i].text;
                break;
            }
        }
        if (!tlangExists) {
            tRes = this.getMaterialText(pIdx, 'en');
        }
        return (tRes);
    }
    getMaterialCategoryText(pIdx, pLang) {
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mExtLoadedMaterialCategory[pIdx].translation.length; i++) {
            tRes = this.mExtLoadedMaterialCategory[pIdx].translation[0].text;
            if (this.mExtLoadedMaterialCategory[pIdx].translation[i].lang === pLang) {
                tlangExists = true;
                tRes = this.mExtLoadedMaterialCategory[pIdx].translation[i].text;
                break;
            }
        }
        if (!tlangExists) {
            tRes = this.getMaterialCategoryText(pIdx, 'en');
        }
        return (tRes);
    }
    getSoundCategoryText(pIdx, pLang) {
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mExtLoadedSoundCategory[pIdx].translation.length; i++) {
            tRes = this.mExtLoadedSoundCategory[pIdx].translation[0].text;
            if (this.mExtLoadedSoundCategory[pIdx].translation[i].lang === pLang) {
                tlangExists = true;
                tRes = this.mExtLoadedSoundCategory[pIdx].translation[i].text;
                break;
            }
        }
        if (!tlangExists) {
            tRes = this.getSoundCategoryText(pIdx, 'en');
        }
        return (tRes);
    }
    getSoundEffectsText(pIdx, pLang) {
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < this.mExtLoadedSoundEffects[pIdx].translation.length; i++) {
            tRes = this.mExtLoadedSoundEffects[pIdx].translation[0].text;
            if (this.mExtLoadedSoundEffects[pIdx].translation[i].lang === pLang) {
                tlangExists = true;
                tRes = this.mExtLoadedSoundEffects[pIdx].translation[i].text;
                break;
            }
        }
        if (!tlangExists) {
            tRes = this.getSoundEffectsText(pIdx, 'en');
        }
        return (tRes);
    }
    getSoundEffectsTextByEntry(pEntry, pLang) {
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < pEntry.translation.length; i++) {
            tRes = pEntry.translation[0].text;
            if (pEntry.translation[i].lang === pLang) {
                tlangExists = true;
                tRes = pEntry.translation[i].text;
                break;
            }
        }
        if (!tlangExists) {
            tRes = this.getSoundEffectsTextByEntry(pEntry, 'en');
        }
        return (tRes);
    }
    getMaterialCategoryTextFromMaterialSet(pmatSet, pLang) {
        let tRes = "";
        let tlangExists = false;
        for (let tmatCat of this.mExtLoadedMaterialCategory) {
            if (tmatCat.id !== pmatSet.category)
                continue;
            for (let tmatCatTransl of tmatCat.translation) {
                tRes = tmatCatTransl.text;
                if (tmatCatTransl.lang === pLang) {
                    tlangExists = true;
                    break;
                }
            }
            if (tlangExists)
                break;
        }
        if (!tlangExists) {
            if (pLang !== "en") {
                tRes = this.getMaterialCategoryTextFromMaterialSet(pmatSet, 'en');
            }
            else {
                tRes = pmatSet.category + " " + pmatSet.value;
            }
        }
        return (tRes);
    }
    getextMaterialText(pmatSet, pLang) {
        const tmatId = this.getMaterialId(pmatSet);
        let tRes = "";
        let tlangExists = false;
        // tslint:disable-next-line:prefer-for-of
        for (let textMat of this.mExtLoadedMaterialDefinitions) {
            const tid = textMat.category + this.materialSplitStr + textMat.id;
            if (tid !== tmatId)
                continue;
            for (let ttransl of textMat.translation) {
                tRes = ttransl.text;
                if (ttransl.lang === pLang) {
                    tlangExists = true;
                    const tmatcatLabel = this.getMaterialCategoryTextFromMaterialSet(pmatSet, pLang);
                    tRes = tmatcatLabel + ": " + ttransl.text;
                    break;
                }
            }
            if (tlangExists) {
                break;
            }
        }
        if (!tlangExists) {
            if (pLang !== "en") {
                tRes = this.getextMaterialText(pmatSet, 'en');
            }
            else {
                tRes = pmatSet.category + " " + pmatSet.value;
            }
        }
        return (tRes);
    }
    prepareArea2DMaterialPropValues() {
        this.mArea2DMaterialPropValueMap = new Map();
        let tBaseText = this.resources.propertyColor;
        let tCol = this.mStandardColor;
        let tPropValue = new PropertyValue(tCol, tBaseText);
        tPropValue.smallIcon = this.generateColorPreviewImage(tCol);
        tPropValue.largeIcon = this.generateColorPreviewImage(tCol);
        this.mArea2DMaterialPropValueMap.set(tCol, tPropValue);
    }
    prepareExtMaterialCategoryPropValues() {
        this.mextMaterialCategoryPropValueMap = new Map();
        for (let i = 0; i < this.mExtLoadedMaterialCategory.length; ++i) {
            let tLang = this.getLocaleLanguage();
            //XXX
            tLang = "en";
            let tText = this.getMaterialCategoryText(i, tLang);
            let tKey = this.mExtLoadedMaterialCategory[i].id;
            let tPropValueExt = new PropertyValue(tKey, tText);
            tPropValueExt.smallIcon = this.mExtLoadedMaterialCategory[i].smallIcon;
            tPropValueExt.largeIcon = this.mExtLoadedMaterialCategory[i].smallIcon;
            if (tKey === "color") {
                tPropValueExt.smallIcon = this.mDataPath + "images/color-picker.png";
                tPropValueExt.largeIcon = this.mDataPath + "images/color-picker.png";
            }
            this.mextMaterialCategoryPropValueMap.set(tKey, tPropValueExt);
            for (let x = 0; x < this.mExtLoadedMaterialDefinitions.length; x++) {
                let tTxt = this.mExtLoadedMaterialDefinitions[x];
                if (tTxt.category !== tKey)
                    continue;
                let tText1 = this.getMaterialText(x, tLang);
                let tmatId = tTxt.category + this.mMaterialSplitStr + tTxt.id;
                let tPropValue = new PropertyValue(tmatId, tText1);
                tPropValue.smallIcon = this.mExtLoadedMaterialDefinitions[x].smallIcon;
                tPropValue.largeIcon = this.mExtLoadedMaterialDefinitions[x].smallIcon;
                this.mArea2DMaterialPropValueMap.set(tmatId, tPropValue);
            }
        }
    }
    prepareExtSoundEffectsPropValues() {
        this.mextSoundEffectsPropValueMap = new Map();
        for (let i = 0; i < this.mExtLoadedSoundEffects.length; ++i) {
            const tsndEff = this.mExtLoadedSoundEffects[i];
            let tLang = this.getLocaleLanguage();
            //XXX
            tLang = "en";
            const tText = this.getSoundEffectsText(i, tLang);
            const tKey = tsndEff.category + RoomManager.CATVALUE_SPLITTER + tsndEff.id;
            const tPropValueExt = new PropertyValue(tKey, tText);
            this.mextSoundEffectsPropValueMap.set(tKey, tPropValueExt);
        }
    }
    prepareGridDimPropValues() {
        if (this.mGridDimPropValueMap == null) {
            this.mGridDimPropValueMap = new Map();
        }
        else {
            this.mGridDimPropValueMap.clear();
        }
        for (let tDim of this.mgridFieldDimension) {
            const tdimStr = Util.convertVector2ToDimStr(tDim);
            const tPropValue = new PropertyValue(tdimStr, tdimStr);
        }
    }
    prepareYesNoPropValues() {
        this.mYesNoPropValueMap = new Map();
        let tChoiceNo = this.mYesNo[0];
        let tPropValueY = new PropertyValue(tChoiceNo, this.resources.propertyValueNo);
        this.mYesNoPropValueMap.set(tChoiceNo.toString(), tPropValueY);
        let tChoiceYes = this.mYesNo[1];
        let tPropValueN = new PropertyValue(tChoiceYes, this.resources.propertyValueYes);
        this.mYesNoPropValueMap.set(tChoiceYes.toString(), tPropValueN);
    }
    prepareOvalCirclePropValues() {
        this.mArenaOvalCirclePropValueMap = new Map();
        let tPropValueOval = new PropertyValue('oval', this.resources.propertyArenaOval);
        this.mArenaOvalCirclePropValueMap.set('oval', tPropValueOval);
        let tPropValueCircle = new PropertyValue('circle', this.resources.propertyArenaCircle);
        this.mArenaOvalCirclePropValueMap.set('circle', tPropValueCircle);
    }
    /**
     * Checks, whether 2 lines are connected in the same point
     */
    areConnected(pLine1, pLine2) {
        let result = false;
        let tL1_P1 = pLine1[1];
        let tL2_P0 = pLine2[0];
        if (Util.samePointsV3(tL1_P1, tL2_P0, 0.0001))
            result = true;
        return result;
    }
    /**
     * Try to find a closed polygon (based on line array)
     */
    findEnd(pLine, pRoom) {
        if (pRoom.length > 2) {
            let tLastLine = pRoom[pRoom.length - 1];
            let tFirstLine = pRoom[0];
            if (this.areConnected(tLastLine, tFirstLine))
                return true;
        }
        for (let tLine of this.m2dLines) {
            let tIdx = pRoom.indexOf(tLine);
            if ((tLine !== pLine && tIdx === -1) && (this.areConnected(pLine, tLine))) {
                pRoom.push(tLine);
                if (this.findEnd(tLine, pRoom)) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Adds a closed polygon to an array to create floors
     */
    addRoom(pRoom) {
        let t2dRoom = new Array();
        for (let tLines of pRoom) {
            let tP1 = new Vector2(tLines[0].x, tLines[0].z);
            let tP2 = new Vector2(tLines[1].x, tLines[1].z);
            let texists = false;
            for (let tPoint of t2dRoom) {
                if (Util.samePointsV2(tP1, tPoint, 0.00001)) {
                    texists = true;
                    break;
                }
            }
            if (!texists)
                t2dRoom.push(tP1);
            texists = false;
            for (let tPoint of t2dRoom) {
                if (Util.samePointsV2(tP2, tPoint, 0.00001)) {
                    texists = true;
                    break;
                }
            }
            if (!texists)
                t2dRoom.push(tP2);
        }
        this.mRooms.push(t2dRoom);
    }
    getWallsFromRoomPolygon(pRoomPolygon) {
        let tRes = new Array();
        for (let tP of pRoomPolygon) {
            for (let tWall of this.mWalls) {
                if (tWall.isWallCutEdge(tP) && (!contains(tRes, tWall))) {
                    addElements(tRes, [tWall]);
                }
            }
        }
        return (tRes);
    }
    getSortedWallIds(pWalls) {
        let tRes = new Array();
        for (let tWall of pWalls)
            tRes.push(tWall.id);
        tRes.sort((a, b) => (a - b));
        return (tRes);
    }
    /**
     * Checks, whether a polygon was found under a (in case of non closed rooms.
     */
    removeFloorUnderWalls() {
        let tWallCenterPoints = new Array();
        for (let mWall of this.mWalls) {
            let tCenter = Vector2.Center(mWall.startPoint, mWall.endPoint);
            tWallCenterPoints.push(tCenter);
        }
        let twallRooms = Array();
        for (let i = 0; i < this.mRooms.length; i++) {
            for (let tCP of tWallCenterPoints) {
                if (Util.pointInPolygon(tCP, this.mRooms[i])) {
                    twallRooms.push(i);
                    break;
                }
            }
        }
        for (let i = twallRooms.length - 1; i >= 0; i--) {
            this.mRooms.splice(twallRooms[i], 1);
        }
    }
    /**
     * Parses an array of 2d lines and brings all lines in
     * the same direction to possible get a line path
     */
    makePath(pLine, pSearchArray) {
        let tFound = false;
        let tFoundLine = null;
        let tRes = {};
        tRes.index = -1;
        for (let i = 0; i < pSearchArray.length; i++) {
            let tcompLine = pSearchArray[i];
            let tP2L1 = pLine[1];
            let tP1L1 = pLine[0];
            let tP2L2 = tcompLine[1];
            let tP1L2 = tcompLine[0];
            if (Util.samePointsV3(tP2L1, tP2L2, 0.0001)) {
                let tHP0 = new Vector3(tP2L2.x, 0, tP2L2.z);
                let tHP1 = new Vector3(tP1L2.x, 0, tP1L2.z);
                tFound = true;
                tFoundLine = new Array();
                tFoundLine.push(tHP0, tHP1);
                tRes.index = i;
                tRes.line = tFoundLine;
                break;
            }
            if (Util.samePointsV3(tP2L1, tP1L2, 0.0001)) {
                let tHP0 = new Vector3(tP1L2.x, 0, tP1L2.z);
                let tHP1 = new Vector3(tP2L2.x, 0, tP2L2.z);
                tFound = true;
                tFoundLine = new Array();
                tFoundLine.push(tHP0, tHP1);
                tRes.index = i;
                tRes.line = tFoundLine;
                break;
            }
        }
        return (tRes);
    }
    /**
     * Line sorting
     */
    sortLines(pRoom, pSearchArray) {
        let t_searchArray = pSearchArray;
        let tRoom = pRoom;
        let tfirstLine = new Array();
        if (pRoom == null) {
            tRoom = new Array();
            let tFL = t_searchArray.shift();
            if (tFL === undefined)
                return;
            tfirstLine = tFL;
            tRoom.push(tfirstLine);
        }
        else {
            tRoom = pRoom;
            tfirstLine = tRoom[tRoom.length - 1];
        }
        // tslint:disable-next-line:prefer-for-of
        for (let c = 0; c < t_searchArray.length; c++) {
            let tRes = this.makePath(tfirstLine, t_searchArray);
            if (tRes.line != null) {
                if (tRoom != null)
                    tRoom.push(tRes.line);
                t_searchArray.splice(tRes.index, 1);
                if (t_searchArray.length === 0) {
                    if (tRoom != null)
                        this.addRoom(tRoom);
                    break;
                }
                this.sortLines(tRoom, t_searchArray);
            }
            else if (tRoom != null) {
                this.addRoom(tRoom);
                tRoom = null;
                this.sortLines(tRoom, t_searchArray);
            }
        }
    }
    removeFloors() {
        while (this.mFloors.length > 0) {
            let tFloor = this.mFloors[0];
            this.mApp.model.removeElement(tFloor);
            this.mFloors.splice(0, 1);
        }
        this.mApp.rendering.notifyModelInteraction();
        this.mApp.viewer.requestRenderFrame();
    }
    setSZLayerVisibility(pVisible) {
        for (let tManufacturer of this.mSeriesList) {
            for (let tSZStandard of this.mSZ_standards) {
                let tstandardList = new Array();
                tstandardList.push(tSZStandard);
                if (tSZStandard === "EN")
                    tstandardList.push("EN16630");
                if (tSZStandard === "ASTM")
                    tstandardList.push("ASTMF3101");
                for (let tStandard of tstandardList) {
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_SAFETYZONE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_SAFETYZONE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-SAFETYZONE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-SAFETYZONE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_BUFFERZONE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_FREE_SPACE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_NOENCROACHMENTZONE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_NOENCROACHMENTZONE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_CLEARANCEZONE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_CLEARANCEZONE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-TRAININGENVELOPE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-TRAININGENVELOPE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_TRAININGENVELOPE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_TRAININGENVELOPE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-TRAININGSPACE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "-TRAININGSPACE", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_TRAININGSPACE_A", pVisible);
                    this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_" + tStandard + "_TRAININGSPACE", pVisible);
                }
            }
            this.mApp.model.layers.setLayerVisible("72_" + this.mProjectManufacturer + "_" + tManufacturer + "_D3_DIMENSIONPOINT_FALLHEIGHT", pVisible);
        }
        if (this.mLayerList !== undefined) {
            for (let tLine of this.mLayerList) {
                this.mApp.model.layers.setLayerVisible(tLine, pVisible);
            }
        }
    }
    setTextPickPlane(pEnabled) {
        for (let tRE of this.mRoomElements) {
            tRE.setTextPickPlane(pEnabled);
        }
    }
    setSkyBox() {
        const skyboxMaterial = new StandardMaterial("skyBox", this.mScene);
        skyboxMaterial.backFaceCulling = false;
        skyboxMaterial.reflectionTexture = new BJSTexture(this.mDataPath + "textures/snowblind.jpg" + "?" + this.mRandomString, this.mScene, true);
        skyboxMaterial.reflectionTexture.coordinatesMode = BJSTexture.FIXED_EQUIRECTANGULAR_MODE;
        skyboxMaterial.disableLighting = true;
        if (this.mSkyBox != null) {
            this.mSkyBox.material = skyboxMaterial;
            this.mSkyBox.isPickable = false;
            if (this.mView == null) {
                let tView = this.view;
                if (tView != null) {
                    this.mView = tView;
                    this.mSkyBox.position = tView.camera.position;
                }
                return;
            }
        }
        this.app.viewer.requestRenderFrame();
    }
    /**
     * Filters only needed mesh (2d shapes, roomelement) for material recognition
     */
    shapeFilter(pMesh) {
        let tname = pMesh.name;
        if (tname.indexOf("shape2d_") === 0)
            return true;
        return false;
    }
    /**
     * Checks, whether a 2d shape is below an OFML article (position) and
     * return the material index if so
     */
    // tslint:disable-next-line:cyclomatic-complexity
    getShapeMaterialBelowArticle(pPosition) {
        let tRes = {};
        tRes.materialIndex = -1;
        tRes.materialColor = this.mStandardColor;
        tRes.materialCategory = "color";
        tRes.materialValue = "";
        tRes.materialId = "";
        let tPosition = pPosition.clone();
        tPosition.y = 0.5;
        let tdownVector = Vector3.Down();
        let tRay = new Ray(tPosition, tdownVector);
        let trayHits = this.mScene.multiPickWithRay(tRay, this.shapeFilter);
        if (trayHits != null && trayHits.length > 0) {
            let tHit = trayHits[0];
            if ((tHit.pickedPoint != null && tHit.pickedMesh != null) &&
                (tHit.pickedMesh.name.indexOf("shape2d") === 0) &&
                (tHit.pickedMesh.parent != null) &&
                (tHit.pickedMesh.parent.name.indexOf("group_") > -1)) {
                let tgrpParent = tHit.pickedMesh.parent.parent;
                if (tgrpParent != null) {
                    let tRE = SceneElement.FromNode(tgrpParent, false);
                    if (tRE instanceof RoomElement) {
                        tRes.materialIndex = tRE.material;
                        tRes.materialColor = tRE.color;
                        tRes.materialId = "";
                        if (tRE.materialId !== undefined)
                            tRes.materialId = tRE.materialId;
                        tRes.materialCategory = "color";
                        if (tRE.materialCategory !== undefined)
                            tRes.materialCategory = tRE.materialCategory;
                        tRes.materialValue = "";
                        if (tRE.materialValue !== undefined)
                            tRes.materialValue = tRE.materialValue;
                    }
                }
            }
        }
        return (tRes);
    }
    sortLinesOutlines(pLines) {
        let tRes = new Array();
        tRes.push(this.mOutLineArray[0]);
        this.mOutLineArray.splice(0, 1);
        let tGlobalBreak = -1;
        while (this.mOutLineArray.length > 0) {
            if (tGlobalBreak === this.mOutLineArray.length) {
                // loop with same length value -> no change! -> break!
                // to avoid infinite loop!
                break;
            }
            tGlobalBreak = this.mOutLineArray.length;
            let tLine = new Array();
            if (tRes.length >= 2) {
                let tStartPoint = tRes[0][0];
                let tEndPoint = tRes[tRes.length - 1][1];
                if (Util.samePointsV3(tStartPoint, tEndPoint, 0.0000001)) {
                    tRes.push(this.mOutLineArray[0]);
                    this.mOutLineArray.splice(0, 1);
                    tLine = this.mOutLineArray[0];
                }
            }
            tLine = tRes[tRes.length - 1];
            for (let i = 0; i < this.mOutLineArray.length; i++) {
                let tcompLine = this.mOutLineArray[i];
                let tP2L1 = tLine[1].clone();
                let tP1L1 = tLine[0].clone();
                let tP2L2 = tcompLine[1].clone();
                let tP1L2 = tcompLine[0].clone();
                if (Util.samePointsV3(tP2L1, tP2L2, 0.0000001)) {
                    this.mOutLineArray.splice(i, 1);
                    tRes.push([tP2L2, tP1L2]);
                    break;
                }
                if (Util.samePointsV3(tP2L1, tP1L2, 0.0000001)) {
                    this.mOutLineArray.splice(i, 1);
                    tRes.push([tP1L2, tP2L2]);
                    break;
                }
            }
        }
        this.mOutLineArray = tRes;
        return (tRes);
    }
    makePathOutline(pLine) {
        for (let tcompLine of this.mOutLineArray) {
            if (tcompLine !== pLine) {
                let tP2L1 = pLine[1];
                let tP1L1 = pLine[0];
                let tP2L2 = tcompLine[1];
                let tP1L2 = tcompLine[0];
                if (Util.samePointsV3(tP2L1, tP2L2, 0.0001)) {
                    let tHP0x = tcompLine[0].x;
                    let tHP0z = tcompLine[0].z;
                    let tHP1x = tcompLine[1].x;
                    let tHP1z = tcompLine[1].z;
                    tcompLine[0].x = tHP1x;
                    tcompLine[0].z = tHP1z;
                    tcompLine[1].x = tHP0x;
                    tcompLine[1].z = tHP0z;
                    this.makePathOutline(tcompLine);
                }
                if (Util.samePointsV3(tP2L1, tP1L2, 0.0001) && !Util.samePointsV3(tP1L1, tP2L2, 0.0001))
                    break;
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getOutLineofMesh(pMesh, pFinalPolygon = true, pDrawNormals = false) {
        let tRes = new Array();
        if (pMesh == null)
            return (tRes);
        let thMesh = null;
        let tWM = pMesh.getWorldMatrix();
        thMesh = pMesh.clone("");
        this.mApp.model.layers.setMeshLayer(thMesh, "room_safetyzones");
        setLocalMatrix(thMesh, tWM);
        thMesh.convertToFlatShadedMesh();
        if (thMesh == null)
            return (tRes);
        let tmIdx = thMesh.getIndices();
        let tpdata = thMesh.getVerticesData(VertexBuffer.PositionKind);
        let tndata = thMesh.getVerticesData(VertexBuffer.NormalKind);
        if (tpdata == null || tndata == null)
            return (tRes);
        let tipData = [];
        for (let i = 0; i < tpdata.length; i += 3) {
            let t1 = tpdata[i] * thMesh.scaling.x;
            let t2 = tpdata[i + 1] * thMesh.scaling.y;
            let t3 = tpdata[i + 2] * thMesh.scaling.z;
            tipData.push(t1);
            tipData.push(t2);
            tipData.push(t3);
        }
        tRes = [];
        let tfn = [];
        thMesh.updateFacetData();
        if (tmIdx == null)
            return (tRes);
        for (let i = 0; i < thMesh.facetNb; i++) {
            let tn = thMesh.getFacetNormal(i);
            if (tn.y === 0) {
                tfn.push(i);
            }
        }
        // tslint:disable-next-line:prefer-for-of
        for (var p = 0; p < tfn.length; p++) {
            let tLine = [];
            let tpStart = tfn[p] * 3;
            let tfIdx1 = tmIdx[tpStart] * 3;
            let tfIdx2 = tmIdx[tpStart + 1] * 3;
            let tfIdx3 = tmIdx[tpStart + 2] * 3;
            let tP1 = Vector3.FromArray(tipData, tfIdx1);
            let tP2 = Vector3.FromArray(tipData, tfIdx2);
            let tP3 = Vector3.FromArray(tipData, tfIdx3);
            if (!pFinalPolygon) {
                if ((tP1.y < 0.0001) && (tP1.y > -0.001)) {
                    tP1.y = 0;
                    tLine.push(tP1);
                }
                if ((tP2.y < 0.0001) && (tP2.y > -0.001)) {
                    tP2.y = 0;
                    tLine.push(tP2);
                }
                if ((tP3.y < 0.0001) && (tP3.y > -0.001)) {
                    tP3.y = 0;
                    tLine.push(tP3);
                }
            }
            else {
                if (tP1.y === -0.001) {
                    tP1.y = 0;
                    tLine.push(tP1);
                }
                if (tP2.y === -0.001) {
                    tP2.y = 0;
                    tLine.push(tP2);
                }
                if (tP3.y === -0.001) {
                    tP3.y = 0;
                    tLine.push(tP3);
                }
            }
            if (tLine.length === 2) {
                let tP11 = tLine[0];
                let tP21 = tLine[1];
                if (!Util.samePointsV3(tP11, tP21, 0.0000001)) {
                    let tSL = false;
                    for (let tR of tRes) {
                        if (Util.sameLineV3(tLine, tR)) {
                            tSL = true;
                            break;
                        }
                    }
                    if (!tSL) {
                        let t1 = tLine[0].add(thMesh.position);
                        let t2 = tLine[1].add(thMesh.position);
                        t1.y = 0;
                        t2.y = 0;
                        tRes.push([t1, t2]);
                    }
                }
            }
        }
        thMesh.dispose();
        this.mOutLineArray = [];
        this.mOutLineArray = tRes;
        tRes = this.sortLinesOutlines(tRes);
        tRes = this.mOutLineArray;
        this.mOutLineArray = [];
        return (tRes);
    }
    calcCamLeftRight() {
        if (this.mView == null)
            return;
        let tcamPos = Util.to2d(this.mView.cameraControl.position);
        let tcamViewDir = Util.to2d(this.mView.cameraControl.direction.clone().normalize());
        let tcamRight = (perpendicularVector2(tcamViewDir)).scale(0.5);
        let tcamLeft = Util.invert2d(tcamRight);
        this.mCamLeftRight[0] = tcamPos.add(tcamLeft);
        this.mCamLeftRight[1] = tcamPos.add(tcamRight);
    }
    calcMeshPerimeter(pMesh) {
        let tRes = 0;
        let tFacetL = Util.getPerimeterLines(pMesh);
        /*
        // think about: not necessary to sort lines ???!
        this.m2dLines = tFacetL;
        this.sortLines();
        tFacetL = this.m2dLines;
        */
        tRes = Util.getLineDistance(tFacetL);
        return (tRes);
    }
    uniqueLineMap(pTrianglePoints, pMap) {
        let tRes = pMap;
        //reset height to 0!
        let tP1 = pTrianglePoints[0];
        let tP2 = pTrianglePoints[1];
        let tP3 = pTrianglePoints[2];
        tP1.y = 0;
        tP2.y = 0;
        tP3.y = 0;
        let tL11 = [tP2, tP1].toString();
        let tL12 = [tP1, tP2].toString();
        let tL21 = [tP3, tP2].toString();
        let tL22 = [tP2, tP3].toString();
        let tL31 = [tP1, tP3].toString();
        let tL32 = [tP3, tP1].toString();
        let tE11 = tRes.get(tL11);
        let tE12 = tRes.get(tL12);
        if (tE11 !== undefined || tE12 !== undefined) {
            tRes.delete(tL11);
            tRes.delete(tL12);
        }
        else {
            tRes.set(tL11, { p1: tP2, p2: tP1 });
        }
        let tE21 = tRes.get(tL21);
        let tE22 = tRes.get(tL22);
        if (tE21 !== undefined || tE22 !== undefined) {
            tRes.delete(tL21);
            tRes.delete(tL22);
        }
        else {
            tRes.set(tL21, { p1: tP3, p2: tP2 });
        }
        let tE31 = tRes.get(tL31);
        let tE32 = tRes.get(tL32);
        if (tE31 !== undefined || tE32 !== undefined) {
            tRes.delete(tL31);
            tRes.delete(tL32);
        }
        else {
            tRes.set(tL31, { p1: tP1, p2: tP3 });
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getRegionOutLines(pMesh) {
        let tRes = [];
        if (pMesh == null)
            return (tRes);
        let tWM = pMesh.getWorldMatrix();
        let tindices = pMesh.getIndices();
        let tpdata = pMesh.getVerticesData(VertexBuffer.PositionKind);
        if (tpdata == null || tindices == null)
            return (tRes);
        // get outside facet (normal: y =0)
        let tfn = [];
        pMesh.updateFacetData();
        for (let i = 0; i < pMesh.facetNb; i++) {
            let tn = pMesh.getFacetNormal(i);
            tn = tn.normalize();
            if (Util.numbersEqual(tn.y, 1, 0.00001)) {
                tfn.push(i);
            }
        }
        let tvMap;
        tvMap = new Map();
        let taimHeight;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < tfn.length; i++) {
            let tpStart = tfn[i] * 3;
            let tfIdx1 = tindices[tpStart] * 3;
            let tfIdx2 = tindices[tpStart + 1] * 3;
            let tfIdx3 = tindices[tpStart + 2] * 3;
            let tP1 = Vector3.FromArray(tpdata, tfIdx1);
            let tP2 = Vector3.FromArray(tpdata, tfIdx2);
            let tP3 = Vector3.FromArray(tpdata, tfIdx3);
            tP1 = Vector3.TransformCoordinates(tP1, tWM);
            tP2 = Vector3.TransformCoordinates(tP2, tWM);
            tP3 = Vector3.TransformCoordinates(tP3, tWM);
            if (taimHeight === undefined) {
                if (Util.numbersEqual(tP1.y, tP2.y) && Util.numbersEqual(tP2.y, tP3.y)) {
                    //if ((tP1.y == tP2.y) && (tP2.y == tP3.y)) {
                    taimHeight = tP1.y;
                    tvMap = this.uniqueLineMap([tP1, tP2, tP3], tvMap);
                }
                else {
                    continue;
                }
            }
            else if (Util.numbersEqual(tP1.y, taimHeight) &&
                Util.numbersEqual(tP2.y, taimHeight) &&
                Util.numbersEqual(tP3.y, taimHeight)) {
                tvMap = this.uniqueLineMap([tP1, tP2, tP3], tvMap);
            }
        }
        //export lines
        let j = 0;
        tvMap.forEach((p1, p2) => {
            let tP1 = p1.p1;
            let tP2 = p1.p2;
            tRes.push([tP1, tP2]);
        });
        this.mOutLineArray = [];
        this.mOutLineArray = tRes;
        tRes = this.sortLinesOutlines(tRes);
        return (tRes);
    }
    getRoomElementByBasketId(pBasketId) {
        let tRes = null;
        if (pBasketId == null)
            return (tRes);
        for (let tRE of this.mRoomElements) {
            if (tRE.BasketID === pBasketId) {
                tRes = tRE;
                break;
            }
        }
        return (tRes);
    }
    _InteractorClicked(pRoomManager) {
        pRoomManager.setClickedWOmove(true);
    }
    _InteractorClickedWOmove() {
        //console.log("Interactor clicked");
    }
    _NotEnoughWallSpaceLeft() {
        //console.log("Not enough wall space left.");
    }
    _AnimationStarted() {
        // console.log("Animation started.");
    }
    _TestTrigger() {
        console.log("Trigger started.");
    }
    _AnimationPaused() {
        // console.log("Animation paused.");
    }
    _AnimationEnded() {
        // console.log("Animation ended.");
    }
    // private _SoundListLoaded = (): void =>
    // {
    //     console.log("Sound list loaded");
    // };
    _deleteSelectionComplete(pRoomManager) {
        return () => {
            pRoomManager.cutOverlappingShapes();
            pRoomManager.cutOverlappingSZShapes();
        };
    }
    getAlignSelection() {
        let tRes = new Array();
        let tSel = this.mApp.model.selection;
        for (let tElement of tSel) {
            if (tElement instanceof RoomElement) {
                switch (tElement.type) {
                    case "PillarB":
                    case "PillarC":
                    case "BackgroundImage":
                    case "Building":
                    case "Circle":
                    case "CustomBuilding":
                    case "Line":
                    case "Rectangle":
                    case "Spline":
                    case "Text":
                        tRes.push(tElement);
                        break;
                    default:
                        break;
                }
            }
            else if (tElement instanceof ArticleElement) {
                tRes.push(tElement);
            }
        }
        return (tRes);
    }
    addSphere() {
        if (this.mPanoDome != null) {
            this.mApp.model.setSelection([this.mPanoDome]);
        }
        else {
            let tPoints = new Array();
            tPoints.push(new Vector3(0, 0, 0));
            tPoints.push(new Vector3(15, 0, 0));
            let tSphere = this._insertSphere(tPoints);
            if (tSphere != null) {
                this.mPanoDome = tSphere;
                let tCmd = new InsertElements(this.mApp, [this.mPanoDome]);
                this.mApp.commands.executeCommand(tCmd).then(() => {
                    this.TransformShape();
                    this.mApp.model.setSelection([this.mPanoDome]);
                });
            }
            if (this.mView == null)
                return;
            this.app.viewer.requestRenderFrame();
        }
    }
    addAudioSphere() {
        let tPoints = new Array();
        tPoints.push(new Vector3(0, 0, 0));
        tPoints.push(new Vector3(4, 0, 0));
        let tSphere = this._insertSphere(tPoints, true);
        if (this.view == null || tSphere == null) {
            return;
        }
        const tviewCenter = Util.getViewCenter(this.view);
        tSphere.setPosition(tviewCenter);
        let tCmd = new InsertElements(this.mApp, [tSphere]);
        this.mApp.commands.executeCommand(tCmd).then(() => {
            this.TransformShape();
            this.mApp.model.setSelection([tSphere]);
        });
        if (this.mView == null) {
            return;
        }
        this.app.viewer.requestRenderFrame();
    }
    moveSphereTexture(pValue) {
        if (this.mPanoDome == null)
            return;
        let tMat = this.mSkyBoxMaterials[0];
        if (tMat.albedoTexture == null)
            return;
        tMat.albedoTexture.vScale = pValue;
        tMat.albedoTexture.notifyTextureMatrixChanged();
        this.app.viewer.requestRenderFrame();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    toggleAnimation() {
        if (this.mAnimationActive) {
            this.mApp.viewer.onDemandRenderingEnabled = true;
            this.mAnimationActive = false;
            this.mRotationSum = 0;
            for (let tAN of this.mAnimatedNode) {
                let t_animNode = tAN;
                if (t_animNode.RotQuat != null)
                    t_animNode.Node.rotationQuaternion = t_animNode.RotQuat.clone();
                else
                    t_animNode.Node.rotationQuaternion = null;
                t_animNode.RotQuat = null;
                this.mScene.stopAnimation(t_animNode.Node, "tutoAnimation");
                this.mScene.stopAnimation(t_animNode.Node, "tutoAnimation1");
            }
            this.app.viewer.requestRenderFrame();
            return;
        }
        this.mApp.viewer.onDemandRenderingEnabled = false;
        let tanimationBox = new Animation("tutoAnimation", "rotationQuaternion.x", 35, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        // Animation keys
        let tkeys = [];
        tkeys.push({
            frame: 0,
            value: 0.4
        });
        tkeys.push({
            frame: 40,
            value: -0.4
        });
        tkeys.push({
            frame: 80,
            value: 0.4
        });
        tanimationBox.setKeys(tkeys);
        this.mAnimations.push(tanimationBox);
        // Creating an easing function
        let teasingFunction = new PowerEase(2.5);
        // For each easing function, you can choose between EASEIN (default), EASEOUT, EASEINOUT
        teasingFunction.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        // Adding the easing function to the animation
        tanimationBox.setEasingFunction(teasingFunction);
        let tselection = this.mApp.model.elements;
        let tswing = [];
        for (let tMA of tselection) {
            if (!(tMA instanceof MainArticleElement))
                continue;
            let tchildNodes = tMA.node.getChildTransformNodes();
            for (let tNode of tchildNodes) {
                if ((tNode.name.indexOf("subarticle_SW990023") > -1)) {
                    let tanimNode = {};
                    tanimNode.MainArticle = tMA;
                    tanimNode.Node = tNode;
                    tanimNode.Node.unfreezeWorldMatrix();
                    let tcn = tanimNode.Node.getChildTransformNodes();
                    for (let tchild of tcn) {
                        tchild.unfreezeWorldMatrix();
                    }
                    tanimNode.RotQuat = tNode.rotationQuaternion != null ? tNode.rotationQuaternion.clone() : null;
                    if (tanimNode.Node.rotationQuaternion != null) {
                        tanimNode.Node.rotationQuaternion.x = 0.4;
                    }
                    tanimNode.Node.animations.push(tanimationBox);
                    this.mScene.beginAnimation(tanimNode.Node, 0, 80, true);
                    tswing.push(tanimNode);
                }
            }
        }
        let tanimationCircle = new Animation("tutoAnimation1", "rotation.y", 50, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        // Animation keys
        let tkeys1 = [];
        tkeys1.push({
            frame: 0,
            value: 0
        });
        tkeys1.push({
            frame: 1,
            value: 0.05
        });
        tanimationCircle.setKeys(tkeys1);
        this.mAnimations.push(tanimationCircle);
        for (let tMA of tselection) {
            if (!(tMA instanceof MainArticleElement))
                continue;
            let tchildNodes = tMA.node.getChildTransformNodes();
            for (let tNode1 of tchildNodes) {
                tNode1.unfreezeWorldMatrix();
            }
            for (let tNode of tchildNodes) {
                if ((tNode.name.indexOf("subarticle_SW951021") > -1)) {
                    let tanimNode = {};
                    tanimNode.MainArticle = tMA;
                    tanimNode.Node = tNode;
                    for (let tNode1 of tchildNodes) {
                        if ((tNode1.name.indexOf("subarticle_SW990022") > -1) ||
                            (tNode1.name.indexOf("subarticle_SW990190") > -1) ||
                            (tNode1.name.indexOf("subarticle_SW990191") > -1)) {
                            tNode1.parent = tNode;
                            tNode1.position.y = 0;
                            tanimNode.MainArticle.updateTransformation();
                        }
                    }
                    tanimNode.RotQuat = tNode.rotationQuaternion != null ? tNode.rotationQuaternion.clone() : null;
                    tanimNode.Node.animations.push(tanimationCircle);
                    this.mScene.beginAnimation(tanimNode.Node, 0, 80, true);
                    tswing.push(tanimNode);
                }
            }
        }
        this.mAnimatedNode = tswing;
        this.mAnimationActive = true;
    }
    setCameraToAnimationStart() {
        const tcamPath = this.getCameraPath();
        if (tcamPath == null || this.view == null) {
            return;
        }
        if (this.mAnimationStatus != null) {
            this.mAnimationStatus.stop();
            this.eventAnimationEnded.trigger(this);
        }
        this.showCamPathView(0);
        this.view.viewer.requestRenderFrame();
    }
    startPathAnimation() {
        var _a;
        if (this.mAnimationPathRE == null || this.mView == null) {
            return;
        }
        if (this.mAnimationStatus != null && this.mAnimationStatus.animationStarted) {
            this.mAnimationStatus.stop();
        }
        let tREgeo = this.mAnimationPathRE.geometry;
        if (tREgeo == null) {
            return;
        }
        const tDuration = tREgeo.getDuration();
        let tREmeshes = (_a = this.mAnimationPathRE.group3d) === null || _a === void 0 ? void 0 : _a.getChildMeshes();
        if (tREmeshes == null) {
            return;
        }
        this.mApp.model.setSelection([]);
        const tcamera = this.mApp.viewer.view.camera;
        this.mApp.viewer.onDemandRenderingEnabled = false;
        const tnbFrames = this.mAnimationPathRE.Params.CamPathPoints.slice().length - 1;
        let tpos = this.mAnimationPathRE.Params.ElementPoints.slice();
        let ttarget = this.mAnimationPathRE.Params.CamPointTargetsCalculated;
        let tlastPos = tpos[tpos.length - 1].clone();
        tlastPos = Vector3.TransformCoordinates(tlastPos, this.mAnimationPathRE.transform);
        const tlastTargetPOI = ttarget[ttarget.length - 1];
        let tlastTarget = tlastTargetPOI.target.clone();
        tmstart = window.performance.now();
        const animations = [
            // move the camera position
            this.animMove(tnbFrames),
            // move the camera target
            this.animTarget(tnbFrames)
        ];
        if (this.mView.cameraControl.cameraMode !== 7) {
            this.mView.cameraControl.cameraMode = 7;
        }
        this.mAnimationPathRE.node.setEnabled(false);
        this.eventAnimationStarted.trigger(this);
        this.mAnimationStatus = this.mApp.scene.beginDirectAnimation(tcamera, animations, 0, tnbFrames, false, 1 / tDuration, this.onAnimationEnd(tlastPos, tlastTarget));
    }
    animMove(nbFrames) {
        const anim = new Animation('movecam', 'position', nbFrames, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
        anim.setKeys(this.mcamData.cameraPositions);
        //easing
        const easingFun = new PowerEase(1);
        easingFun.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);
        anim.setEasingFunction(easingFun);
        return (anim);
    }
    animTarget(nbFrames) {
        const anim = new Animation('lookcam', 'target', nbFrames, Animation.ANIMATIONTYPE_VECTOR3);
        anim.setKeys(this.mcamData.cameraTargets);
        //easing
        const easingFun = new PowerEase(1);
        easingFun.setEasingMode(EasingFunction.EASINGMODE_EASEIN);
        anim.setEasingFunction(easingFun);
        return (anim);
    }
    onAnimationEnd(lastPos, lastTarget) {
        return () => {
            if (this.mApp == null || this.mApp.viewer.view == null) {
                return;
            }
            this.eventAnimationEnded.trigger(this);
            tmend = window.performance.now();
            console.log(`Execution time: ${tmend - tmstart} ms`);
            const tCamera = this.mApp.viewer.view.camera;
            tCamera.parent = null;
            const tDir = (lastTarget.subtract(lastPos)).normalize().scale(0.125);
            const tlastPosOff = lastPos.add(tDir);
            this.mApp.viewer.onDemandRenderingEnabled = true;
            if (this.mcamPath_ResetView) {
                this.mApp.viewer.view.cameraControl.setPosition(tlastPosOff);
                this.mApp.viewer.view.cameraControl.setTarget(lastTarget);
                this.mcamPath_ResetView = true;
            }
            if (this.mAnimationPathRE != null) {
                if (!this.mHideAnimationPath) {
                    this.mAnimationPathRE.node.setEnabled(this.mCamPathVisibility);
                }
                else {
                    this.mAnimationPathRE.node.setEnabled(false);
                }
            }
            this.mApp.viewer.requestRenderFrame();
        };
    }
    pathAnimation_pause() {
        if (this.mAnimationStatus == null || this.mAnimationPathRE == null) {
            return;
        }
        const tcamRE = this.getCameraPath();
        if (tcamRE == null) {
            return;
        }
        const tcamGeo = tcamRE.geometry;
        if (tcamGeo == null) {
            return;
        }
        if (this.mAnimationStatus.animationStarted) {
            this.mApp.viewer.onDemandRenderingEnabled = true;
            const tcam = this.mAnimationStatus.target;
            const tviewCamPos = tcam.position.clone();
            const tboxPos = tcamGeo.getNearestPosIndex3d(tcam.position);
            const tcamPos = tcamGeo.getPosOfIndex(tboxPos);
            const tDir = (tcam.target.subtract(tviewCamPos)).normalize().scale(0.15);
            const tlastPosOff = tviewCamPos.add(tDir);
            this.mApp.viewer.view.cameraControl.setPosition(tlastPosOff);
            this.mApp.viewer.view.cameraControl.setTarget(tcam.target.clone());
            tcamGeo.setBoxPosByIndex(tboxPos);
            tcamRE.updateGeometry();
            this.eventAnimationPaused.trigger(this);
            this.mAnimationStatus.pause();
            if (!this.mHideAnimationPath) {
                this.mAnimationPathRE.node.setEnabled(this.mCamPathVisibility);
            }
            else {
                this.mAnimationPathRE.node.setEnabled(false);
            }
            return;
        }
        this.mAnimationPathRE.node.setEnabled(false);
        this.mApp.viewer.onDemandRenderingEnabled = false;
        this.eventAnimationStarted.trigger(this);
        this.mAnimationStatus.restart();
    }
    getCameraPath() {
        let tcamPath = null;
        for (let tREtmp of this.mRoomElements) {
            if (tREtmp.type === "CameraPath") {
                tcamPath = tREtmp;
                return (tcamPath);
            }
        }
        return (null);
    }
    getGeoMarker() {
        let tgeoMarker = null;
        for (let tREtmp of this.mRoomElements) {
            if (tREtmp.type === "GeoMarker") {
                tgeoMarker = tREtmp;
                return (tgeoMarker);
            }
        }
        return (null);
    }
    _addCamData() {
        let tRE1 = this.getCameraPath();
        if (tRE1 == null) {
            this.mAnimationPathRE = null;
            return;
        }
        this.mAnimationStatus = null;
        this.eventAnimationEnded.trigger(this);
        this.mAnimationPathRE = tRE1;
        this.mcamData.cameraPositions = [];
        this.mcamData.cameraTargets = [];
        const tcam = tRE1;
        const tcamGeo = tRE1.geometry;
        if (tcamGeo == null) {
            return;
        }
        let tpos = tcam.Params.CamPathPoints.slice();
        let ttargets = tcam.Params.CamPointTargetsCalculated;
        const tstartPos = tcamGeo.getBoxPosIndex();
        // tslint:disable-next-line:prefer-for-of
        for (let i = tstartPos; i < tpos.length; i++) {
            let tPos = tpos[i].clone(); //.add(tcam.node.position.clone());
            tPos = Vector3.TransformCoordinates(tPos, tcam.transform);
            // tPos.y = tRE1.height;
            let tTarget = ttargets[i].target.clone();
            // tTarget = Vector3.TransformCoordinates(tTarget, tcam.transform);
            // tTarget.y = tRE1.height;
            this.mcamData.cameraPositions.push({
                frame: i,
                value: tPos
            });
            this.mcamData.cameraTargets.push({
                frame: i,
                value: tTarget
            });
        }
    }
    deleteCustomCamTarget() {
        let tRE1 = this.getCameraPath();
        if (tRE1 == null) {
            this.mAnimationPathRE = null;
            return;
        }
        const tREgeo = tRE1.geometry;
        if (tREgeo == null) {
            return;
        }
        tREgeo.deleteCurrentCustomTarget();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    detectHillForInsert(pElement) {
        const tHeight = 50;
        const tTotalBounds = BoundingBox.FromElements([pElement], undefined, true);
        const tOrgCenter3d = tTotalBounds.getCenter();
        let tPos = pElement.node.position.clone();
        const tRayPos = new Vector3(tOrgCenter3d.x, tHeight, tOrgCenter3d.z);
        const tRayDirection = Vector3.Down();
        let tRay = new Ray(tRayPos, tRayDirection);
        let trayHits = this.mScene.multiPickWithRay(tRay, this.shapeFilter);
        if (trayHits != null && trayHits.length > 0) {
            let tHit = trayHits[0];
            if ((tHit.pickedPoint != null && tHit.pickedMesh != null) &&
                (tHit.pickedMesh.name.indexOf("shape2d") === 0) &&
                (tHit.pickedMesh.parent != null) &&
                (tHit.pickedMesh.parent.name.indexOf("group_") > -1)) {
                let tgrpParent = tHit.pickedMesh.parent.parent;
                if (tgrpParent != null) {
                    let tElement = SceneElement.FromNode(tgrpParent, false);
                    if (tElement instanceof RoomElement) {
                        const tRE = tElement;
                        // tslint:disable-next-line:no-collapsible-if
                        if (tRE.type === "Line" || tRE.type === "Rectangle" || tRE.type === "Spline" || tRE.type === "Circle") {
                            // tslint:disable-next-line:triple-equals
                            if (tRE.Params.terrainType != 'Flat') {
                                const tH = tRE.node.position.y + tRE.Params.hillHeight;
                                tPos.y = tH;
                            }
                        }
                    }
                }
            }
        }
        return (tPos);
    }
    addCustomCamTarget() {
        let tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            this.mAnimationPathRE = null;
            return;
        }
        const tREgeo = tcamPath.geometry;
        if (tREgeo == null) {
            return;
        }
        tREgeo.setBoxTargetCustom();
        if (tREgeo.currentViewAlongPath()) {
            this.CustomCamTarget_viewAlong();
        }
        tREgeo.computeCamPathData();
        tcamPath.notifyChange(ElementChangedFlags.PropertyValues);
        tcamPath.notifyChange(ElementChangedFlags.Properties);
        tcamPath.updateGeometry();
        this._addCamData();
    }
    CustomCamTarget_viewAlong() {
        let tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            this.mAnimationPathRE = null;
            return;
        }
        const tREgeo = tcamPath.geometry;
        if (tREgeo == null) {
            return;
        }
        const tcustTarget = tREgeo.getCurrCustomTarget();
        if (tcustTarget == null) {
            return;
        }
        tREgeo.setViewAlongPath(tcustTarget.index, true);
        tREgeo.computeCamPathData(CameraPath.splitPath);
        this._addCamData();
        tcamPath.notifyChange(ElementChangedFlags.PropertyValues);
        tcamPath.notifyChange(ElementChangedFlags.Properties);
        tcamPath.updateGeometry();
    }
    CustomCamTarget_notViewAlong() {
        let tcamPath = this.getCameraPath();
        if (tcamPath == null) {
            this.mAnimationPathRE = null;
            return;
        }
        const tREgeo = tcamPath.geometry;
        if (tREgeo == null) {
            return;
        }
        const tcustTarget = tREgeo.getCurrCustomTarget();
        if (tcustTarget == null) {
            return;
        }
        tcustTarget.viewAlongPath = false;
        tREgeo.computeCamPathData(CameraPath.splitPath);
        this._addCamData();
        tcamPath.notifyChange(ElementChangedFlags.PropertyValues);
        tcamPath.notifyChange(ElementChangedFlags.Properties);
        tcamPath.updateGeometry();
    }
    getEOXMATZfile(pID, pmatXML) {
        var nID = pmatXML.getElementsByTagName("ID");
        let tParentElement = null;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < nID.length; i++) {
            let tID = nID[i];
            if (tID.innerHTML === pID) {
                tParentElement = tID.parentElement;
                if (tParentElement == null)
                    return ("");
                let tfiles = tParentElement.getElementsByTagName("file");
                if (tfiles.length >= 1) {
                    let tfile = tfiles[0].innerHTML;
                    return (tfile);
                }
            }
        }
        return ("");
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getEOXmatColorValue(pID, pprodXML, pJSON_texture) {
        var tprodInfo = pprodXML.getElementsByTagName("productInfo");
        let t__id = "";
        let t__color = "";
        // tslint:disable-next-line:prefer-for-of
        for (let x = 0; x < tprodInfo.length; x++) {
            let tchild = tprodInfo[x];
            if (!tchild.hasAttribute("ID"))
                continue;
            let tID = tchild.getAttribute("ID");
            if (tID !== pID)
                continue;
            let tchildNodes = tchild.childNodes;
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < tchildNodes.length; i++) {
                let tentry = tchildNodes[i];
                if (tentry.nodeName === "description") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    let tLang = tentry.getAttribute("lang");
                    let tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        t__id = tText.trim();
                        continue;
                    }
                }
                if (tentry.nodeName === "details") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    let tLang = tentry.getAttribute("lang");
                    let tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        t__color = tText.trim();
                        continue;
                    }
                }
                if (t__id !== "" && t__color !== "") {
                    break;
                }
            }
            if (t__id !== "" && t__color !== "") {
                break;
            }
        }
        let tParentElement = tprodInfo[0].parentElement;
        if (tParentElement == null)
            return;
        if (t__id !== "" && t__color !== "") {
            pJSON_texture.albedocolor = t__color;
            pJSON_texture.id = t__id;
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getEOXMaterialsOfCategory(pMatCategory, pCatNode, pmatXML, pprodXML) {
        let nID = pCatNode.childNodes;
        let tRes = null;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < nID.length; i++) {
            let _entryId = "";
            let tJSON_MATentry = {};
            tJSON_MATentry.category = pMatCategory;
            tJSON_MATentry.id = "";
            tJSON_MATentry.smallIcon = "";
            tJSON_MATentry.translation = new Array();
            let tNode = nID[i];
            if (pCatNode.hasAttribute("visibility")) {
                let tnodeVisible = (pCatNode.getAttribute("visibility"));
                if (tnodeVisible != null && tnodeVisible.toLocaleLowerCase() === "invisible")
                    continue;
            }
            // id
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < nID.length; x++) {
                let tchild = nID[x];
                if (tchild.nodeName.toLocaleLowerCase() === "id") {
                    _entryId = tchild.innerHTML;
                    break;
                }
            }
            // small icon
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < nID.length; x++) {
                let tchild = nID[x];
                if (tchild.nodeName.toLocaleLowerCase() === "file") {
                    tJSON_MATentry.smallIcon = tchild.innerHTML;
                    break;
                }
            }
            // translations
            tJSON_MATentry.translation = [];
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < nID.length; x++) {
                let tchild = nID[x];
                if (tchild.nodeName.toLocaleLowerCase() === "label") {
                    if (!tchild.hasAttribute("lang") && !tchild.hasAttribute("text"))
                        continue;
                    var tLang = tchild.getAttribute("lang");
                    var tText = tchild.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "en" && tText.trim() !== "") {
                        tJSON_MATentry.id = tText.trim().toLocaleLowerCase().replace(/[^a-z0-9]/ig, "_");
                    }
                    let tJSON_Translation = {};
                    tJSON_Translation.lang = tLang.trim().toLocaleLowerCase();
                    tJSON_Translation.text = tText;
                    tJSON_MATentry.translation.push(tJSON_Translation);
                }
            }
            if (tJSON_MATentry.category === "color") {
                // color
                if (pprodXML == null) {
                    tRes = null;
                    break;
                }
                this.getEOXmatColorValue(_entryId, pprodXML, tJSON_MATentry);
                if (tJSON_MATentry.albedocolor !== "") {
                    tRes = tJSON_MATentry;
                    break;
                }
                tRes = null;
                break;
            }
            // matz
            if (pmatXML == null) {
                tRes = null;
                break;
            }
            let tmatzFile = this.getEOXMATZfile(_entryId, pmatXML).trim();
            if (tmatzFile !== "") {
                let tmatID = Util.GetFilenameFromURL(tmatzFile).toLocaleLowerCase();
                tJSON_MATentry.id = tmatID;
                if (pprodXML != null) {
                    let tdefID = this.getEOX_ID_definition(_entryId, pprodXML);
                    if (tdefID !== '')
                        tJSON_MATentry.id = tdefID;
                }
                tJSON_MATentry.matz = tmatzFile;
                tRes = tJSON_MATentry;
                break;
            }
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getEOXCategoryVisibilities(pMatCategory, pobjID, pprodXML) {
        var tprodInfo = pprodXML.getElementsByTagName("productInfo");
        let t__include = "";
        let t__exclude = "";
        let t__id = "";
        let tLang = "";
        let tText = "";
        // tslint:disable-next-line:prefer-for-of
        for (let x = 0; x < tprodInfo.length; x++) {
            let tchild = tprodInfo[x];
            if (!tchild.hasAttribute("ID"))
                continue;
            let tID = tchild.getAttribute("ID");
            if (tID !== pobjID)
                continue;
            let tchildNodes = tchild.childNodes;
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < tchildNodes.length; i++) {
                let tentry = tchildNodes[i];
                if (tentry.nodeName === "description") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    tLang = tentry.getAttribute("lang");
                    tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        t__include = tText.trim();
                        continue;
                    }
                }
                if (tentry.nodeName === "details") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    tLang = tentry.getAttribute("lang");
                    tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        t__exclude = tText.trim();
                        continue;
                    }
                }
                if (tentry.nodeName === "subtitle") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    tLang = tentry.getAttribute("lang");
                    tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        t__id = tText.trim();
                        continue;
                    }
                }
                if (t__include !== "" && t__exclude !== "") {
                    break;
                }
            }
            if (t__include !== "" && t__exclude !== "") {
                break;
            }
        }
        if (t__id !== "")
            pMatCategory.id = t__id;
        pMatCategory.filter_include = t__include;
        pMatCategory.filter_exclude = t__exclude;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getEOX_ID_definition(pobjID, pprodXML) {
        var tprodInfo = pprodXML.getElementsByTagName("productInfo");
        let t__id = "";
        let tLang = "";
        let tText = "";
        let tRes = "";
        // tslint:disable-next-line:prefer-for-of
        for (let x = 0; x < tprodInfo.length; x++) {
            let tchild = tprodInfo[x];
            if (!tchild.hasAttribute("ID"))
                continue;
            let tID = tchild.getAttribute("ID");
            if (tID !== pobjID)
                continue;
            let tchildNodes = tchild.childNodes;
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < tchildNodes.length; i++) {
                let tentry = tchildNodes[i];
                if (tentry.nodeName === "subtitle") {
                    if (!tentry.hasAttribute("lang") && !tentry.hasAttribute("text"))
                        continue;
                    tLang = tentry.getAttribute("lang");
                    tText = tentry.getAttribute("text");
                    if (tLang == null || tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "de" && tText.trim() !== "") {
                        tRes = tText.trim();
                        break;
                    }
                }
            }
            if (tRes.trim() !== "")
                break;
        }
        return (tRes);
    }
    // folder entries
    // tslint:disable-next-line:cyclomatic-complexity
    getNodeEntries(pNode, pJSON_material, pmatXML, pprodXML) {
        var _a;
        let nID = pNode.childNodes;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < nID.length; i++) {
            let _entryId = "";
            let tJSON_category = {};
            tJSON_category.id = "";
            tJSON_category.filter_include = "";
            tJSON_category.filter_exclude = "";
            tJSON_category.smallIcon = "";
            tJSON_category.translation = new Array();
            let tNode = nID[i];
            if (tNode.nodeName !== "folder")
                continue;
            // tslint:disable-next-line:no-collapsible-if
            if (tNode.hasAttribute("visibility")) {
                if (tNode != null) {
                    let tnodeVisible = tNode.getAttribute("visibility");
                    if (tnodeVisible != null && tnodeVisible.toLocaleLowerCase() === "invisible")
                        continue;
                }
            }
            let tNodeChilds = tNode.childNodes;
            // id
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < tNodeChilds.length; x++) {
                let tchild = tNodeChilds[x];
                if (tchild.nodeName.toLocaleLowerCase() === "id") {
                    _entryId = tchild.innerHTML;
                    break;
                }
            }
            // small icon
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < tNodeChilds.length; x++) {
                let tchild = tNodeChilds[x];
                if (tchild.nodeName.toLocaleLowerCase() === "file") {
                    tJSON_category.smallIcon = tchild.innerHTML;
                    break;
                }
            }
            // translations
            tJSON_category.translation = [];
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < tNodeChilds.length; x++) {
                let tchild = tNodeChilds[x];
                if (tchild.nodeName.toLocaleLowerCase() === "label") {
                    if (!tchild.hasAttribute("lang") && !tchild.hasAttribute("text"))
                        continue;
                    var tLang = tchild.getAttribute("lang");
                    if (tLang == null)
                        continue;
                    var tText = tchild.getAttribute("text");
                    if (tText == null)
                        continue;
                    if (tLang.toLocaleLowerCase() === "en" && tText.trim() !== "") {
                        tJSON_category.id = tText.trim().toLocaleLowerCase().replace(/[^a-z0-9]/ig, "_");
                    }
                    let tJSON_Translation = {};
                    tJSON_Translation.lang = tLang.trim().toLocaleLowerCase();
                    tJSON_Translation.text = tText;
                    tJSON_category.translation.push(tJSON_Translation);
                }
            }
            //materials
            // tslint:disable-next-line:prefer-for-of
            for (let x = 0; x < tNodeChilds.length; x++) {
                let tchild = tNodeChilds[x];
                if (tchild.nodeName.toLocaleLowerCase() === "node") {
                    if (!tchild.hasAttribute("type"))
                        continue;
                    let tnodeType = (_a = tchild.getAttribute("type")) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase().trim();
                    if (tnodeType === "material") {
                        let tmatJSON = this.getEOXMaterialsOfCategory(tJSON_category.id, tchild, pmatXML, pprodXML);
                        if (tmatJSON != null) {
                            if (pJSON_material.category == null) {
                                pJSON_material.category = new Array();
                            }
                            if (pJSON_material.textures == null) {
                                pJSON_material.textures = new Array();
                            }
                            pJSON_material.textures.push(tmatJSON);
                        }
                    }
                    if (tnodeType === "object") {
                        let tobjChild = tchild.childNodes;
                        let tobj_ID = "";
                        let tentry_visibility = false;
                        // tslint:disable-next-line:prefer-for-of
                        for (let j = 0; j < tobjChild.length; j++) {
                            let t_child = tobjChild[j];
                            if (t_child.nodeName.toLocaleLowerCase() === "label") {
                                if (!t_child.hasAttribute("lang") && !t_child.hasAttribute("text"))
                                    continue;
                                let tLang1 = t_child.getAttribute("lang");
                                if (tLang1 == null)
                                    continue;
                                let tText1 = t_child.getAttribute("text");
                                if (tText1 == null)
                                    continue;
                                if (tLang1.toLocaleLowerCase() === "de" && tText1.trim() === "_visibility_") {
                                    tentry_visibility = true;
                                }
                            }
                            if (t_child.nodeName.toLocaleLowerCase() === "id") {
                                tobj_ID = t_child.innerHTML;
                            }
                            if (tobj_ID !== "" && tentry_visibility) {
                                break;
                            }
                        }
                        if (tobj_ID !== "" && tentry_visibility) {
                            if (pprodXML != null) {
                                this.getEOXCategoryVisibilities(tJSON_category, tobj_ID, pprodXML);
                            }
                        }
                        else {
                            let tmatJSON = this.getEOXMaterialsOfCategory(tJSON_category.id, tchild, pmatXML, pprodXML);
                            if (tmatJSON != null) {
                                if (pJSON_material.category == null) {
                                    pJSON_material.category = new Array();
                                }
                                if (pJSON_material.textures == null) {
                                    pJSON_material.textures = new Array();
                                }
                                pJSON_material.textures.push(tmatJSON);
                            }
                        }
                    }
                }
            }
            pJSON_material.category.push(tJSON_category);
        }
    }
    enableMeasureText(pVal) {
        for (let tMeas of this.mMeasures) {
            tMeas.enableTextNode = pVal;
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    EOX2JSON(pSearchLabel) {
        let tJSON_material = {};
        if (!window.DOMParser || this.mEOX_CAT == null || this.mEOX_CAT.trim() === "")
            return;
        let tParser = new DOMParser();
        let txml_CAT = null;
        let txml_MAT = null;
        let txml_PROD = null;
        txml_CAT = tParser.parseFromString(this.mEOX_CAT, "text/xml");
        if (this.mEOX_MAT != null)
            txml_MAT = tParser.parseFromString(this.mEOX_MAT, "text/xml");
        if (this.mEOX_PROD != null)
            txml_PROD = tParser.parseFromString(this.mEOX_PROD, "text/xml");
        if (txml_CAT == null)
            return;
        let tsearchLabel = pSearchLabel;
        var nRoot = txml_CAT.getElementsByTagName("label");
        let tParentElement = null;
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < nRoot.length; i++) {
            let tNode = nRoot[i];
            if (!tNode.hasAttribute("lang") && !tNode.hasAttribute("text"))
                continue;
            let tLang = tNode.getAttribute("lang");
            let tText = tNode.getAttribute("text");
            if (tLang == null || tText == null)
                return;
            if ((tLang.toLocaleLowerCase() === "de") && (tText.toLocaleLowerCase() === tsearchLabel.toLocaleLowerCase())) {
                tParentElement = tNode.parentElement;
                break;
            }
        }
        if (tParentElement == null) {
            alert('Knoten: ' + '"' + tsearchLabel + '"' + ' nicht gefunden!');
            return;
        }
        this.getNodeEntries(tParentElement, tJSON_material, txml_MAT, txml_PROD);
        console.log(tJSON_material);
        let tJSONstr = JSON.stringify(tJSON_material);
        let blob = new Blob([tJSONstr], {
            type: "text/json"
        });
        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = "textures_XXX.json";
        a.click();
    }
    _keyEvents() {
        document.addEventListener("keydown", this.onKeyDown, false);
        document.addEventListener("keyup", this.onKeyUp, false);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    onKeyDown(event) {
        if (event.key == null)
            return;
        if (!contains(this.mcurrentKey, event.key.toLocaleLowerCase()))
            this.mcurrentKey.push(event.key.toLocaleLowerCase());
        /*
        const tCamera = this.view!.camera;
        let tDirs: Array<string> = new Array("ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown");

        let tPos = tCamera.position.clone();
        tPos.y = 0;

        let ttargetPos = tCamera.getTarget();
        ttargetPos.y = 0;

        let tcamDir = (ttargetPos.subtract(tPos)).normalize();

        let tcamDir_perp = Util.to3d(perpendicularVector2(Util.to2d(tcamDir)));

        // rotate
        if (event.key === 'ArrowLeft' && event.ctrlKey) {
            this.view!.camera.rotation.y -= tmoveOffset * 0.1;
        }

        if (event.key === 'ArrowRight' && event.ctrlKey) {
            this.view!.camera.rotation.y += tmoveOffset * 0.1;
        }

        if (event.key === 'ArrowUp' && event.ctrlKey) {
            this.view!.camera.rotation.x -= tmoveOffset * 0.1;
        }

        if (event.key === 'ArrowDown' && event.ctrlKey) {
            this.view!.camera.rotation.x += tmoveOffset * 0.1;
        }

        // move
        if (event.key === "ArrowUp" && !event.ctrlKey) {
            this.view!.camera.position.addInPlace(tcamDir.scale(0.5));
        }

        if (event.key === "ArrowDown" && !event.ctrlKey) {
            this.view!.camera.position.addInPlace(tcamDir.scale(-0.5));
        }

        if (event.key === "ArrowLeft" && !event.ctrlKey) {
            this.view!.camera.position.addInPlace(tcamDir_perp.scale(-0.5));
        }

        if (event.key === "ArrowRight" && !event.ctrlKey) {
            this.view!.camera.position.addInPlace(tcamDir_perp.scale(0.5));
        }

        this.app.viewer.requestRenderFrame();
        */
    }
    // tslint:disable-next-line:cyclomatic-complexity
    onKeyUp(event) {
        if (event.key == null)
            return;
        if (contains(this.mcurrentKey, event.key.toLocaleLowerCase()))
            removeElement(this.mcurrentKey, event.key.toLocaleLowerCase());
    }
    deserializeWalls() {
        if (this.mLoadedWalls_deserialize == null)
            return;
        Wall.deserialize_wall(this, this.mLoadedWalls_deserialize);
        this.mApp.viewer.forceRenderFrame();
        this.deserializeWallElements();
    }
    deserializeWallElements() {
        setTimeout(() => {
            this.mApp.viewer.forceRenderFrame();
            let tCnt = Math.min(40, this.mLoadedWalls_deserialize.length);
            tCnt = this.mLoadedWalls_deserialize.length;
            for (let i = 0; i < tCnt; i++) {
                let twDD = this.mLoadedWalls_deserialize[i];
                this.checkMaterial(twDD.wall.materialSetA).then(() => {
                    this.checkMaterial(twDD.wall.materialSetB).then(() => {
                        twDD.wall.updateGeometry();
                    });
                });
                if (twDD.ioData.openings != null) {
                    for (let tIOOpening of twDD.ioData.openings)
                        WallOpening._load(this, twDD.wall, tIOOpening);
                }
                if (twDD.ioData.additions != null) {
                    for (let tIOAddition of twDD.ioData.additions)
                        WallAddition._load(this, twDD.Context, twDD.wall, tIOAddition);
                }
            }
        }, 300);
    }
    startWallCreation() {
        if (this.mLoadedWalls_deserialize == null)
            return;
        this.deserializeWalls();
        this.mSceneIsLoading = false;
    }
    _updateOfmlArticle() {
        for (const article of this.mApp.model.elements) {
            if (!(article instanceof MainArticleElement))
                continue;
            article.updateOfmlArticle(false, true);
        }
    }
    storeSZVisibility() {
        this.mSZshapeVisibility = new Map();
        for (let tRE of this.mREShapes) {
            if (tRE.type !== "SafetyZone")
                continue;
            this.mSZshapeVisibility.set(tRE.BasketID, { visible: tRE.get_SafetyZone_Visibility() });
        }
    }
    removeSafetyZones() {
        let tSZlist = new Array();
        for (let tRE of this.mRoomElements) {
            if (tRE.type === "SafetyZone")
                tSZlist.push(tRE);
        }
        for (let tSZ of tSZlist) {
            this._removeSZ(tSZ);
        }
        this.mAddedMainArticles = [];
        this.mApp.viewer.requestRenderFrame();
    }
    reCreateSafetyZones() {
        let tSZlist = new Array();
        for (const tRE of this.mRoomElements) {
            if (tRE.type === "SafetyZone")
                tSZlist.push(tRE);
        }
        for (const tSZ of tSZlist) {
            this._removeSZ(tSZ);
        }
        this.mAddedMainArticles = [];
        this.createSafetyZonesAfterLoad();
        for (let tRE of this.mREShapes) {
            if (tRE.type !== "SafetyZone")
                continue;
            let tisVisible = true;
            let tentry = this.mSZshapeVisibility.get(tRE.BasketID);
            if (tentry != null) {
                tisVisible = tentry.visible;
            }
            tRE.set_SafetyZone_Visibility(tisVisible);
        }
        this.cutOverlappingSZShapes();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async exportSceneGLB(pAdditionalNodes) {
        this.prepareElementsForGLBExport();
        let tObjects = [];
        for (let telem of this.mApp.model.elements) {
            if (telem instanceof RoomElement) {
                const tRE = telem;
                if (tRE.type === "DummySafetyZone") {
                    continue;
                }
                if (tRE.type === "CameraPath") {
                    continue;
                }
                if (tRE.type === "Text") {
                    continue;
                }
            }
            if (telem instanceof Measure) {
                continue;
            }
            // console.log(telem.node.name);
            tObjects.push(telem);
        }
        for (let telem of this.mREShapes) {
            if (telem instanceof RoomElement) {
                const tRE = telem;
                if (tRE.type === "SafetyZone") {
                    tObjects.push(tRE);
                }
            }
        }
        for (let tobj of tObjects) {
            if (tobj instanceof MainArticleElement) {
                const tMA = tobj;
                const titemProps = await tMA.getItemProperties();
                if (titemProps.article !== undefined && isNotNullOrEmpty(titemProps.article.baseArticleNumber)) {
                    tobj.node.name = titemProps.article.baseArticleNumber;
                    tobj.node.id = titemProps.article.baseArticleNumber;
                }
            }
        }
        // let tPromise = egr.wcf.core.mdl.io.GltfScene.Export(this.mApp.model, tObjects, 1).then((result) => {
        let tPromise = roomGltfScene.Export(this.mApp.model, tObjects, this, pAdditionalNodes, 1).then((result) => {
            if (result != null) {
                this.resetGeometries();
                return (result);
            }
            return (null);
        });
        return (tPromise);
    }
    async getSceneGLB(pAdditionalNodes) {
        await this.exportSceneGLB(pAdditionalNodes).then((result) => {
            if (result != null) {
                let a = document.createElement("a");
                a.href = window.URL.createObjectURL(result);
                a.download = "scene.glb";
                a.click();
            }
        });
    }
    async exportGLB() {
        // BABYLON.GLTF2Export.GLTFAsync(this.mScene, "fileName").then((gltf) => {
        //     gltf.downloadFiles();
        // });
        BJSGLTF2Export.GLBAsync(this.mScene, "fileName").then((glb) => {
            glb.downloadFiles();
        });
        // let tPromise = egr.wcf.core.mdl.io.GltfScene.Export(this.mApp.model, null, 1).then((result) => {
        //     if (result != null) {
        //         return (result);
        //     }
        //     return (null);
        // });
        // return (tPromise);
        return (Promise.resolve(null));
    }
    _moveTexture() {
        if (this.mSkyBox == null || this.mSkyBox.material == null) {
            return;
        }
        let tskyMat = this.mSkyBox.material;
        if (tskyMat.diffuseTexture == null) {
            return;
        }
        tskyMat.diffuseTexture.vOffset += 10;
    }
}
RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y = 0;
RoomManager.ROOM_SAFETYZONE_POSITION_Y = 0.015;
RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y = 0.030;
RoomManager.ROOM_CameraPathHeight = 1.73;
RoomManager.ROOM_SHAPE2D_PREFIX = 'shape2d_';
RoomManager.ROOM_SHAPE2D_SUFFIXOUTLINE = '_outline';
RoomManager.ROOM_SHAPE2D_SUFFIXPICKLINE = '_pickline';
RoomManager.ROOM_SHAPE2D_BISECTORLINE = '_bisectorline';
RoomManager.WALLOPENING_2D_LINE_HEIGHT = 0.001;
RoomManager.WINDOW_ROUND_TESSELATION = 50;
RoomManager.OPACITYWHILESTARTTOOL = 0.5;
RoomManager.AUDIO_LOCAL_EFFECT = 'local_effect';
RoomManager.AUDIO_BACKGROUND_EFFECT = 'background_effect';
RoomManager.AUDIO_BACKGROUND_MUSIC = 'background_music';
RoomManager.CATVALUE_SPLITTER = '_-_';
RoomManager.AUDIOLIST = [
    { title: 'Going on gravel and grass', url: 'https://cdn.pixabay.com/audio/2021/08/04/audio_ad180669e0.mp3' },
    { title: 'Children outside', url: 'https://cdn.pixabay.com/audio/2022/03/15/audio_1dabfec457.mp3' },
    { title: 'Squeaking swing', url: 'https://cdn.pixabay.com/audio/2022/03/15/audio_554ffe464f.mp3' }
];
RoomManager.ROOM_FLOOR_OPACITY_VALUE = 0.3;
//// private ///////////////////////////////////////////////////////////////////////////////
RoomManager.CAM_FRAME_LENGTH = 2;
RoomManager.CAM_Y_VALUE = 1.86;
let gTmpRay = Ray.Zero();
let tmoveOffset = 0.3;
var tmstart = 0;
var tmend = 0;
